import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Box,
  Grid,
  CircularProgress,
  Alert,
  Paper,
  Tabs,
  Tab,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Avatar,
  Tooltip,
  Chip,
  FormControlLabel,
  Checkbox,
  TextField,
  Divider,
  Slider,
  Card,
  CardMedia,
  CardContent,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Menu,
  ClickAwayListener,
  Popper,
  Grow,
  SelectChangeEvent,
  Autocomplete,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemIcon,
  FormGroup,
  Switch,
  Drawer,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { HexColorPicker } from 'react-colorful';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';
import ImageIcon from '@mui/icons-material/Image';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CropIcon from '@mui/icons-material/Crop';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import CheckIcon from '@mui/icons-material/Check';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import InventoryIcon from '@mui/icons-material/Inventory';
import ViewCarouselIcon from '@mui/icons-material/ViewCarousel';
import TuneIcon from '@mui/icons-material/Tune';
import PeopleIcon from '@mui/icons-material/People';
import PrintIcon from '@mui/icons-material/Print';
import KitchenIcon from '@mui/icons-material/Kitchen';
import MenuIcon from '@mui/icons-material/Menu';
import LinkIcon from '@mui/icons-material/Link';
import QRMenuProductSelectionModal from './QRMenuProductSelectionModal';
import ImageLibrarySelectionModal from '../../accounting/inventory/ImageLibrarySelectionModal';
import { useNavigate } from 'react-router-dom';
import Cropper from 'react-easy-crop';
import PrintingSettingsTab from '../components/PrintingSettingsTab';
import ReceiptSettingsModal from '../components/ReceiptSettingsModal';
import { printTestPage } from '../components/PrintReceiptService';
import KitchenScreenSettings, { KitchenScreenSettingsData, OrderCardColorSettings } from '../components/KitchenScreenSettings';
import axios from 'axios';
import { mainApi, accountingApi } from '../../../config/apiClient';
import {
  getPersonnel as getPersonnelSql,
  bulkSetScreenPermissions,
} from '../../../services/migration/personnelDataService';
import { useTranslation } from 'react-i18next';
import GuideButton from '../../common/guide/GuideButton';
import { AppDatePicker } from '../../common/AppDatePicker';

export interface QRMenuSettingsModalProps {
  open: boolean;
  onClose: () => void;
  menuId: string | null;
  companyId: string | null;
  menuName?: string;
  moduleType?: 'qr' | 'ticket';
  onImageUpload?: (productId: string, file: File) => Promise<string>;
  isKitchenMode?: boolean; // Mutfak Ekranı modunda sadece ilgili sekmeleri göster
}

// Ürün veri tipi
interface InventoryItem {
  id: string;
  name: string;
  categoryId?: string | null;
  warehouseId?: string | null;
  salePrice?: number;
  useBranchPricing?: boolean;
  stockCode?: string;
  barcode?: string;
  imageUrl?: string;
  description?: string;
}

// Menü veri tipi
interface Menu {
  id: string;
  name: string;
  products?: string[];
  imageUrl?: string;
}

// Personel veri tipi (mutfak erişimi için)
interface Personnel {
  id: string;
  name: string;
  timestamp?: number;
}

// Mutfak personeli izinleri tipi
interface KitchenPersonnelPermissions {
  accessKitchenScreen: boolean; // Mutfak ekranına erişim yetkisi
  viewAllOrders: boolean; // Tüm siparişleri görme yetkisi
  updateOrderStatus: boolean; // Sipariş durumunu güncelleme yetkisi
  cancelOrders: boolean; // Sipariş iptal etme yetkisi
  showPrintButton: boolean; // Yazdırma butonu göster/gizle
  listReadyOrders: boolean; // Hazır siparişleri listele (false ise 5 saniyede kaldır)
  waiterMode: boolean; // Garson modu (true ise "Garson Ekranı" başlığı)
  autoPrint: boolean; // Otomatik yazdırma (yeni siparişler geldiğinde otomatik yazdır)
  showPrices: boolean; // Fiyatları göster (false ise hiçbir yerde fiyat görünmez)
}

// Yazıcı tanımlama için tip
interface DefinedPrinter {
  id: string;
  name: string;
}

// Kategori tipi
interface InventoryCategory {
  id: string;
  name: string;
  parentId: string | null;
  level: number;
  createdAt: string;
  branchesId?: string | null;
}

// Adisyon görüntü ayarları
interface ReceiptDisplaySettings {
  companyName: string;
  companyAddress: string;
  taxNumber: string;
  showReceiptHeader: boolean;
  showVat: boolean;
  showDiscountInfo: boolean;
  showCashierName: boolean;
  showReceiptNumber: boolean;
  receiptNumberResetValue: number;
  resetAtEndOfDay: boolean;
  printCopyCount: number;
  productGrouping: string;
  standardFooterNote: string;
  thankYouMessage: string;
  receiptWidth: number;
  characterLength: number;
  marginTop: number;
  marginBottom: number;
  marginLeft: number;
  marginRight: number;
  headerType: 'text' | 'logo';
  logoUrl: string;
}

// QR Menü veri tipi
interface QRMenuSettings {
  mode?: 'qr' | 'ticket';
  products?: string[];
  template?: string;
  menus?: { [menuId: string]: Menu };
  relatedProductsByProductId?: { [productId: string]: string[] };
  appearance?: {
    template?: string;
    primaryColor?: string;
    secondaryColor?: string;
    fontFamily?: string;
    ticketCompanyName?: string;
    ticketCalculateButtonColor?: string;
    ticketReservationButtonColor?: string;
    ticketBackgroundImageUrl?: string;
    ticketNoticeText?: string;
    ticketRulesText?: string;
    ticketCallCenterText?: string;
    ticketLeftInfoText?: string;
    ticketSidebarLogoUrl?: string;
    ticketChildMaxAge?: number;
    ticketEnableFreeAge?: boolean;
    ticketFreeMaxAge?: number;
    ticketRequireAdultForReservation?: boolean;
    ticketMinReservationOffsetDays?: number;
    ticketMaxReservationDate?: string;
    ticketMaxGuestCount?: number;
    ticketServiceDetailsTitle?: string;
    ticketServiceDetailsContent?: string;
    ticketSalesAgreementTitle?: string;
    ticketSalesAgreementContent?: string;
    ticketKvkkTitle?: string;
    ticketKvkkContent?: string;
    ticketContactAddress?: string;
    ticketContactPhonePrimary?: string;
    ticketContactPhoneSecondary?: string;
    ticketContactPhoneTertiary?: string;
    ticketContactEmail?: string;
    ticketContactEmailSecondary?: string;
    // Iletisim bilgilerinin gosterim duzeni: 'stacked' (alt alta) veya 'inline' (yan yana, ortali). Varsayilan: 'stacked'.
    ticketContactLayout?: 'stacked' | 'inline';
    // 'product' = stoktaki yetiskin/cocuk urun fiyati; 'dynamic' = yas bandi + sezonluk fiyatlandirma. Varsayilan: 'dynamic'.
    ticketPricingMode?: 'product' | 'dynamic';
    ticketDiscountBandOneMaxAge?: number;
    ticketDiscountBandOnePercent?: number;
    ticketDiscountBandTwoMaxAge?: number;
    ticketDiscountBandTwoPercent?: number;
    ticketAdultPriceMayJune?: number;
    ticketAdultPriceJulyAugust?: number;
    ticketAdultPriceSeptember?: number;
    showPrices?: boolean;
    showImages?: boolean;
    showDescriptions?: boolean;
    cardStyle?: 'standard' | 'compact' | 'full-width' | 'minimal';
    showLogo?: boolean;
    showSplashScreen?: boolean;
    currencySymbol?: string;
    menuLogoUrl?: string;
    qrtikCoverImageUrl?: string;
    socialLinks?: {
      website?: string;
      facebook?: string;
      instagram?: string;
      x?: string;
      youtube?: string;
      tiktok?: string;
    };
    businessLocationUrl?: string;
    wifiPassword?: string;
    allowOrderNotes?: boolean;
    // Sepete ekle butonu ayarları
    addToCartButton?: {
      enabled?: boolean;
      text?: string;
      style?: 'filled' | 'outlined' | 'text';
      size?: 'small' | 'medium' | 'large';
      position?: 'bottom' | 'overlay' | 'side';
      backgroundColor?: string;
      textColor?: string;
      borderColor?: string;
      borderRadius?: number;
      showIcon?: boolean;
      animation?: 'none' | 'pulse' | 'bounce';
    };
  };
  // Mutfak personeli ayarları
  kitchenPersonnel?: {
    assignedPersonnelIds?: string[];
    personnelPermissions?: { [personnelId: string]: KitchenPersonnelPermissions };
  };
  // Yazdırma ayarları
  printingSettings?: {
    printingMode?: 'single' | 'category';
    defaultReceiptPrinter?: DefinedPrinter | null;
    definedPrinters?: DefinedPrinter[];
    categoryPrinterAssignments?: { [categoryId: string]: string | null };
    receiptDisplaySettings?: ReceiptDisplaySettings;
  };
  // Mutfak ekranı ayarları
  kitchenScreenSettings?: KitchenScreenSettingsData;
}

type SocialPlatformKey = 'website' | 'facebook' | 'instagram' | 'x' | 'youtube' | 'tiktok';

const SOCIAL_PLATFORM_OPTIONS: Array<{ key: SocialPlatformKey; label: string; placeholder: string }> = [
  { key: 'website', label: 'Web Sitesi', placeholder: 'https://ornek.com' },
  { key: 'facebook', label: 'Facebook', placeholder: 'https://facebook.com/sayfa' },
  { key: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/hesap' },
  { key: 'x', label: 'X (Twitter)', placeholder: 'https://x.com/hesap' },
  { key: 'youtube', label: 'YouTube', placeholder: 'https://youtube.com/@kanal' },
  { key: 'tiktok', label: 'TikTok', placeholder: 'https://tiktok.com/@hesap' }
];

// Yardımcı fonksiyonlar - Resim kırpma işlemleri için
const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.src = url;
  });

// Resmi belirtilen koordinatlara göre kırpma
async function getCroppedImg(
  imageSrc: string,
  pixelCrop: { x: number; y: number; width: number; height: number },
  uploadType?: 'product' | 'menu' | 'menuLogo' | 'ticketBackground' | 'ticketSidebarLogo' | 'qrtikCover' // Yükleme tipini parametre olarak al
): Promise<Blob> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Canvas context is not available');
  }

  // Kırpma boyutunu yükleme tipine göre ayarla
  let targetSize = 500; // Varsayılan ürün/menü resmi boyutu
  if (uploadType === 'menuLogo' || uploadType === 'ticketSidebarLogo') {
    targetSize = 1024; // Menü logosu için özel boyut
  }

  canvas.width = targetSize;
  canvas.height = targetSize;

  ctx.fillStyle = 'white';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    targetSize, // Dinamik boyut
    targetSize  // Dinamik boyut
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Canvas is empty'));
          return;
        }
        resolve(blob);
      },
      'image/jpeg',
      0.95
    );
  });
}

// Resim yükleme modal bileşeni
interface ProductImageModalProps {
  open: boolean;
  onClose: () => void;
  onUpload: (file: File) => Promise<void>;
  loading: boolean;
  uploadType?: 'product' | 'menu' | 'menuLogo' | 'ticketBackground' | 'ticketSidebarLogo' | 'qrtikCover';
  setImageUploading: (isUploading: boolean) => void;
}

const ProductImageModal: React.FC<ProductImageModalProps> = ({ open, onClose, onUpload, loading, uploadType, setImageUploading }) => {
  const { t } = useTranslation('salesSystem');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const [showCropper, setShowCropper] = useState(false);

  let allowedFormats = ['jpg', 'jpeg', 'webp'];
  if (uploadType === 'menuLogo' || uploadType === 'ticketSidebarLogo' || uploadType === 'ticketBackground' || uploadType === 'qrtikCover') {
    allowedFormats = [...allowedFormats, 'png'];
  }

  const cropSize = uploadType === 'menuLogo' || uploadType === 'ticketSidebarLogo' ? 1024 : 500;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      const fileNameParts = file.name.split('.');
      const extension = fileNameParts[fileNameParts.length - 1].toLowerCase();

      if (allowedFormats.includes(extension)) {
        setSelectedFile(file);
        setFileError(null);
        if (uploadType !== 'menuLogo' && uploadType !== 'ticketSidebarLogo' && uploadType !== 'ticketBackground' && uploadType !== 'qrtikCover') { // Logo/kapak tipleri için kırpıcıyı gösterme
          const reader = new FileReader();
          reader.onload = () => {
            if (typeof reader.result === 'string') {
              setImageSrc(reader.result);
              setShowCropper(true);
            }
          };
          reader.readAsDataURL(file);
        } else {
          setImageSrc(null); // Menü logosu için kırpıcı önizlemesi yok
          setShowCropper(false);
        }
      } else {
        setSelectedFile(null);
        setImageSrc(null);
        setShowCropper(false);
        setFileError(t('qrMenuSettingsModal.imageUpload.errors.invalidFormat', { formats: allowedFormats.join(', ') }));
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    }
  };

  const onCropComplete = useCallback((croppedArea: any, croppedAreaPixelsValue: any) => {
    setCroppedAreaPixels(croppedAreaPixelsValue);
  }, []);

  const handleUploadClick = async () => {
    if (uploadType === 'menuLogo' || uploadType === 'ticketSidebarLogo' || uploadType === 'ticketBackground' || uploadType === 'qrtikCover') {
      if (selectedFile) {
        try {
          setImageUploading(true); // Prop olarak gelen fonksiyonu kullan
          await onUpload(selectedFile);
          setSelectedFile(null);
          setFileError(null);
          if (fileInputRef.current) fileInputRef.current.value = '';
          onClose(); 
        } catch (error) {
          setFileError(t('qrMenuSettingsModal.imageUpload.errors.logoUploadFailed'));
          console.error('Logo yükleme hatası:', error);
          setImageUploading(false); // Hata durumunda false yap
        } finally {
          // setImageUploading(false); // Başarılı veya başarısız, işlem bitince false olmalı - bu onUpload'u yapan yerde ele alınabilir.
        }
      } else {
        setFileError(t('qrMenuSettingsModal.imageUpload.errors.fileNotSelected'));
      }
      return;
    }

    if (imageSrc && croppedAreaPixels) {
      try {
        setImageUploading(true); // Prop olarak gelen fonksiyonu kullan
        const croppedImageBlob = await getCroppedImg(imageSrc, croppedAreaPixels, uploadType);
        const originalFileName = selectedFile?.name || 'cropped-image.jpg';
        const fileExtension = originalFileName.split('.').pop() || 'jpg';
        const croppedFile = new File(
          [croppedImageBlob], 
          `cropped-${Date.now()}.${fileExtension}`, 
          { type: `image/${fileExtension === 'jpg' ? 'jpeg' : fileExtension}` }
        );
        await onUpload(croppedFile);
        setSelectedFile(null);
        setImageSrc(null);
        setShowCropper(false);
        setFileError(null);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        if (fileInputRef.current) fileInputRef.current.value = '';
        // onClose(); // Bu zaten onUpload içinde çağrılıyor (QRMenuSettingsModal.uploadImageToFirebase içinde)
      } catch (error) {
        setFileError(t('qrMenuSettingsModal.imageUpload.errors.cropFailed'));
        console.error('Kırpma hatası:', error);
        setImageUploading(false); // Hata durumunda false yap
      } finally {
        // setImageUploading(false); // Başarılı veya başarısız, işlem bitince false olmalı - bu onUpload'u yapan yerde ele alınabilir.
      }
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setImageSrc(null);
    setShowCropper(false);
    setFileError(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  // Yükle butonu için disabled durumu
  const isUploadDisabled = () => {
    if (uploadType === 'menuLogo' || uploadType === 'ticketSidebarLogo') {
      return !selectedFile || !!fileError || loading;
    }
    return !imageSrc || !!fileError || loading || !croppedAreaPixels; // Kırpma için croppedAreaPixels da gerekli
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          minHeight: showCropper ? 700 : 'auto',
          maxHeight: showCropper ? '90vh' : 'auto'
        }
      }}
    >
      <DialogTitle>
        {(uploadType === 'menuLogo' || uploadType === 'ticketBackground' || uploadType === 'ticketSidebarLogo' || uploadType === 'qrtikCover')
          ? t('qrMenuSettingsModal.imageUpload.titleNoCrop')
          : t('qrMenuSettingsModal.imageUpload.titleProduct')}
        <IconButton
          aria-label={t('qrMenuSettingsModal.imageUpload.close')}
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {uploadType !== 'menuLogo' && uploadType !== 'ticketBackground' && uploadType !== 'ticketSidebarLogo' && uploadType !== 'qrtikCover' && showCropper && imageSrc ? (
          <Box sx={{ my: 2, height: 550, position: 'relative' }}>
            <Box sx={{ position: 'relative', width: '100%', height: 500, backgroundColor: '#f0f0f0' }}>
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
                objectFit="contain"
                showGrid={true}
                style={{ 
                  containerStyle: { 
                    width: '100%', 
                    height: '100%' 
                  },
                  cropAreaStyle: {
                    border: '2px solid #fff',
                    boxShadow: '0 0 0 9999em rgba(0, 0, 0, 0.5)'
                  }
                }}
              />
            </Box>
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
              <ZoomOutIcon sx={{ color: 'text.secondary' }} />
              <Slider
                value={zoom}
                min={1}
                max={3}
                step={0.1}
                aria-labelledby="Zoom"
                onChange={(e, zoom) => setZoom(zoom as number)}
                sx={{ mx: 2, flexGrow: 1 }}
              />
              <ZoomInIcon sx={{ color: 'text.secondary' }} />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
              {t('qrMenuSettingsModal.imageUpload.cropHint', { size: cropSize })}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ my: 2 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept={allowedFormats.map(f => `.${f}`).join(',')}
              onChange={handleFileChange}
              style={{ display: 'none' }}
              id="product-image-input"
            />
            <label htmlFor="product-image-input">
              <Button
                variant="contained"
                component="span"
                fullWidth
                startIcon={<ImageIcon />}
              >
                {selectedFile && (uploadType === 'menuLogo' || uploadType === 'ticketBackground' || uploadType === 'ticketSidebarLogo' || uploadType === 'qrtikCover') ? selectedFile.name : t('qrMenuSettingsModal.imageUpload.selectImage')}
              </Button>
            </label>
            {fileError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {fileError}
              </Alert>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {(uploadType === 'menuLogo' || uploadType === 'ticketBackground' || uploadType === 'ticketSidebarLogo' || uploadType === 'qrtikCover') 
                ? t('qrMenuSettingsModal.imageUpload.noteAsIs', { formats: allowedFormats.join(', ') })
                : t('qrMenuSettingsModal.imageUpload.noteCropped', { formats: allowedFormats.join(', '), size: cropSize })}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {t('qrMenuSettingsModal.imageUpload.cancel')}
        </Button>
        <Button 
          onClick={handleUploadClick} 
          color="primary" 
          variant="contained"
          disabled={isUploadDisabled()}
        >
          {loading ? <CircularProgress size={24} /> : t('qrMenuSettingsModal.imageUpload.upload')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const QRMenuSettingsModal: React.FC<QRMenuSettingsModalProps> = ({
  open,
  onClose,
  menuId,
  companyId,
  menuName,
  moduleType = 'qr',
  onImageUpload,
  isKitchenMode = false
}) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('salesSystem');
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width: 1499px)');
  const isTicketMode = moduleType === 'ticket';
  const moduleTitle = isTicketMode ? t('qrMenuSettings.moduleTitle.ticket') : t('qrMenuSettings.moduleTitle.qr');
  const productsTabLabel = isTicketMode ? t('qrMenuSettings.tabs.tickets') : t('qrMenuSettings.tabs.products');
  const [mobileTabDrawerOpen, setMobileTabDrawerOpen] = useState(false);
  
  const [settings, setSettings] = useState<QRMenuSettings | null>(null);
  const isQrTikTemplate = settings?.appearance?.template === 'qrtik';
  const [socialPlatformToAdd, setSocialPlatformToAdd] = useState<SocialPlatformKey>('website');
  const [initialSettings, setInitialSettings] = useState<QRMenuSettings | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  
  // 📱 Mobil için tab tanımları
  const tabs = useMemo(() => {
    const allTabs = [
      { id: 0, label: productsTabLabel, icon: <InventoryIcon fontSize="small" />, kitchenOnly: false },
      { id: 1, label: t('qrMenuSettings.tabs.templates'), icon: <ViewCarouselIcon fontSize="small" />, kitchenOnly: false },
      { id: 2, label: t('qrMenuSettings.tabs.extraSettings'), icon: <TuneIcon fontSize="small" />, kitchenOnly: false },
      { id: 3, label: t('qrMenuSettings.tabs.personnel'), icon: <PeopleIcon fontSize="small" />, kitchenOnly: true },
      { id: 4, label: t('qrMenuSettings.tabs.printing'), icon: <PrintIcon fontSize="small" />, kitchenOnly: true },
      { id: 5, label: t('qrMenuSettings.tabs.kitchenScreen'), icon: <KitchenIcon fontSize="small" />, kitchenOnly: true },
    ];
    
    if (isKitchenMode) {
      // Kitchen mode'da sadece Personel, Yazdırma, Mutfak Ekranı göster
      return allTabs.filter(tab => tab.kitchenOnly).map((tab, index) => ({ ...tab, id: index }));
    }
    if (isTicketMode) {
      // Ticket modunda mutfak sekmeleri gosterilmez.
      return allTabs.filter(tab => !tab.kitchenOnly);
    }
    return allTabs;
  }, [isKitchenMode, isTicketMode, productsTabLabel, t]);
  
  const currentTab = tabs.find(tab => tab.id === activeTab) || tabs[0];
  const ticketMinOffsetValue = useMemo(() => {
    const raw = settings?.appearance?.ticketMinReservationOffsetDays;
    if (raw === undefined || raw === null) return null;
    const parsed = typeof raw === 'number' ? raw : Number(raw);
    if (Number.isNaN(parsed)) return null;
    return Math.max(0, Math.min(365, Math.floor(parsed)));
  }, [settings?.appearance?.ticketMinReservationOffsetDays]);
  const ticketEarliestReservationDate = useMemo(() => {
    if (ticketMinOffsetValue === null) return null;
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + ticketMinOffsetValue);
    return d;
  }, [ticketMinOffsetValue]);
  const ticketEarliestReservationIso = useMemo(() => {
    if (!ticketEarliestReservationDate) return '';
    const yyyy = ticketEarliestReservationDate.getFullYear();
    const mm = String(ticketEarliestReservationDate.getMonth() + 1).padStart(2, '0');
    const dd = String(ticketEarliestReservationDate.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }, [ticketEarliestReservationDate]);
  const todayIso = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }, []);
  const ticketDateRangeValidationText = useMemo(() => {
    const maxRaw = settings?.appearance?.ticketMaxReservationDate;
    if (!maxRaw || !ticketEarliestReservationIso) return '';
    if (maxRaw < ticketEarliestReservationIso) {
      return 'En geç rezervasyon tarihi, en erken rezervasyon tarihinden önce olamaz.';
    }
    return '';
  }, [settings?.appearance?.ticketMaxReservationDate, ticketEarliestReservationIso]);
  // Online bilet fiyatlandirma modu. Mevcut firmalari bozmamak icin varsayilan 'dynamic' (yas bandi + sezon).
  const ticketPricingMode = useMemo<'product' | 'dynamic'>(() => {
    return settings?.appearance?.ticketPricingMode === 'product' ? 'product' : 'dynamic';
  }, [settings?.appearance?.ticketPricingMode]);
  const isDynamicPricingMode = ticketPricingMode === 'dynamic';
  const [allInventoryItems, setAllInventoryItems] = useState<InventoryItem[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [productSelectionModalOpen, setProductSelectionModalOpen] = useState(false);
  
  // Menü özellikleri için state'ler
  const [menus, setMenus] = useState<Menu[]>([]);
  const [activeMenuTab, setActiveMenuTab] = useState<number>(0);
  const [menuDialogOpen, setMenuDialogOpen] = useState<boolean>(false);
  const [newMenuName, setNewMenuName] = useState<string>('');
  const [selectedProductsForMenu, setSelectedProductsForMenu] = useState<{ [menuId: string]: string[] }>({});
  const [initialSelectedProductsForMenu, setInitialSelectedProductsForMenu] = useState<{ [menuId: string]: string[] }>({});
  const [currentMenuForProducts, setCurrentMenuForProducts] = useState<string | null>(null);
  const [relatedProductsByProductId, setRelatedProductsByProductId] = useState<{ [productId: string]: string[] }>({});
  const [initialRelatedProductsByProductId, setInitialRelatedProductsByProductId] = useState<{ [productId: string]: string[] }>({});
  const [relatedProductSelectionModalOpen, setRelatedProductSelectionModalOpen] = useState(false);
  const [currentProductForRelatedSelection, setCurrentProductForRelatedSelection] = useState<string | null>(null);
  
  // Her menü için ayrı sayfalama state'leri
  const [menuPagination, setMenuPagination] = useState<{ [menuId: string]: { page: number; rowsPerPage: number } }>({});
  
  // Resim yükleme için state'ler
  const [currentProductId, setCurrentProductId] = useState<string | null>(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [currentUploadType, setCurrentUploadType] = useState<'product' | 'menu' | 'menuLogo' | 'ticketBackground' | 'ticketSidebarLogo' | 'qrtikCover'>('product');
  
  // Menü adı değiştirme için state
  const [currentMenuName, setCurrentMenuName] = useState<string>('');
  
  // Şube bilgileri için state'ler (kayıt sırasında korunması için)
  const [currentBranchId, setCurrentBranchId] = useState<string | null>(null);
  const [currentBranchName, setCurrentBranchName] = useState<string | null>(null);
  const [effectiveMenuPriceMap, setEffectiveMenuPriceMap] = useState<Record<string, number>>({});
  const selectedSocialPlatforms = useMemo(() => {
    const links = settings?.appearance?.socialLinks || {};
    return SOCIAL_PLATFORM_OPTIONS.filter((option) =>
      Object.prototype.hasOwnProperty.call(links, option.key)
    );
  }, [settings?.appearance?.socialLinks]);
  const availableSocialPlatforms = useMemo(() => {
    const selectedKeys = new Set(selectedSocialPlatforms.map((item) => item.key));
    return SOCIAL_PLATFORM_OPTIONS.filter((option) => !selectedKeys.has(option.key));
  }, [selectedSocialPlatforms]);
  const handleAddSocialPlatform = useCallback(() => {
    setSettings((prev) => {
      if (!prev) return prev;
      const nextSocialLinks = {
        ...(prev.appearance?.socialLinks || {})
      };
      if (!Object.prototype.hasOwnProperty.call(nextSocialLinks, socialPlatformToAdd)) {
        nextSocialLinks[socialPlatformToAdd] = '';
      }
      return {
        ...prev,
        appearance: {
          ...(prev.appearance || {}),
          socialLinks: nextSocialLinks
        }
      };
    });
  }, [socialPlatformToAdd]);
  const handleRemoveSocialPlatform = useCallback((platform: SocialPlatformKey) => {
    setSettings((prev) => {
      if (!prev) return prev;
      const nextSocialLinks = {
        ...(prev.appearance?.socialLinks || {})
      };
      delete nextSocialLinks[platform];
      return {
        ...prev,
        appearance: {
          ...(prev.appearance || {}),
          socialLinks: nextSocialLinks
        }
      };
    });
  }, []);

  const loadBranchPriceOverrides = useCallback(async (
    activeCompanyId: string,
    activeBranchId: string,
    items: InventoryItem[]
  ) => {
    try {
      const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      const inventoryItemIds = items
        .map(i => i.id)
        .filter(id => guidRegex.test(id));

      if (inventoryItemIds.length === 0) return;

      const branchCandidatesSet = new Set<string>([activeBranchId]);
      try {
        const branchResponse = await mainApi.get(`/api/Branch/company/${activeCompanyId}`);
        const rawBranches = Array.isArray(branchResponse?.data)
          ? branchResponse.data
          : Array.isArray(branchResponse?.data?.branches)
            ? branchResponse.data.branches
            : [];

        const matched = rawBranches.find((b: any) => b?.id === activeBranchId || b?.firebaseId === activeBranchId);
        if (matched?.id) branchCandidatesSet.add(String(matched.id));
        if (matched?.firebaseId) branchCandidatesSet.add(String(matched.firebaseId));
      } catch {
        // Branch lookup başarısızsa mevcut branch id ile devam edilir.
      }

      const branchCandidates = Array.from(branchCandidatesSet);
      const merged: Record<string, number> = {};

      for (let i = 0; i < branchCandidates.length; i++) {
        const branchIdCandidate = branchCandidates[i];
        if (!branchIdCandidate) continue;
        try {
          const response = await accountingApi.post(`/api/InventoryBranchPrice/bulk-by-branch`, {
            companyId: activeCompanyId,
            branchId: branchIdCandidate,
            inventoryItemIds
          });

          const data = response?.data || {};
          Object.entries(data).forEach(([itemId, value]: [string, any]) => {
            const salePrice = Number(value?.salePrice ?? value?.SalePrice);
            if (!Number.isNaN(salePrice)) {
              merged[itemId.toLowerCase()] = salePrice;
            }
          });
        } catch {
          // Bir aday branch ID başarısız olsa da diğer adaylarla devam edilir.
        }
      }

      if (Object.keys(merged).length > 0) {
        setEffectiveMenuPriceMap(prev => ({ ...prev, ...merged }));
      }
    } catch (error) {
      console.warn('⚠️ Şube fiyat override yükleme hatası:', error);
    }
  }, []);

  // Personel ayarları için state'ler
  const [allPersonnel, setAllPersonnel] = useState<Personnel[]>([]);
  const [selectedPersonnelIds, setSelectedPersonnelIds] = useState<string[]>([]);
  const [selectedPersonnelForPermissions, setSelectedPersonnelForPermissions] = useState<string | null>(null);
  const [personnelPermissionsMap, setPersonnelPermissionsMap] = useState<{ [personnelId: string]: KitchenPersonnelPermissions }>({});
  const [personnelLoading, setPersonnelLoading] = useState(false);

  // Yazdırma ayarları için state'ler
  const [printingMode, setPrintingMode] = useState<'single' | 'category'>('single');
  const [defaultReceiptPrinter, setDefaultReceiptPrinter] = useState<DefinedPrinter | null>(null);
  const [definedPrinters, setDefinedPrinters] = useState<DefinedPrinter[]>([]);
  const [categoryPrinterAssignments, setCategoryPrinterAssignments] = useState<{ [categoryId: string]: string | null }>({});
  const [localPrinters, setLocalPrinters] = useState<DefinedPrinter[]>([]);
  const [loadingLocalPrinters, setLoadingLocalPrinters] = useState(false);
  const [localPrintersError, setLocalPrintersError] = useState<string | null>(null);
  const [printTestLoading, setPrintTestLoading] = useState<string | null>(null);
  const [receiptDisplaySettings, setReceiptDisplaySettings] = useState<ReceiptDisplaySettings>({
    companyName: '',
    companyAddress: '',
    taxNumber: '',
    showReceiptHeader: true,
    showVat: true,
    showDiscountInfo: false,
    showCashierName: false, // Kasiyer bölümü kapalı
    showReceiptNumber: false, // Fiş sırası kapalı
    receiptNumberResetValue: 999999,
    resetAtEndOfDay: true,
    printCopyCount: 1,
    productGrouping: 'category',
    standardFooterNote: 'Afiyet olsun!',
    thankYouMessage: 'Tekrar bekleriz.',
    receiptWidth: 80,
    characterLength: 32,
    marginTop: 2,
    marginBottom: 5,
    marginLeft: 5,
    marginRight: 5,
    headerType: 'text', // Varsayılan olarak metin başlık
    logoUrl: ''
  });
  // Kategori bazlı yazdırma için benzersiz kategori ID'leri ve kategori listesi
  const uniqueCategoryIds = useMemo(() => {
    // Seçili ürünlerin kategori ID'lerini topla
    const allCategoryIds = allInventoryItems
      .filter(item => selectedProducts.includes(item.id))
      .map(item => item.categoryId)
      .filter((catId): catId is string => !!catId);
    
    // Menülerdeki ürünlerin kategori ID'lerini de ekle
    Object.values(selectedProductsForMenu).forEach(menuProducts => {
      const menuCategoryIds = allInventoryItems
        .filter(item => menuProducts.includes(item.id))
        .map(item => item.categoryId)
        .filter((catId): catId is string => !!catId);
      allCategoryIds.push(...menuCategoryIds);
    });
    
    return Array.from(new Set(allCategoryIds));
  }, [selectedProducts, selectedProductsForMenu, allInventoryItems]);

  const categoriesToListForPrinting = useMemo(() => {
    // Kategori ID'lerini kategori adlarına çevir (categoryId aslında categoryName olarak geliyor)
    const categories = uniqueCategoryIds
      .map(catId => ({
        id: catId,
        name: catId, // QR Menu API'sinde categoryId aslında categoryName olarak geliyor
        parentId: null,
        level: 0,
        createdAt: new Date().toISOString()
      } as InventoryCategory))
      .sort((a, b) => a.name.localeCompare(b.name));
    
    console.log('🖨️ Kategori bazlı yazdırma için kategoriler:', categories);
    return categories;
  }, [uniqueCategoryIds]);
  
  // Receipt Settings Modal state
  const [receiptSettingsModalOpen, setReceiptSettingsModalOpen] = useState(false);

  // Mutfak ekranı ayarları için state'ler
  const [kitchenScreenSettings, setKitchenScreenSettings] = useState<KitchenScreenSettingsData>({
    orderCardColors: {
      pending: {
        '0-5': { backgroundColor: '#e8f5e8', borderColor: '#4caf50', timeColor: '#2e7d32' },
        '6-15': { backgroundColor: '#fff3e0', borderColor: '#ff9800', timeColor: '#f57c00' },
        '16-25': { backgroundColor: '#fce4ec', borderColor: '#e91e63', timeColor: '#c2185b' },
        '25+': { backgroundColor: '#ffebee', borderColor: '#f44336', timeColor: '#d32f2f' }
      },
      preparing: { backgroundColor: '#fff8e1', borderColor: '#ffc107', timeColor: '#f9a825' },
      ready: { backgroundColor: '#e8f5e8', borderColor: '#4caf50', timeColor: '#2e7d32' },
      delivered: { backgroundColor: '#f3e5f5', borderColor: '#9c27b0', timeColor: '#7b1fa2' },
      cancelled: { backgroundColor: '#fafafa', borderColor: '#9e9e9e', timeColor: '#616161' }
    },
    timeRanges: {
      range1: 5,   // 0-5 dakika
      range2: 15,  // 6-15 dakika  
      range3: 25   // 16-25 dakika
    }
  });

  // Şablon yönetimi için state'ler
  const [templateSaveModalOpen, setTemplateSaveModalOpen] = useState(false);
  const [templateApplyModalOpen, setTemplateApplyModalOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [selectedTemplateKey, setSelectedTemplateKey] = useState<string>('');
  const [savedTemplates, setSavedTemplates] = useState<{ [key: string]: any }>({});

  // Silme onay modalı için state'ler
  const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{
    type: 'menu' | 'product' | 'menuProduct';
    menuId?: string;
    productId?: string;
    itemName?: string;
  } | null>(null);

  // Resim kütüphanesi modalı için state'ler
  const [isImageLibraryModalOpen, setIsImageLibraryModalOpen] = useState(false);
  const [selectedImageItem, setSelectedImageItem] = useState<InventoryItem | null>(null);

  // Yaygın yazı tipleri listesi
  const fontFamilies = [
    'Arial', 'Helvetica', 'Verdana', 'Tahoma', 'Trebuchet MS',
    'Times New Roman', 'Georgia', 'Garamond', 'Courier New', 'Brush Script MT',
    'Roboto', 'Open Sans', 'Montserrat', 'Lato', 'Raleway', 'Poppins',
    'Source Sans Pro', 'Ubuntu', 'Playfair Display', 'Merriweather'
  ];
  const [selectedFont, setSelectedFont] = useState<string>('Roboto');

  // Kayıtlı şablonları localStorage'dan yükleme
  useEffect(() => {
    if (open) {
      try {
        const savedTemplatesData = localStorage.getItem('qrMenuTemplates');
        if (savedTemplatesData) {
          const templates = JSON.parse(savedTemplatesData);
          setSavedTemplates(templates);
          console.log('✅ Kayıtlı şablonlar yüklendi:', Object.keys(templates).length);
        }
      } catch (error) {
        console.error('❌ Şablonlar yüklenirken hata:', error);
        setSavedTemplates({});
      }
    }
  }, [open]);

  // Personel verilerini Firebase'den yükleme (QuickSaleScreen benzeri)
  useEffect(() => {
    if (open && companyId) {
      const fetchPersonnelFromFirebase = async () => {
        setPersonnelLoading(true);
        try {
          // Faz 3: personel listesi SQL'den (personnelDataService).
          const sqlPersonnel = await getPersonnelSql(companyId);
          const fetchedAllPersonnel: Personnel[] = (sqlPersonnel || []).map((p: any) => ({
            id: p.id, // Firebase UID
            name: p.name || 'İsimsiz Personel',
            timestamp: p.timestamp || Date.now(),
          }));
          setAllPersonnel(fetchedAllPersonnel);
          console.log(`✅ SQL'den ${fetchedAllPersonnel.length} personel yüklendi`);
        } catch (error) {
          console.error('❌ Personel yükleme hatası:', error);
          setAllPersonnel([]);
        } finally {
          setPersonnelLoading(false);
        }
      };
      
      fetchPersonnelFromFirebase();
    }
  }, [open, companyId]);

  // Menü ayarlarını ve ürünleri yükleme
  useEffect(() => {
    if (open && menuId && companyId) {
      setLoading(true);
      setError(null);
      
      const fetchData = async () => {
        try {
          setEffectiveMenuPriceMap({});
          let loadedBranchId: string | null = null;
          console.log(`🔄 SQL'den menü tanımı yükleniyor: /api/QRMenu/menu-definitions/${menuId}`);
          
          const menuDefResponse = await mainApi.get(`/api/QRMenu/menu-definitions/${menuId}`);
          
          let loadedSettings: QRMenuSettings = {};
          let loadedProducts: string[] = [];
          
          const menuDefResult = menuDefResponse.data;
            if (menuDefResult.success && menuDefResult.data) {
              const menuDef = menuDefResult.data;
              console.log(`✅ SQL'den menü tanımı yüklendi:`, menuDef);
              
              // Menü adını set et
              setCurrentMenuName(menuDef.name || menuName || '');
              
              // Şube bilgilerini sakla (kayıt sırasında korunması için)
              setCurrentBranchId(menuDef.branchId || null);
              setCurrentBranchName(menuDef.branchName || null);
              loadedBranchId = menuDef.branchId || null;
              console.log(`🏢 Şube bilgileri yüklendi: branchId=${menuDef.branchId}, branchName=${menuDef.branchName}`);

              // QR müşteri tarafında görülen efektif fiyatları da çek.
              // Böylece paneldeki ürün listesi default yerine şube fiyatını yansıtabilir.
              try {
                const publicMenuResponse = await mainApi.get(`/api/QRMenu/v2/public/company/${companyId}/qr-menu-full/${menuId}`);
                const detailedProducts = publicMenuResponse?.data?.data?.detailedProducts;
                if (Array.isArray(detailedProducts)) {
                  const nextMap: Record<string, number> = {};
                  detailedProducts.forEach((p: any) => {
                    if (!p?.id) return;
                    const parsedPrice = Number(p.price);
                    if (!Number.isNaN(parsedPrice)) {
                      nextMap[String(p.id)] = parsedPrice;
                    }
                  });
                  setEffectiveMenuPriceMap(nextMap);
                }
              } catch (effectivePriceError) {
                console.warn('⚠️ Efektif menü fiyatları alınamadı, default fiyat gösterilecek:', effectivePriceError);
              }
              
              // Settings'i parse et
              if (menuDef.settings) {
                try {
                  loadedSettings = JSON.parse(menuDef.settings);
                  setSettings(loadedSettings);
                  setInitialSettings(JSON.parse(JSON.stringify(loadedSettings)));
                  
                  // Yazı tipi ayarını yükle
                  if (loadedSettings.appearance?.fontFamily) {
                    setSelectedFont(loadedSettings.appearance.fontFamily);
                  }
                  
                  // Personel ayarlarını yükle
                  if (loadedSettings.kitchenPersonnel) {
                    setSelectedPersonnelIds(loadedSettings.kitchenPersonnel.assignedPersonnelIds || []);
                    setPersonnelPermissionsMap(loadedSettings.kitchenPersonnel.personnelPermissions || {});
                    console.log(`✅ ${loadedSettings.kitchenPersonnel.assignedPersonnelIds?.length || 0} mutfak personeli yüklendi`);
                  }
                  
                  // Yazdırma ayarlarını yükle
                  if (loadedSettings.printingSettings) {
                    setPrintingMode(loadedSettings.printingSettings.printingMode || 'single');
                    setDefaultReceiptPrinter(loadedSettings.printingSettings.defaultReceiptPrinter || null);
                    setDefinedPrinters(loadedSettings.printingSettings.definedPrinters || []);
                    setCategoryPrinterAssignments(loadedSettings.printingSettings.categoryPrinterAssignments || {});
                    if (loadedSettings.printingSettings.receiptDisplaySettings) {
                      setReceiptDisplaySettings(loadedSettings.printingSettings.receiptDisplaySettings);
                    }
                    console.log(`✅ Yazdırma ayarları yüklendi`);
                  }
                  
                  // Mutfak ekranı ayarlarını yükle
                  if (loadedSettings.kitchenScreenSettings) {
                    setKitchenScreenSettings(loadedSettings.kitchenScreenSettings);
                    console.log(`✅ Mutfak ekranı ayarları yüklendi`);
                  }

                  const loadedRelatedMap = loadedSettings.relatedProductsByProductId || {};
                  setRelatedProductsByProductId(loadedRelatedMap);
                  setInitialRelatedProductsByProductId(JSON.parse(JSON.stringify(loadedRelatedMap)));
                } catch (parseError) {
                  console.warn('❌ Settings JSON parse hatası:', parseError);
                  loadedSettings = {};
                  setSettings({});
                  setInitialSettings({});
                  setRelatedProductsByProductId({});
                  setInitialRelatedProductsByProductId({});
                }
              }
              
              // ProductIds'i parse et
              if (menuDef.productIds) {
                try {
                  loadedProducts = JSON.parse(menuDef.productIds);
                  setSelectedProducts(loadedProducts);
                  console.log(`✅ ${loadedProducts.length} ürün ID'si yüklendi`);
                } catch (parseError) {
                  console.warn('❌ ProductIds JSON parse hatası:', parseError);
                  loadedProducts = [];
                  setSelectedProducts([]);
                }
              }
            } else {
              console.warn('❌ Menü tanımı bulunamadı:', menuDefResult.message);
              setCurrentMenuName(menuName || '');
              setSettings({});
              setInitialSettings({});
              setRelatedProductsByProductId({});
              setInitialRelatedProductsByProductId({});
            }
          
          // Sadece SQL'den BU MENU DEFINITION'a ait menüleri yükle
          try {
            console.log(`🔄 SQL'den BU menü tanımına ait alt menüler yükleniyor (MenuId: ${menuId})`);
            
            // ✅ YENİ: Sadece bu menü tanımındaki menüleri kullan (settings'ten)
            // Settings'te menus objesi varsa, bunlar bu menu definition'a ait menülerdir
            if (loadedSettings?.menus && Object.keys(loadedSettings.menus).length > 0) {
              console.log(`✅ Settings'ten ${Object.keys(loadedSettings.menus).length} alt menü yüklendi`);
                
              const settingsMenus = Object.entries(loadedSettings.menus).map(([menuId, menuData]: [string, any]) => {
                  const menu: Menu = {
                    id: menuId,
                  name: menuData.name || 'İsimsiz Menü',
                  products: menuData.products || []
                  };
                  
                if (menuData.imageUrl && menuData.imageUrl.trim()) {
                  menu.imageUrl = menuData.imageUrl;
                  }
                  
                  return menu;
                });
                
              setMenus(settingsMenus);

                // Her menünün sıralı ürünlerini state'e yükle
                const productsForMenu: { [menuId:string]: string[] } = {};
              settingsMenus.forEach((menu: Menu) => {
                  productsForMenu[menu.id] = menu.products || [];
                });
                
                setSelectedProductsForMenu(productsForMenu);
                setInitialSelectedProductsForMenu(JSON.parse(JSON.stringify(productsForMenu))); // Deep copy for change detection

              console.log('✅ Alt menüler ve sıralı ürünleri settings\'ten başarıyla yüklendi.');
              } else {
              console.log('ℹ️ Bu menü tanımında henüz alt menü bulunmuyor');
                setMenus([]); // Boş liste set et
            }
          } catch (sqlError) {
            console.error('❌ Alt menü yükleme hatası:', sqlError);
            setMenus([]); // Hata durumunda boş liste
          }
          
          // Tüm ürünleri hem ESKİ SYSTEM (QRMenuStockList) hem de YENİ SİSTEM (InventoryItems IsQRMenu=true) den al
          try {
            console.log('🔄 [HYBRİD SYSTEM] Hem eski hem yeni sistemden ürünler yükleniyor...');
            
            let allItems: InventoryItem[] = [];
            
            // 1️⃣ ESKİ SİSTEM: QRMenuStockList'ten ürünleri al
            try {
              console.log(`🔄 [ESKİ SİSTEM] QRMenuStockList'ten ürünler yükleniyor: /api/QRMenu/stocks`);
              const oldResponse = await mainApi.get(`/api/QRMenu/stocks`);
              const oldResult = oldResponse.data;
              if (oldResult.success && oldResult.data) {
                const oldItems: InventoryItem[] = oldResult.data.map((item: any) => ({
                  id: item.id,
                  name: item.name || 'İsimsiz Ürün',
                  categoryId: item.categoryName || null,
                  warehouseId: null,
                  salePrice: item.salePrice || 0,
                  useBranchPricing: item.useBranchPricing ?? item.UseBranchPricing ?? false,
                  stockCode: item.stockCode || '-',
                  barcode: item.barcode || '-',
                  imageUrl: item.imageUrl || undefined,
                  description: item.description || ''
                }));
                allItems = [...oldItems];
                console.log(`✅ [ESKİ SİSTEM] ${oldItems.length} ürün yüklendi (QRMenuStockList)`);
              } else {
                console.warn('⚠️ [ESKİ SİSTEM] Eski sistemden ürün alınamadı');
              }
            } catch (oldError) {
              console.warn('⚠️ [ESKİ SİSTEM] Eski sistem hatası (devam ediliyor):', oldError);
            }
            
            // 2️⃣ YENİ SİSTEM: InventoryItems'dan TÜM ürünleri al (IsQRMenu kontrolü kaldırıldı)
            try {
              console.log(`🔄 [YENİ SİSTEM] InventoryItems'dan TÜM ürünler yükleniyor: /api/Inventory/items`);
              const newResponse = await accountingApi.get(`/api/Inventory/items`);
              const newResult = newResponse.data;
              const rawItems = Array.isArray(newResult) 
                ? newResult 
                : (newResult.data && Array.isArray(newResult.data) ? newResult.data : null);
              
              if (rawItems) {
                const newItems: InventoryItem[] = rawItems.map((item: any) => ({
                  id: item.id,
                  name: item.name || 'İsimsiz Ürün',
                  categoryId: item.categoryId || item.categoryName || null,
                  warehouseId: null,
                  salePrice: item.salePrice || 0,
                  useBranchPricing: item.useBranchPricing ?? item.UseBranchPricing ?? false,
                  stockCode: item.stockCode || '-',
                  barcode: item.barcode || '-',
                  imageUrl: item.imageUrl || undefined,
                  description: item.description || ''
                }));
                
                const existingIds = new Set(allItems.map(item => item.id));
                const uniqueNewItems = newItems.filter(item => !existingIds.has(item.id));
                allItems = [...allItems, ...uniqueNewItems];
                
                console.log(`✅ [YENİ SİSTEM] ${newItems.length} ürün yüklendi (TÜM InventoryItems), ${uniqueNewItems.length} yeni ürün eklendi`);
              } else {
                console.warn('⚠️ [YENİ SİSTEM] Yeni sistemden ürün alınamadı (geçersiz yanıt formatı)');
              }
            } catch (newError) {
              console.warn('⚠️ [YENİ SİSTEM] Yeni sistem hatası (devam ediliyor):', newError);
            }
            
            // 3️⃣ Toplam sonuç
            setAllInventoryItems(allItems);
            console.log(`✅ [HYBRİD SYSTEM] TOPLAM ${allItems.length} ürün yüklendi (${allItems.length} benzersiz)`);

            if (loadedBranchId && allItems.length > 0) {
              await loadBranchPriceOverrides(companyId, loadedBranchId, allItems);
            }
            
            if (allItems.length === 0) {
              console.warn('⚠️ [HYBRİD SYSTEM] Her iki sistemden de ürün alınamadı');
            }
          } catch (apiError) {
            console.error('❌ [HYBRİD SYSTEM] Genel API hatası:', apiError);
            setAllInventoryItems([]);
          }
        } catch (err) {
          console.error("Menü ayarları veya ürünler yüklenirken hata:", err);
          setError(t('qrMenuSettingsModal.errors.dataLoadFailed'));
        } finally {
          setLoading(false);
        }
      };
      
      fetchData();
    }
  }, [open, menuId, companyId, loadBranchPriceOverrides]);

  const getDisplayPrice = useCallback((product: InventoryItem): number => {
    const effectivePrice = effectiveMenuPriceMap[product.id.toLowerCase()] ?? effectiveMenuPriceMap[product.id];
    if (typeof effectivePrice === 'number' && !Number.isNaN(effectivePrice)) {
      return effectivePrice;
    }
    return product.salePrice ?? 0;
  }, [effectiveMenuPriceMap]);

  // Tab değişikliği
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    // Mobil drawer'ı kapat
    if (isMobile) {
      setMobileTabDrawerOpen(false);
    }
  };

  // Sayfa değiştirme
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Sayfa başına satır sayısı değiştirme
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Menü sayfalama fonksiyonları
  const getMenuPagination = (menuId: string) => {
    return menuPagination[menuId] || { page: 0, rowsPerPage: 5 };
  };

  const handleMenuPageChange = (menuId: string, newPage: number) => {
    setMenuPagination(prev => ({
      ...prev,
      [menuId]: { ...getMenuPagination(menuId), page: newPage }
    }));
  };

  const handleMenuRowsPerPageChange = (menuId: string, newRowsPerPage: number) => {
    setMenuPagination(prev => ({
      ...prev,
      [menuId]: { page: 0, rowsPerPage: newRowsPerPage }
    }));
  };

  // Ürün seçme modalını açma
  const handleOpenProductSelectionModal = () => {
    setCurrentMenuForProducts(null); // Ana ürün listesi için seçim
    setProductSelectionModalOpen(true);
  };
  
  // Menü için ürün seçme modalını açma
  const handleOpenMenuProductSelectionModal = (menuId: string) => {
    setCurrentMenuForProducts(menuId);
    setProductSelectionModalOpen(true);
  };

  // Ürün silme fonksiyonu - Ana ürün listesinden - Onay modalını aç
  const handleOpenDeleteProductConfirm = (productId: string) => {
    const product = allInventoryItems.find(item => item.id === productId);
    setDeleteTarget({
      type: 'product',
      productId: productId,
      itemName: product?.name || 'Bilinmeyen Ürün'
    });
    setDeleteConfirmModalOpen(true);
  };

  // Ürün silme fonksiyonu - Ana ürün listesinden
  const handleRemoveProduct = (productId: string) => {
    setSelectedProducts(prev => prev.filter(id => id !== productId));
    setRelatedProductsByProductId(prev => {
      const next: { [productId: string]: string[] } = {};
      Object.entries(prev).forEach(([ownerId, relatedIds]) => {
        if (ownerId === productId) return;
        next[ownerId] = relatedIds.filter(id => id !== productId);
      });
      return next;
    });
    setSettings(prev => ({
      ...(prev || {}),
      products: (prev?.products || []).filter(id => id !== productId),
      relatedProductsByProductId: Object.entries(prev?.relatedProductsByProductId || {}).reduce((acc: { [productId: string]: string[] }, [ownerId, relatedIds]) => {
        if (ownerId === productId) return acc;
        acc[ownerId] = Array.isArray(relatedIds)
          ? relatedIds.filter(id => id !== productId)
          : [];
        return acc;
      }, {})
    }));
    
    // Modal'ı kapat
    setDeleteConfirmModalOpen(false);
    setDeleteTarget(null);
  };

  // Ürün silme fonksiyonu - Menü ürünlerinden - Onay modalını aç
  const handleOpenDeleteMenuProductConfirm = (menuId: string, productId: string) => {
    const product = allInventoryItems.find(item => item.id === productId);
    const menu = menus.find(m => m.id === menuId);
    setDeleteTarget({
      type: 'menuProduct',
      menuId: menuId,
      productId: productId,
      itemName: `"${product?.name || 'Bilinmeyen Ürün'}" ürününü "${menu?.name || 'menüden'}" menüsünden`
    });
    setDeleteConfirmModalOpen(true);
  };

  // Ürün silme fonksiyonu - Menü ürünlerinden
  const handleRemoveProductFromMenu = async (menuId: string, productId: string) => {
    try {
      const updatedProductIds = (selectedProductsForMenu[menuId] || []).filter(id => id !== productId);
      
      console.log(`🔄 Menüden ürün siliniyor: /api/QRMenu/menus/${menuId}/products`);
      
      const response = await mainApi.post(`/api/QRMenu/menus/${menuId}/products`, {
        productIds: updatedProductIds
      });
      
      console.log('✅ Ürün SQL\'den başarıyla silindi:', response.data);
      
      setSelectedProductsForMenu(prev => ({
        ...prev,
        [menuId]: updatedProductIds
      }));
      
      setMenus(prev => prev.map(menu => 
        menu.id === menuId 
          ? { ...menu, products: updatedProductIds } 
          : menu
      ));
      
      const menuToUpdate = menus.find(m => m.id === menuId);
      setSettings(prev => ({
        ...(prev || {}),
        menus: {
          ...(prev?.menus || {}),
          [menuId]: {
            id: menuId,
            name: menuToUpdate?.name || prev?.menus?.[menuId]?.name || 'İsimsiz Menü',
            products: updatedProductIds,
            ...(menuToUpdate?.imageUrl && { imageUrl: menuToUpdate.imageUrl })
          }
        }
      }));
      console.log(`✅ [SETTINGS UPDATE] settings.menus.${menuId}.products güncellendi (silme):`, updatedProductIds.length, 'ürün');
      
      setDeleteConfirmModalOpen(false);
      setDeleteTarget(null);
    } catch (err) {
      console.error('❌ Menü ürün silme API hatası:', err);
      setError(t('qrMenuSettingsModal.errors.productDeleteFailed'));
      setDeleteConfirmModalOpen(false);
      setDeleteTarget(null);
    }
  };

  // Seçili ürünleri güncelleme
  const handleSelectProducts = async (selectedIds: string[], selectedItems?: InventoryItem[]) => {
    // ✅ Seçilen ürünlerin bilgilerini allInventoryItems'a ekle (eğer yoksa)
    if (selectedItems && selectedItems.length > 0) {
      const existingIds = new Set(allInventoryItems.map(item => item.id));
      const newItems = selectedItems.filter(item => !existingIds.has(item.id));
      
      if (newItems.length > 0) {
        console.log(`✅ [INVENTORY UPDATE] ${newItems.length} yeni ürün allInventoryItems'a ekleniyor:`, newItems.map(i => i.name));
        setAllInventoryItems(prev => [...prev, ...newItems]);
      }
    }
    
    if (currentMenuForProducts) {
      // Menü için ürün seçimi - SQL'e kaydet
      try {
        console.log(`🔄 Menüye ürün ekleniyor: /api/QRMenu/menus/${currentMenuForProducts}/products`);
        console.log('🔍 Frontend\'den gönderilen ürün ID\'leri:', selectedIds);
        
        const response = await mainApi.post(`/api/QRMenu/menus/${currentMenuForProducts}/products`, {
          productIds: selectedIds
        });
        
        console.log('✅ Ürünler SQL\'e başarıyla eklendi:', response.data);
        
        setSelectedProductsForMenu(prev => ({
          ...prev,
          [currentMenuForProducts]: selectedIds
        }));
        
        setMenus(prev => prev.map(menu => 
          menu.id === currentMenuForProducts 
            ? { ...menu, products: selectedIds } 
            : menu
        ));
        
        const menuToUpdate = menus.find(m => m.id === currentMenuForProducts);
        setSettings(prev => ({
          ...(prev || {}),
          menus: {
            ...(prev?.menus || {}),
            [currentMenuForProducts]: {
              id: currentMenuForProducts,
              name: menuToUpdate?.name || prev?.menus?.[currentMenuForProducts]?.name || 'İsimsiz Menü',
              products: selectedIds,
              ...(menuToUpdate?.imageUrl && { imageUrl: menuToUpdate.imageUrl })
            }
          }
        }));
        console.log(`✅ [SETTINGS UPDATE] settings.menus.${currentMenuForProducts}.products güncellendi:`, selectedIds.length, 'ürün');
      } catch (err) {
        console.error('❌ Menü ürün ekleme API hatası:', err);
        setError(t('qrMenuSettingsModal.errors.productAddFailed'));
      }
    } else {
      // Ana ürün listesi için seçim
      setSelectedProducts(selectedIds);
      // settings'i de güncelle
      setSettings(prev => ({
        ...(prev || {}),
        products: selectedIds
      }));
    }
  };

  const handleOpenRelatedProductSelectionModal = (productId: string) => {
    setCurrentProductForRelatedSelection(productId);
    setRelatedProductSelectionModalOpen(true);
  };

  const handleSelectRelatedProducts = (selectedIds: string[]) => {
    if (!currentProductForRelatedSelection) return;

    // Ana ürünü yanı ürün listesine eklemeyi engelle.
    const sanitizedIds = selectedIds.filter(id => id !== currentProductForRelatedSelection);

    setRelatedProductsByProductId(prev => ({
      ...prev,
      [currentProductForRelatedSelection]: sanitizedIds
    }));

    setSettings(prev => ({
      ...(prev || {}),
      relatedProductsByProductId: {
        ...(prev?.relatedProductsByProductId || {}),
        [currentProductForRelatedSelection]: sanitizedIds
      }
    }));

    setRelatedProductSelectionModalOpen(false);
    setCurrentProductForRelatedSelection(null);
  };

  // Resim yükleme butonuna tıklama - Ürünler için (Resim Kütüphanesi Modalını Aç)
  const handleImageButtonClick = (event: React.MouseEvent, productId: string) => {
    event.stopPropagation();
    if (!companyId) {
      setError(t('qrMenuSettingsModal.errors.companyIdNotFoundUpload'));
      return;
    }
    
    // Ürün bilgilerini bul
    const product = allInventoryItems.find(item => item.id === productId);
    if (product) {
      setSelectedImageItem(product);
      setCurrentUploadType('product'); // Ürün resmi kaydı doğru branch'e gitsin
      setIsImageLibraryModalOpen(true);
    } else {
      setError(t('qrMenuSettingsModal.errors.productNotFound'));
    }
  };
  
  // Menü için resim yükleme butonuna tıklama (Resim Kütüphanesi Modalını Aç)
  const handleMenuImageButtonClick = (event: React.MouseEvent, menuIdForImage: string) => {
    event.stopPropagation();
    if (!companyId) {
      setError(t('qrMenuSettingsModal.errors.companyIdNotFoundUpload'));
      return;
    }
    
    // Menü bilgilerini bul
    const menu = menus.find(m => m.id === menuIdForImage);
    if (menu) {
      // Menü için geçici bir InventoryItem oluştur
      const menuAsItem: InventoryItem = {
        id: menu.id,
        name: menu.name,
        imageUrl: menu.imageUrl
      };
      setSelectedImageItem(menuAsItem);
      setCurrentUploadType('menu'); // Menü için kayıt ederken kullanacağız
      setIsImageLibraryModalOpen(true);
    } else {
      setError(t('qrMenuSettingsModal.errors.menuNotFound'));
    }
  };
  
  // Menü resmini silme
  const handleDeleteMenuImage = async (event: React.MouseEvent, menuIdForDelete: string) => {
    event.stopPropagation();
    if (!companyId) {
      setError(t('qrMenuSettingsModal.errors.companyIdNotFoundDelete'));
      return;
    }
    
    try {
      console.log(`🗑️ Menü resmi siliniyor: ${menuIdForDelete}`);
      
      // Local state'i güncelle (menus)
      setMenus(prev => prev.map(menu => menu.id === menuIdForDelete ? { ...menu, imageUrl: undefined } : menu));
      console.log(`✅ Menü resmi local state'den silindi: ${menuIdForDelete}`);
      
      // Settings state'ini de güncelle (hasChanges için)
      setSettings(prev => {
        const updatedMenus = { ...(prev?.menus || {}) };
        if (updatedMenus[menuIdForDelete]) {
          const { imageUrl, ...rest } = updatedMenus[menuIdForDelete];
          updatedMenus[menuIdForDelete] = rest;
        }
        
        return {
          ...(prev || {}),
          menus: updatedMenus
        };
      });
      console.log(`✅ Settings state'i güncellendi (menü resmi silindi): ${menuIdForDelete}`);
      
      // SQL'deki QRMenus tablosundan LogoUrl'i temizle
      try {
        console.log(`🔄 SQL'den menü logosu siliniyor: /api/QRMenu/menus/${menuIdForDelete}/logo`);
        
        const logoDeleteResponse = await mainApi.post(`/api/QRMenu/menus/${menuIdForDelete}/logo`, {
          logoUrl: null
        });
        console.log('✅ Menü logosu SQL\'den başarıyla silindi:', logoDeleteResponse.data);
      } catch (logoError) {
        console.error('❌ Menü logosu SQL silme API hatası:', logoError);
      }
    } catch (error) {
      console.error("❌ Menü resmi silme hatası:", error);
      setError(t('qrMenuSettingsModal.errors.menuImageDeleteFailed'));
    }
  };
  
  // Menü ekleme dialog'ını açma
  const handleOpenMenuDialog = () => {
    setNewMenuName('');
    setMenuDialogOpen(true);
  };
  
  // Menü ekleme dialog'ını kapatma
  const handleCloseMenuDialog = () => {
    setMenuDialogOpen(false);
  };
  
  // Yeni menü ekleme
  const handleAddMenu = async () => {
    if (!newMenuName.trim() || !companyId) return;
    
    try {
      // Yeni menü ID'si oluştur
      const newMenuId = `menu_${Date.now()}`;
      
      console.log(`🔄 Yeni menü SQL'e ekleniyor: /api/QRMenu/menus`);
      
      const menuData = {
        name: newMenuName.trim(),
        description: null,
        firebaseId: newMenuId,
        branchId: null,
        branchName: null,
        template: 'standard',
        primaryColor: '#1976d2',
        secondaryColor: null,
        fontFamily: 'Roboto',
        currencySymbol: '₺',
        showPrices: true,
        showImages: true,
        showDescriptions: true,
        showLogo: true,
        logoUrl: null,
        settings: null
      };
      
      const response = await mainApi.post(`/api/QRMenu/menus`, menuData);
      console.log('✅ Menü SQL\'e başarıyla eklendi:', response.data);
      
      const newMenu: Menu = {
        id: newMenuId,
        name: newMenuName.trim(),
        products: []
      };
      
      setMenus(prev => [...prev, newMenu]);
      setSelectedProductsForMenu(prev => ({
        ...prev,
        [newMenuId]: []
      }));
      
      setMenuDialogOpen(false);
      setActiveMenuTab(menus.length);
    } catch (err) {
      console.error('❌ Menü ekleme API hatası:', err);
      setError('Menü eklenirken bir hata oluştu.');
    }
  };
  
  // Menü sekmesi değişikliği
  const handleMenuTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveMenuTab(newValue);
  };
  
  // Menü sıralamasını değiştirme (sola/sağa)
  const handleMoveMenu = (menuId: string, direction: 'left' | 'right') => {
    const currentIndex = menus.findIndex(m => m.id === menuId);
    
    if (direction === 'left' && currentIndex > 0) {
      const newMenus = [...menus];
      [newMenus[currentIndex - 1], newMenus[currentIndex]] = [newMenus[currentIndex], newMenus[currentIndex - 1]]; // Swap
      setMenus(newMenus);
      
      // Aktif tab'ı da güncelle (eğer kaydırılan menü aktifse)
      if (activeMenuTab === currentIndex) {
        setActiveMenuTab(currentIndex - 1);
      } else if (activeMenuTab === currentIndex - 1) {
        setActiveMenuTab(currentIndex);
      }
      
      console.log(`⬅️ [MENU ORDER] Menü sola kaydırıldı: ${menus[currentIndex].name}`);
    } else if (direction === 'right' && currentIndex < menus.length - 1) {
      const newMenus = [...menus];
      [newMenus[currentIndex + 1], newMenus[currentIndex]] = [newMenus[currentIndex], newMenus[currentIndex + 1]]; // Swap
      setMenus(newMenus);
      
      // Aktif tab'ı da güncelle (eğer kaydırılan menü aktifse)
      if (activeMenuTab === currentIndex) {
        setActiveMenuTab(currentIndex + 1);
      } else if (activeMenuTab === currentIndex + 1) {
        setActiveMenuTab(currentIndex);
      }
      
      console.log(`➡️ [MENU ORDER] Menü sağa kaydırıldı: ${menus[currentIndex].name}`);
    }
  };
  
  // Menü silme onay modalını aç
  const handleOpenDeleteMenuConfirm = (menuId: string, menuName: string) => {
    setDeleteTarget({
      type: 'menu',
      menuId: menuId,
      itemName: menuName
    });
    setDeleteConfirmModalOpen(true);
  };

  // Resim kütüphanesi modalını aç
  const handleOpenImageLibraryModal = () => {
    setIsImageLibraryModalOpen(true);
  };

  // Resim kütüphanesi modalını kapat
  const handleCloseImageLibraryModal = () => {
    setIsImageLibraryModalOpen(false);
    setSelectedImageItem(null);
  };

  // Kütüphaneden seçilen resimleri kaydet
  const handleSaveImages = async (selectedUrls: string[]) => {
    console.log('📸 [IMAGE LIBRARY] Seçilen resimler:', selectedUrls);
    
    if (!selectedImageItem || !companyId) {
      setError("Resim kaydedilemedi: Ürün veya şirket bilgisi eksik");
      handleCloseImageLibraryModal();
      return;
    }
    
    try {
      const imageUrlString = selectedUrls.length > 0 ? selectedUrls.join(',') : '';
      
      // Menü logosu mu, menü resmi mi, yoksa ürün resmi mi?
      if (currentUploadType === 'menuLogo') {
        // Menü logosu - Settings'e kaydet
        setSettings(prev => ({
          ...(prev || {}),
          appearance: {
            ...(prev?.appearance || {}),
            menuLogoUrl: selectedUrls.length > 0 ? selectedUrls[0] : undefined // Sadece ilk resmi al
          }
        }));
        
        console.log(`✅ [IMAGE LIBRARY] Menü logosu güncellendi`);
      } else if (currentUploadType === 'ticketBackground') {
        setSettings(prev => ({
          ...(prev || {}),
          appearance: {
            ...(prev?.appearance || {}),
            ticketBackgroundImageUrl: selectedUrls.length > 0 ? selectedUrls[0] : undefined
          }
        }));
        console.log(`✅ [IMAGE LIBRARY] Ticket background güncellendi`);
      } else if (currentUploadType === 'ticketSidebarLogo') {
        setSettings(prev => ({
          ...(prev || {}),
          appearance: {
            ...(prev?.appearance || {}),
            ticketSidebarLogoUrl: selectedUrls.length > 0 ? selectedUrls[0] : undefined
          }
        }));
        console.log(`✅ [IMAGE LIBRARY] Ticket sidebar logo güncellendi`);
      } else if (currentUploadType === 'qrtikCover') {
        setSettings(prev => ({
          ...(prev || {}),
          appearance: {
            ...(prev?.appearance || {}),
            qrtikCoverImageUrl: selectedUrls.length > 0 ? selectedUrls[0] : undefined
          }
        }));
        console.log(`✅ [IMAGE LIBRARY] QrTık cover güncellendi`);
      } else if (currentUploadType === 'menu') {
        // Menü resmi - SQL QRMenus tablosuna LogoUrl olarak kaydedilir (aşağıda)
        console.log(`✅ [IMAGE LIBRARY] Menü resmi güncelleniyor: ${selectedImageItem.name}`);
        
        // Local state'i güncelle (menus)
        setMenus(prev => prev.map(m => 
          m.id === selectedImageItem.id 
            ? { ...m, imageUrl: imageUrlString } 
            : m
        ));
        
        // ✅ ÖNEMLİ: settings.menus'u da güncelle (API Settings JSON'dan okuyor!)
        const menuToUpdate = menus.find(m => m.id === selectedImageItem.id);
        setSettings(prev => ({
          ...(prev || {}),
          menus: {
            ...(prev?.menus || {}),
            [selectedImageItem.id]: {
              id: selectedImageItem.id,
              name: menuToUpdate?.name || prev?.menus?.[selectedImageItem.id]?.name || 'İsimsiz Menü',
              products: prev?.menus?.[selectedImageItem.id]?.products || selectedProductsForMenu[selectedImageItem.id] || [],
              imageUrl: imageUrlString
            }
          }
        }));
        console.log(`✅ [SETTINGS UPDATE] settings.menus.${selectedImageItem.id}.imageUrl güncellendi`);
        
        // ✅ SQL'deki QRMenus tablosuna LogoUrl'i de güncelle
        try {
          console.log(`🔄 SQL'e menü logosu kaydediliyor: /api/QRMenu/menus/${selectedImageItem.id}/logo`);
          
          const logoUpdateResponse = await mainApi.post(`/api/QRMenu/menus/${selectedImageItem.id}/logo`, {
            logoUrl: imageUrlString
          });
          console.log('✅ Menü logosu SQL\'e başarıyla kaydedildi:', logoUpdateResponse.data);
        } catch (logoError) {
          console.error('❌ Menü logosu SQL kaydetme API hatası:', logoError);
        }
      } else {
        // Ürün resmi - SQL InventoryItems.ImageUrl olarak kaydedilir.
        // NOT: items/{id}/update tüm alanları override ettiği için önce mevcut
        // ürünün tam DTO'sunu çekip yalnızca imageUrl'i değiştirerek geri gönderiyoruz.
        // Eski sistem (QRMenuStockList) ürünleri InventoryItems'da olmadığı için
        // 404 alınırsa sessizce atlanır (sadece local state güncellenir).
        try {
          const detailResponse = await accountingApi.get(`/api/Inventory/items/${selectedImageItem.id}`);
          const fullItem = detailResponse.data;
          if (fullItem && fullItem.id) {
            await accountingApi.post(`/api/Inventory/items/${selectedImageItem.id}/update`, {
              ...fullItem,
              imageUrl: imageUrlString || null,
              imageUrls: imageUrlString ? imageUrlString.split(',').filter(Boolean) : null,
            });
            console.log(`✅ [IMAGE LIBRARY] Ürün resmi SQL'e kaydedildi: ${selectedImageItem.name}`);
          }
        } catch (productImageError) {
          console.warn('⚠️ [IMAGE LIBRARY] Ürün resmi SQL kaydı atlandı (eski sistem ürünü olabilir):', productImageError);
        }
        
        // Local state'i güncelle
        setAllInventoryItems(prev => prev.map(item => 
          item.id === selectedImageItem.id 
            ? { ...item, imageUrl: imageUrlString } 
            : item
        ));
      }
      
      setError(null);
    } catch (error) {
      console.error('❌ [IMAGE LIBRARY] Resim kaydetme hatası:', error);
      setError('Resim kaydedilirken bir hata oluştu');
    } finally {
      handleCloseImageLibraryModal();
    }
  };
  
  // Menü silme
  const handleRemoveMenu = async (menuId: string) => {
    if (!companyId) {
      setError("Şirket ID bulunamadı. Menü silinemez.");
      return;
    }
    
    try {
      console.log(`🗑️ Menü siliniyor: ${menuId}`);
      
      const response = await mainApi.post(`/api/QRMenu/menus/${menuId}/delete`);
      console.log('✅ Menü SQL\'den başarıyla silindi:', response.data);
      
      setMenus(prev => prev.filter(menu => menu.id !== menuId));
      
      setSelectedProductsForMenu(prev => {
        const updated = { ...prev };
        delete updated[menuId];
        return updated;
      });
      
      if (activeMenuTab >= menus.length - 1) {
        setActiveMenuTab(Math.max(0, menus.length - 2));
      }
      
      console.log(`✅ Menü başarıyla silindi: ${menuId}`);
    } catch (err) {
      console.error('❌ Menü silme API hatası:', err);
      setError('Menü silinirken bir hata oluştu.');
    }
    
    // Modal'ı kapat
    setDeleteConfirmModalOpen(false);
    setDeleteTarget(null);
  };

  // Ayarları kaydetme
  const handleSave = async () => {
    if (!menuId || !companyId) {
      setError("Menü ID veya Şirket ID bulunamadı.");
      return;
    }
    
    setSaving(true);
    setError(null);
    
    try {
      // Seçili ürünleri ve menüleri ayarlara ekle
      const updatedSettings = {
        ...(settings || {}),
        mode: isTicketMode ? 'ticket' : ((settings || {}).mode || 'qr'),
        products: selectedProducts,
        relatedProductsByProductId: relatedProductsByProductId,
        menus: menus.reduce((acc: { [key: string]: Menu }, menu) => {
          acc[menu.id] = {
            id: menu.id,
            name: menu.name,
            products: selectedProductsForMenu[menu.id] || [],
            // undefined değerleri kaydetmemek için kontrol et
            ...(menu.imageUrl && { imageUrl: menu.imageUrl })
          };
          return acc;
        }, {}),
        // Mutfak personeli ayarlarını ekle
        kitchenPersonnel: {
          assignedPersonnelIds: selectedPersonnelIds,
          personnelPermissions: personnelPermissionsMap
        },
        // Yazdırma ayarlarını ekle
        printingSettings: {
          printingMode: printingMode,
          defaultReceiptPrinter: defaultReceiptPrinter,
          definedPrinters: definedPrinters,
          categoryPrinterAssignments: categoryPrinterAssignments,
          receiptDisplaySettings: receiptDisplaySettings
        },
        // Mutfak ekranı ayarlarını ekle
        kitchenScreenSettings: kitchenScreenSettings
      };
      
      console.log('🖨️ Kaydedilecek yazdırma ayarları:', {
        printingMode,
        defaultReceiptPrinter,
        definedPrinters,
        categoryPrinterAssignments
      });
      
      console.log('🔄 [SIRALAMA-KAYDET] Kaydedilecek menü sıralama ayarları:');
      console.log('📋 [SIRALAMA-KAYDET] selectedProductsForMenu:', selectedProductsForMenu);
      console.log('📋 [SIRALAMA-KAYDET] updatedSettings.menus:', updatedSettings.menus);
      console.log('📋 [SIRALAMA-KAYDET] Tam updatedSettings:', updatedSettings);
      
      // Firebase'e mutfak personeli yetkilerini kaydet
      await saveKitchenPermissionsToFirebase();
      
      // Menü tanımını SQL'e kaydet
      console.log(`🔄 SQL'e menü tanımı kaydediliyor: /api/QRMenu/menu-definitions/${menuId}`);
      
      const menuDefinitionData = {
        firebaseId: menuId,
        name: currentMenuName.trim() || 'İsimsiz Menü',
        description: null,
        branchId: currentBranchId, // Mevcut şube ID'sini koru
        branchName: currentBranchName, // Mevcut şube adını koru
        createdAt: new Date().toISOString(),
        productIds: JSON.stringify(selectedProducts),
        // Ticket kayitlari listede dogru bolumde kalmasi icin ana template alanini koru.
        template: isTicketMode ? 'ticket' : (settings?.appearance?.template || 'standard'),
        subMenus: JSON.stringify(menus.reduce((acc: { [key: string]: any }, menu) => {
          acc[menu.id] = {
            id: menu.id,
            name: menu.name,
            products: selectedProductsForMenu[menu.id] || [],
            ...(menu.imageUrl && { imageUrl: menu.imageUrl })
          };
          return acc;
        }, {})),
        settings: JSON.stringify(updatedSettings)
      };
      
      console.log('🔄 [SIRALAMA-KAYDET] SQL\'e gönderilecek menuDefinitionData:');
      console.log('📋 [SIRALAMA-KAYDET] subMenus (JSON):', menuDefinitionData.subMenus);
      console.log('📋 [SIRALAMA-KAYDET] settings (JSON):', menuDefinitionData.settings);
      console.log('🏢 [ŞUBE-KAYDET] Şube bilgileri korunuyor:', {
        branchId: menuDefinitionData.branchId,
        branchName: menuDefinitionData.branchName,
        currentBranchId: currentBranchId,
        currentBranchName: currentBranchName
      });
      
      const response = await mainApi.post(`/api/QRMenu/menu-definitions`, menuDefinitionData);
      console.log('✅ Menü tanımı SQL\'e başarıyla kaydedildi:', response.data);
      
      if (updatedSettings.printingSettings) {
        const printingSettingsKey = `qrMenuPrintingSettings_${menuId}`;
        localStorage.setItem(printingSettingsKey, JSON.stringify(updatedSettings.printingSettings));
        console.log('✅ Yazdırma ayarları localStorage\'a kaydedildi:', printingSettingsKey);
      }
      
      setSettings(updatedSettings);
      setInitialSettings(JSON.parse(JSON.stringify(updatedSettings)));
      setInitialRelatedProductsByProductId(JSON.parse(JSON.stringify(relatedProductsByProductId)));
      
      onClose();
    } catch (err) {
      console.error("❌ Menü tanımı kaydetme API hatası:", err);
      setError("Ayarlar kaydedilirken bir hata oluştu.");
    } finally {
      setSaving(false);
    }
  };

  // Değişiklik olup olmadığını kontrol et
  const printingChanges = {
    printingMode: printingMode !== (initialSettings?.printingSettings?.printingMode || 'single'),
    defaultReceiptPrinter: JSON.stringify(defaultReceiptPrinter) !== JSON.stringify(initialSettings?.printingSettings?.defaultReceiptPrinter || null),
    definedPrinters: JSON.stringify(definedPrinters) !== JSON.stringify(initialSettings?.printingSettings?.definedPrinters || []),
    categoryPrinterAssignments: JSON.stringify(categoryPrinterAssignments) !== JSON.stringify(initialSettings?.printingSettings?.categoryPrinterAssignments || {}),
    receiptDisplaySettings: JSON.stringify(receiptDisplaySettings) !== JSON.stringify(initialSettings?.printingSettings?.receiptDisplaySettings || {})
  };
  
  const hasPrintingChanges = Object.values(printingChanges).some(changed => changed);
  
  if (hasPrintingChanges) {
    console.log('🔄 Yazdırma ayarlarında değişiklik tespit edildi:', printingChanges);
  }
  
  // Mutfak ekranı ayarları değişiklik kontrolü
  const hasKitchenScreenChanges = JSON.stringify(kitchenScreenSettings) !== JSON.stringify(initialSettings?.kitchenScreenSettings || {
    orderCardColors: {
      pending: {
        '0-5': { backgroundColor: '#e8f5e8', borderColor: '#4caf50', timeColor: '#2e7d32' },
        '6-15': { backgroundColor: '#fff3e0', borderColor: '#ff9800', timeColor: '#f57c00' },
        '16-25': { backgroundColor: '#fce4ec', borderColor: '#e91e63', timeColor: '#c2185b' },
        '25+': { backgroundColor: '#ffebee', borderColor: '#f44336', timeColor: '#d32f2f' }
      },
      preparing: { backgroundColor: '#fff8e1', borderColor: '#ffc107', timeColor: '#f9a825' },
      ready: { backgroundColor: '#e8f5e8', borderColor: '#4caf50', timeColor: '#2e7d32' },
      delivered: { backgroundColor: '#f3e5f5', borderColor: '#9c27b0', timeColor: '#7b1fa2' },
      cancelled: { backgroundColor: '#fafafa', borderColor: '#9e9e9e', timeColor: '#616161' }
    },
    timeRanges: { range1: 5, range2: 15, range3: 25 }
  });
  
  // Menü sırası değişikliğini kontrol et
  const initialMenusFromSettings = initialSettings?.menus ? Object.values(initialSettings.menus) : [];
  const hasMenuOrderChanged = JSON.stringify(menus.map(m => m.id)) !== JSON.stringify(initialMenusFromSettings.map((m: any) => m.id));
  
  if (hasMenuOrderChanged) {
    console.log('🔄 Menü sıralamasında değişiklik tespit edildi');
    console.log('📋 Mevcut sıra:', menus.map(m => m.name));
    console.log('📋 Başlangıç sırası:', initialMenusFromSettings.map((m: any) => m.name));
  }
  
  const hasChanges = JSON.stringify(settings) !== JSON.stringify(initialSettings) || 
                     currentMenuName.trim() !== (menuName || '') ||
                     // Personel seçimi değişikliklerini kontrol et
                     JSON.stringify(selectedPersonnelIds) !== JSON.stringify(initialSettings?.kitchenPersonnel?.assignedPersonnelIds || []) ||
                     JSON.stringify(personnelPermissionsMap) !== JSON.stringify(initialSettings?.kitchenPersonnel?.personnelPermissions || {}) ||
                     // Menü ürün sırası değişikliğini kontrol et
                     JSON.stringify(selectedProductsForMenu) !== JSON.stringify(initialSelectedProductsForMenu) ||
                     // Yan ürün eşleşmelerini kontrol et
                     JSON.stringify(relatedProductsByProductId) !== JSON.stringify(initialRelatedProductsByProductId) ||
                     // Yazdırma ayarları değişikliklerini kontrol et
                     hasPrintingChanges ||
                     // Mutfak ekranı ayarları değişikliklerini kontrol et
                     hasKitchenScreenChanges ||
                     // Menü sırası değişikliğini kontrol et
                     hasMenuOrderChanged;

  // Seçili ürünleri filtrele
  const selectedProductItems = allInventoryItems.filter(item => 
    selectedProducts.includes(item.id)
  );

  // Sayfalandırılmış ürünler
  const paginatedProducts = selectedProductItems.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Menüyü görüntüleme fonksiyonu
  const handleViewMenu = () => {
    if (menuId) {
      // Modal'ı kapat ve QR menüyü görüntüle
      onClose();
      navigate(`/qr-menu/${menuId}`);
    }
  };

  // Şablon seçimi işlemi
  const handleTemplateSelect = (templateId: string) => {
    setSettings(prev => ({
      ...(prev || {}),
      appearance: {
        ...(prev?.appearance || {}),
        template: templateId
      }
    }));
  };

  // Menü logosu yükleme butonuna tıklama (Resim Kütüphanesi Modalını Aç)
  const handleMenuLogoUploadClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!companyId || !menuId) {
      setError("Şirket veya Menü ID bulunamadı. Logo yüklenemez.");
      return;
    }
    
    // Menü logosu için geçici bir InventoryItem oluştur
    const menuLogoAsItem: InventoryItem = {
      id: menuId,
      name: currentMenuName || 'Menü Logosu',
      imageUrl: settings?.appearance?.menuLogoUrl
    };
    setSelectedImageItem(menuLogoAsItem);
    setCurrentUploadType('menuLogo');
    setIsImageLibraryModalOpen(true);
  };

  const handleTicketBackgroundUploadClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!companyId || !menuId) {
      setError("Şirket veya Menü ID bulunamadı. Arkaplan resmi yüklenemez.");
      return;
    }

    const backgroundAsItem: InventoryItem = {
      id: menuId,
      name: `${currentMenuName || 'Ticket'} Arkaplan`,
      imageUrl: settings?.appearance?.ticketBackgroundImageUrl
    };
    setSelectedImageItem(backgroundAsItem);
    setCurrentUploadType('ticketBackground');
    setIsImageLibraryModalOpen(true);
  };

  const handleTicketSidebarLogoUploadClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!companyId || !menuId) {
      setError("Şirket veya Menü ID bulunamadı. Sol logo yüklenemez.");
      return;
    }

    const sidebarLogoAsItem: InventoryItem = {
      id: menuId,
      name: `${currentMenuName || 'Ticket'} Sol Logo`,
      imageUrl: settings?.appearance?.ticketSidebarLogoUrl
    };
    setSelectedImageItem(sidebarLogoAsItem);
    setCurrentUploadType('ticketSidebarLogo');
    setIsImageLibraryModalOpen(true);
  };

  const handleQrTikCoverUploadClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!companyId || !menuId) {
      setError("Şirket veya Menü ID bulunamadı. Kapak resmi yüklenemez.");
      return;
    }

    const qrtikCoverAsItem: InventoryItem = {
      id: menuId,
      name: `${currentMenuName || 'QrTık'} Kapak`,
      imageUrl: settings?.appearance?.qrtikCoverImageUrl
    };
    setSelectedImageItem(qrtikCoverAsItem);
    setCurrentUploadType('qrtikCover');
    setIsImageLibraryModalOpen(true);
  };

  // Menü logosu silme
  const [deleteLogoConfirmOpen, setDeleteLogoConfirmOpen] = useState(false);
  const [deletingLogo, setDeletingLogo] = useState(false);

  const handleDeleteMenuLogo = async () => {
    if (!companyId || !menuId) return;
    setDeletingLogo(true);
    try {
      // Logo artık Image Service'te (SQL) tutuluyor; sadece menü ayarındaki referansı temizliyoruz.
      setSettings(prev => ({
        ...(prev || {}),
        appearance: {
          ...(prev?.appearance || {}),
          menuLogoUrl: undefined,
        }
      }));

      console.log('✅ Menü logosu state\'den silindi');
    } catch (error) {
      console.error('❌ Menü logosu silme hatası:', error);
      setError('Menü logosu silinirken bir hata oluştu.');
    } finally {
      setDeletingLogo(false);
      setDeleteLogoConfirmOpen(false);
    }
  };

  // Personel işlemleri için handler fonksiyonları
  const handlePersonnelSelectionChange = (newSelectedIds: string[]) => {
    setSelectedPersonnelIds(newSelectedIds);
    
    // Seçili personel listesinden çıkarılan personelin izinlerini temizle
    const removedPersonnelIds = selectedPersonnelIds.filter(id => !newSelectedIds.includes(id));
    if (removedPersonnelIds.length > 0) {
      setPersonnelPermissionsMap(prev => {
        const updated = { ...prev };
        removedPersonnelIds.forEach(id => {
          delete updated[id];
        });
        return updated;
      });
      
      // Eğer şu an seçili olan personel kaldırıldıysa, seçimi temizle
      if (selectedPersonnelForPermissions && removedPersonnelIds.includes(selectedPersonnelForPermissions)) {
        setSelectedPersonnelForPermissions(null);
      }
    }
  };

  const handleSelectPersonnelForPermissions = (personnelId: string) => {
    setSelectedPersonnelForPermissions(personnelId);
    
    // Eğer bu personel için henüz izin ayarları yoksa, varsayılan değerleri oluştur
    if (!personnelPermissionsMap[personnelId]) {
      setPersonnelPermissionsMap(prev => ({
        ...prev,
        [personnelId]: {
          accessKitchenScreen: true,
          viewAllOrders: true,
          updateOrderStatus: true,
          cancelOrders: false,
          showPrintButton: true, // Varsayılan olarak yazdırma butonu göster
          listReadyOrders: true, // Varsayılan olarak hazır siparişleri listele
          waiterMode: false, // Varsayılan olarak garson modu kapalı
          autoPrint: false, // Varsayılan olarak otomatik yazdırma kapalı
          showPrices: true // Varsayılan olarak fiyatları göster
        }
      }));
    }
  };

  const handlePermissionChange = (personnelId: string, permission: string, value: boolean) => {
    setPersonnelPermissionsMap(prev => ({
      ...prev,
      [personnelId]: {
        ...prev[personnelId],
        [permission]: value
      }
    }));
  };

  // Faz 3: mutfak personeli yetkilerini SQL'e kaydet (SystemUserScreenPermissions screenType='kitchenScreens').
  const saveKitchenPermissionsToFirebase = async () => {
    if (!companyId || !menuId) return;
    
    try {
      const items = selectedPersonnelIds
        .filter((personnelId) => !!personnelPermissionsMap[personnelId])
        .map((personnelId) => ({
          firebaseUid: personnelId,
          screenType: 'kitchenScreens',
          screenId: String(menuId),
          isEnabled: true,
          settings: personnelPermissionsMap[personnelId],
        }));
      if (items.length > 0) {
        await bulkSetScreenPermissions(items);
      }
      console.log('✅ Tüm mutfak personeli yetkileri SQL\'e kaydedildi');
    } catch (error) {
      console.error('❌ Mutfak yetkileri kaydetme hatası:', error);
      throw error;
    }
  };

  // Yazdırma ayarları için fonksiyonlar
  const handlePrintingModeChange = (event: React.ChangeEvent<HTMLInputElement>, newMode: string) => {
    setPrintingMode(newMode as 'single' | 'category');
  };

  const handleSetDefaultPrinter = (printer: DefinedPrinter) => {
    console.log('🖨️ Varsayılan yazıcı ayarlanıyor:', printer);
    setDefaultReceiptPrinter(printer);
    if (!definedPrinters.find(p => p.id === printer.id)) {
      setDefinedPrinters(prev => [...prev, printer]);
    }
    console.log('✅ Varsayılan yazıcı state\'e kaydedildi:', printer);
  };

  const handleRemoveDefaultPrinter = () => {
    setDefaultReceiptPrinter(null);
  };

  const handleCategoryPrinterChange = (categoryId: string, printerId: string | null) => {
    setCategoryPrinterAssignments(prev => ({
      ...prev,
      [categoryId]: printerId
    }));
  };

  const handleRefreshLocalPrinters = async () => {
    setLoadingLocalPrinters(true);
    setLocalPrintersError(null);
    
    try {
      const response = await axios.get('http://localhost:3003/api/yazicilariGetir');
      if (response.data && Array.isArray(response.data)) {
        const printers: DefinedPrinter[] = response.data.map((printer: any) => ({
          id: printer.name || printer.id,
          name: printer.name || printer.id
        }));
        setLocalPrinters(printers);
        console.log('✅ Yerel yazıcılar yüklendi:', printers.length);
      } else {
        setLocalPrintersError('Yazıcı listesi alınamadı.');
      }
    } catch (error: any) {
      console.error('❌ Yerel yazıcılar yüklenirken hata:', error);
      setLocalPrintersError('Yerel yazıcı servisi bağlantı hatası. Local-service çalışıyor mu?');
    } finally {
      setLoadingLocalPrinters(false);
    }
  };

  // Yazdırma sekmesi açıldığında otomatik olarak yerel yazıcıları yükle
  useEffect(() => {
    if (open && activeTab === 4 && localPrinters.length === 0 && !loadingLocalPrinters) {
      console.log('🖨️ Yazdırma sekmesi açıldı, yerel yazıcılar otomatik yükleniyor...');
      handleRefreshLocalPrinters();
    }
  }, [open, activeTab, localPrinters.length, loadingLocalPrinters]);

  const handlePrintTestPage = async (printerId: string, printerName: string) => {
    setPrintTestLoading(printerId);
    try {
      await printTestPage(printerName);
      console.log('✅ Test sayfası yazdırıldı:', printerName);
    } catch (error: any) {
      console.error('❌ Test sayfası yazdırma hatası:', error);
      setError(`Test sayfası yazdırılırken hata oluştu: ${error.message}`);
    } finally {
      setPrintTestLoading(null);
    }
  };

  const handleOpenReceiptSettingsModal = () => {
    console.log('🔧 Fiş ayarları modalı açılıyor...');
    setReceiptSettingsModalOpen(true);
  };

  // Fiş ayarları modalını kapat
  const handleCloseReceiptSettingsModal = () => {
    setReceiptSettingsModalOpen(false);
  };

  // Fiş ayarlarını kaydet
  const handleSaveReceiptSettings = (newSettings: ReceiptDisplaySettings) => {
    console.log('💾 Fiş ayarları kaydediliyor:', newSettings);
    setReceiptDisplaySettings(newSettings);
  };

  const handleResetReceiptNumber = () => {
    if (menuId) {
      localStorage.removeItem(`qrMenuReceiptNumber_${menuId}`);
      localStorage.removeItem(`qrMenuReceiptDate_${menuId}`);
      console.log('✅ Adisyon numarası sıfırlandı');
    }
  };

  // Menüdeki ürünleri sıralama
  const handleMoveProduct = (menuId: string, productId: string, direction: 'up' | 'down') => {
    const productIds = selectedProductsForMenu[menuId] || [];
    const index = productIds.indexOf(productId);

    if (direction === 'up' && index > 0) {
      const newProductIds = [...productIds];
      [newProductIds[index - 1], newProductIds[index]] = [newProductIds[index], newProductIds[index - 1]]; // Swap
      
      setSelectedProductsForMenu(prev => ({
        ...prev,
        [menuId]: newProductIds
      }));
    } else if (direction === 'down' && index < productIds.length - 1) {
      const newProductIds = [...productIds];
      [newProductIds[index + 1], newProductIds[index]] = [newProductIds[index], newProductIds[index + 1]]; // Swap

      setSelectedProductsForMenu(prev => ({
        ...prev,
        [menuId]: newProductIds
      }));
    }
  };

  // Şablon kaydetme fonksiyonu
  const handleSaveTemplate = () => {
    if (!templateName.trim()) {
      setError(t('qrMenuSettingsModal.templateErrors.nameRequired'));
      return;
    }

    try {
      // Kayıt edilecek ayarları hazırla (personel ve yazdırma ayarları hariç)
      const templateData = {
        // Ürünler ve menüler
        products: selectedProducts,
        menus: menus.reduce((acc: { [key: string]: Menu }, menu) => {
          acc[menu.id] = {
            id: menu.id,
            name: menu.name,
            products: selectedProductsForMenu[menu.id] || [],
            ...(menu.imageUrl && { imageUrl: menu.imageUrl })
          };
          return acc;
        }, {}),
        selectedProductsForMenu: selectedProductsForMenu,
        relatedProductsByProductId: relatedProductsByProductId,
        
        // Görünüm ayarları
        appearance: settings?.appearance || {},
        
        // Menü adı
        menuName: currentMenuName,
        
        // Mutfak ekranı ayarları
        kitchenScreenSettings: kitchenScreenSettings,
        
        // Kayıt tarihi
        createdAt: new Date().toISOString(),
        createdBy: companyId
      };

      // Mevcut şablonları al
      const existingTemplates = { ...savedTemplates };
      
      // Yeni şablonu ekle
      existingTemplates[templateName.trim()] = templateData;
      
      // localStorage'a kaydet
      localStorage.setItem('qrMenuTemplates', JSON.stringify(existingTemplates));
      
      // State'i güncelle
      setSavedTemplates(existingTemplates);
      
      console.log('✅ Şablon kaydedildi:', templateName.trim());
      
      // Modal'ı kapat ve form'u temizle
      setTemplateSaveModalOpen(false);
      setTemplateName('');
      
      // Başarı mesajı (error state'ini geçici olarak kullanıyoruz)
      setError(null);
      
    } catch (error) {
      console.error('❌ Şablon kaydetme hatası:', error);
      setError(t('qrMenuSettingsModal.templateErrors.saveFailed'));
    }
  };

  // Şablon uygulama fonksiyonu
  const handleApplyTemplate = () => {
    if (!selectedTemplateKey) {
      setError(t('qrMenuSettingsModal.templateErrors.selectTemplate'));
      return;
    }

    try {
      const template = savedTemplates[selectedTemplateKey];
      if (!template) {
        setError(t('qrMenuSettingsModal.templateErrors.templateNotFound'));
        return;
      }

      console.log('🔄 Şablon uygulanıyor:', selectedTemplateKey);

      // Ürünleri uygula
      if (template.products) {
        setSelectedProducts(template.products);
      }

      // Menüleri uygula
      if (template.menus) {
        const menusArray = Object.values(template.menus) as Menu[];
        setMenus(menusArray);
      }

      // Menü ürün sıralarını uygula
      if (template.selectedProductsForMenu) {
        setSelectedProductsForMenu(template.selectedProductsForMenu);
      }

      if (template.relatedProductsByProductId) {
        setRelatedProductsByProductId(template.relatedProductsByProductId);
      }

      // Görünüm ayarlarını uygula
      if (template.appearance) {
        setSettings(prev => ({
          ...(prev || {}),
          appearance: template.appearance,
          ...(template.relatedProductsByProductId ? { relatedProductsByProductId: template.relatedProductsByProductId } : {})
        }));
        
        // Font ayarını da güncelle
        if (template.appearance.fontFamily) {
          setSelectedFont(template.appearance.fontFamily);
        }
      }

      // Menü adını uygula
      if (template.menuName) {
        setCurrentMenuName(template.menuName);
      }

      // Mutfak ekranı ayarlarını uygula
      if (template.kitchenScreenSettings) {
        setKitchenScreenSettings(template.kitchenScreenSettings);
      }

      console.log('✅ Şablon başarıyla uygulandı:', selectedTemplateKey);
      
      // Modal'ı kapat ve seçimi temizle
      setTemplateApplyModalOpen(false);
      setSelectedTemplateKey('');
      setError(null);
      
    } catch (error) {
      console.error('❌ Şablon uygulama hatası:', error);
      setError(t('qrMenuSettingsModal.templateErrors.applyFailed'));
    }
  };

  // Şablon silme fonksiyonu
  const handleDeleteTemplate = (templateKey: string) => {
    try {
      const updatedTemplates = { ...savedTemplates };
      delete updatedTemplates[templateKey];
      
      localStorage.setItem('qrMenuTemplates', JSON.stringify(updatedTemplates));
      setSavedTemplates(updatedTemplates);
      
      console.log('✅ Şablon silindi:', templateKey);
    } catch (error) {
      console.error('❌ Şablon silme hatası:', error);
      setError(t('qrMenuSettingsModal.templateErrors.deleteFailed'));
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth 
      fullScreen={isMobile}
      PaperProps={{ sx: { height: isMobile ? '100vh' : '80vh' } }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'primary.main', color: 'white', py: 1, px: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <SettingsIcon sx={{ mr: 0.8, fontSize: '1.25rem' }}/>
          <Typography variant={isMobile ? 'body2' : 'subtitle1'} sx={{ maxWidth: isMobile ? 200 : 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {isMobile
              ? t('qrMenuSettings.titleMobile', { moduleTitle })
              : t('qrMenuSettings.titleDesktop', { moduleTitle, menu: menuName || menuId })}
          </Typography>
          <GuideButton guideKey="sales.qrMenuSettings" size="small" color="inherit" />
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
          <CloseIcon fontSize="small"/>
        </IconButton>
      </DialogTitle>
      
      <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column', height: isMobile ? 'calc(100vh - 116px)' : 'calc(80vh - 116px)', overflow: 'hidden' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
          {/* 💻 MASAÜSTÜ: Normal Tabs */}
          {!isMobile && (
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange} 
              aria-label={t('qrMenuSettings.tabsAriaLabel', { moduleTitle })}
              variant="fullWidth"
              indicatorColor="primary"
              textColor="primary"
            >
              {!isKitchenMode && <Tab label={productsTabLabel} sx={{textTransform: 'none', fontSize: '0.8rem'}} />}
              {!isKitchenMode && <Tab label={t('qrMenuSettings.tabs.templates')} sx={{textTransform: 'none', fontSize: '0.8rem'}} />}
              {!isKitchenMode && <Tab label={t('qrMenuSettings.tabs.extraSettings')} sx={{textTransform: 'none', fontSize: '0.8rem'}} />}
              {!isTicketMode && <Tab label={t('qrMenuSettings.tabs.personnel')} sx={{textTransform: 'none', fontSize: '0.8rem'}} />}
              {!isTicketMode && <Tab label={t('qrMenuSettings.tabs.printing')} sx={{textTransform: 'none', fontSize: '0.8rem'}} />}
              {!isTicketMode && <Tab label={t('qrMenuSettings.tabs.kitchenScreen')} sx={{textTransform: 'none', fontSize: '0.8rem'}} />}
            </Tabs>
          )}
          
          {/* 📱 MOBİL: Dropdown Menü Butonu */}
          {isMobile && (
            <Button
              fullWidth
              variant="text"
              onClick={() => setMobileTabDrawerOpen(true)}
              sx={{
                py: 1.5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                textTransform: 'none',
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {currentTab?.icon}
                <Typography variant="body2" fontWeight="bold">
                  {currentTab?.label}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>{t('qrMenuSettings.changeTab')}</Typography>
                <MenuIcon fontSize="small" />
              </Box>
            </Button>
          )}
        </Box>
        
        {/* 📱 MOBİL: Tab Seçim Drawer */}
        <Drawer
          anchor="bottom"
          open={mobileTabDrawerOpen}
          onClose={() => setMobileTabDrawerOpen(false)}
          ModalProps={{
            sx: { zIndex: 1400 } // Dialog'un üstünde görünsün (Dialog default: 1300)
          }}
          PaperProps={{
            sx: {
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              maxHeight: '60vh',
              zIndex: 1401,
            }
          }}
        >
          <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white' }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Bölüm Seçin
            </Typography>
          </Box>
          <List sx={{ py: 0 }}>
            {tabs.map((tab) => (
              <ListItemButton
                key={tab.id}
                selected={activeTab === tab.id}
                onClick={() => handleTabChange({} as React.SyntheticEvent, tab.id)}
                sx={{
                  py: 1.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&.Mui-selected': {
                    bgcolor: 'white',
                    '&:hover': {
                      bgcolor: 'grey.50',
                    }
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: activeTab === tab.id ? 'primary.main' : 'text.secondary' }}>
                  {tab.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={tab.label} 
                  primaryTypographyProps={{ 
                    fontWeight: activeTab === tab.id ? 'bold' : 'normal',
                    color: activeTab === tab.id ? 'primary.main' : 'text.primary'
                  }}
                />
                {activeTab === tab.id && (
                  <Typography variant="caption" color="primary" fontWeight="bold" sx={{ ml: 1 }}>
                    ✓ Seçili
                  </Typography>
                )}
              </ListItemButton>
            ))}
          </List>
        </Drawer>

        <Box sx={{flexGrow: 1, overflow: 'auto', p: 2, height: '100%'}}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', p: 2 }}>
              <CircularProgress size={30}/>
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ m: 1.5 }}>{error}</Alert>
          ) : (
            <>
              {!isKitchenMode && activeTab === 0 && (
                <Paper elevation={0} sx={{p: 2, minHeight: '500px'}}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">{productsTabLabel}</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {!isTicketMode && (
                        <Button 
                          variant="outlined"
                          startIcon={<MenuBookIcon />}
                          onClick={handleOpenMenuDialog}
                          size="small"
                          color="secondary"
                        >
                          Menü Ekle
                        </Button>
                      )}
                      <Button 
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleOpenProductSelectionModal}
                        size="small"
                      >
                        {isTicketMode ? t('qrMenuSettingsModal.menus.buttons.addTicketType') : t('qrMenuSettingsModal.menus.buttons.addStandaloneProduct')}
                      </Button>
                    </Box>
                  </Box>
                  
                  {/* Menü Sekmeleri */}
                  {menus.length > 0 && (
                    <Box sx={{ mt: 2, mb: 2 }}>
                      {/* Menü Sıralama Bilgisi */}
                      <Box sx={{ 
                        display: { xs: 'none', md: 'flex' }, // Mobilde gizle
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        mb: 1,
                        px: 1
                      }}>
                        <Typography variant="caption" color="text.secondary">
                          {t('qrMenuSettingsModal.menus.reorderHint')}
                        </Typography>
                      </Box>
                      
                    <Box sx={{ 
                      borderBottom: 1, 
                      borderColor: 'divider', 
                      display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}>
                      {/* 💻 MASAÜSTÜ: Normal Tabs */}
                      {!isMobile && (
                        <Tabs
                          value={activeMenuTab}
                          onChange={handleMenuTabChange}
                          variant="scrollable"
                          scrollButtons="auto"
                          allowScrollButtonsMobile
                          sx={{ 
                            flexGrow: 1,
                            '.MuiTab-root': { 
                              textTransform: 'none', 
                              minHeight: 'auto',
                              py: 1
                            }
                          }}
                        >
                          {menus.map((menu, index) => (
                            <Tab
                              key={menu.id}
                              label={(
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <RestaurantMenuIcon sx={{ fontSize: '0.9rem' }} />
                                    <span>{menu.name}</span>
                                  <IconButton 
                                    size="small" 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                        handleOpenDeleteMenuConfirm(menu.id, menu.name);
                                    }}
                                      sx={{ p: 0.2, ml: 0.5 }}
                                  >
                                      <CloseIcon sx={{ fontSize: '0.875rem' }} />
                                  </IconButton>
                                </Box>
                              )}
                            />
                          ))}
                        </Tabs>
                      )}
                      
                      {/* 📱 MOBİL: Dropdown Select */}
                      {isMobile && (
                        <FormControl size="small" sx={{ flexGrow: 1, minWidth: 150 }}>
                          <InputLabel id="mobile-menu-select-label">{t('qrMenuSettingsModal.menus.selectMenu')}</InputLabel>
                          <Select
                            labelId="mobile-menu-select-label"
                            value={activeMenuTab}
                            label={t('qrMenuSettingsModal.menus.selectMenu')}
                            onChange={(e) => {
                              const newValue = e.target.value as number;
                              handleMenuTabChange({} as React.SyntheticEvent, newValue);
                            }}
                          >
                            {menus.map((menu, index) => (
                              <MenuItem key={menu.id} value={index}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', justifyContent: 'space-between' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <RestaurantMenuIcon sx={{ fontSize: '1rem' }} />
                                    <span>{menu.name}</span>
                                  </Box>
                                  <IconButton 
                                    size="small" 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleOpenDeleteMenuConfirm(menu.id, menu.name);
                                    }}
                                    sx={{ p: 0.2 }}
                                  >
                                    <CloseIcon sx={{ fontSize: '0.875rem' }} />
                                  </IconButton>
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                        
                        {/* Menü Sıralama Butonları - Sadece Masaüstünde */}
                        {!isMobile && menus.length > 1 && (
                          <Box sx={{ 
                            display: 'flex', 
                            gap: 0.5, 
                            borderLeft: 1, 
                            borderColor: 'divider',
                            pl: 1,
                            pr: 0.5
                          }}>
                            <Tooltip title={t('qrMenuSettingsModal.menus.tooltips.moveMenuLeft')}>
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    const currentMenu = menus[activeMenuTab];
                                    if (currentMenu) {
                                      handleMoveMenu(currentMenu.id, 'left');
                                    }
                                  }}
                                  disabled={activeMenuTab === 0}
                                  color="primary"
                                  sx={{ p: 0.5 }}
                                >
                                  <ArrowBackIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title={t('qrMenuSettingsModal.menus.tooltips.moveMenuRight')}>
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    const currentMenu = menus[activeMenuTab];
                                    if (currentMenu) {
                                      handleMoveMenu(currentMenu.id, 'right');
                                    }
                                  }}
                                  disabled={activeMenuTab === menus.length - 1}
                                  color="primary"
                                  sx={{ p: 0.5 }}
                                >
                                  <ArrowForwardIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}
                  
                  {/* Menü İçeriği */}
                  {menus.length > 0 ? (
                    menus.map((menu, index) => {
                      // Menü ürünleri
                      const menuProductIds = selectedProductsForMenu[menu.id] || [];
                      const menuProductItems = menuProductIds
                        .map(id => allInventoryItems.find(item => item.id === id))
                        .filter((item): item is InventoryItem => !!item);
                      
                      // Her menü için ayrı sayfalama
                      const menuPaginationData = getMenuPagination(menu.id);
                      const paginatedMenuProducts = menuProductItems.slice(
                        menuPaginationData.page * menuPaginationData.rowsPerPage,
                        menuPaginationData.page * menuPaginationData.rowsPerPage + menuPaginationData.rowsPerPage
                      );
                      
                      return (
                        <Box
                          key={menu.id}
                          role="tabpanel"
                          hidden={activeMenuTab !== index}
                          id={`menu-tabpanel-${menu.id}`}
                          aria-labelledby={`menu-tab-${menu.id}`}
                          sx={{ mt: 2, minHeight: '250px' }}
                        >
                          {activeMenuTab === index && (
                            <Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: isMobile ? 'wrap' : 'nowrap', gap: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  {/* Menü adı - Sadece masaüstünde görünür */}
                                  {!isMobile && (
                                    <Typography variant="subtitle1" fontWeight="bold">
                                      {t('qrMenuSettingsModal.menus.menuTitle', { name: menu.name })}
                                    </Typography>
                                  )}
                                  {/* Menü resmi */}
                                  <Box sx={{ ml: isMobile ? 0 : 2, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                    {menu.imageUrl ? (
                                      <>
                                        <Tooltip title={t('qrMenuSettingsModal.menus.tooltips.updateMenuImage')}>
                                          <Avatar 
                                            src={menu.imageUrl}
                                            alt={menu.name}
                                            variant="rounded"
                                            sx={{ 
                                              width: 40, 
                                              height: 40,
                                              border: '1px solid #eee',
                                              cursor: 'pointer'
                                            }}
                                            onClick={(e) => handleMenuImageButtonClick(e, menu.id)}
                                          />
                                        </Tooltip>
                                        <Tooltip title={t('qrMenuSettingsModal.menus.tooltips.deleteMenuImage')}>
                                          <IconButton
                                            size="small"
                                            color="error"
                                            onClick={(e) => handleDeleteMenuImage(e, menu.id)}
                                            sx={{ p: 0.5 }}
                                          >
                                            <DeleteIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </>
                                    ) : (
                                      <Tooltip title={t('qrMenuSettingsModal.menus.tooltips.addMenuImage')}>
                                        <Avatar 
                                          variant="rounded"
                                          sx={{ 
                                            width: 40, 
                                            height: 40, 
                                            bgcolor: 'grey.100',
                                            border: '1px solid #eee',
                                            cursor: 'pointer'
                                          }}
                                          onClick={(e) => handleMenuImageButtonClick(e, menu.id)}
                                        >
                                          <ImageIcon fontSize="small" sx={{ color: 'grey.500' }} />
                                        </Avatar>
                                      </Tooltip>
                                    )}
                                  </Box>
                                </Box>
                                <Box sx={{ display: 'flex', gap: isMobile ? 0.5 : 1, flexGrow: isMobile ? 1 : 0, justifyContent: isMobile ? 'flex-end' : 'flex-start' }}>
                                  {/* 💻 MASAÜSTÜ: Tam butonlar */}
                                  {!isMobile && (
                                    <>
                                      <Button
                                        variant="outlined"
                                        startIcon={<ImageIcon />}
                                        onClick={(e) => handleMenuImageButtonClick(e, menu.id)}
                                        size="small"
                                        color="secondary"
                                      >
                                        {menu.imageUrl ? t('qrMenuSettingsModal.menus.buttons.updateImage') : t('qrMenuSettingsModal.menus.buttons.addImage')}
                                      </Button>
                                      {menu.imageUrl && (
                                        <Button
                                          variant="outlined"
                                          startIcon={<DeleteIcon />}
                                          onClick={(e) => handleDeleteMenuImage(e, menu.id)}
                                          size="small"
                                          color="error"
                                        >
                                          {t('qrMenuSettingsModal.menus.buttons.deleteImage')}
                                        </Button>
                                      )}
                                      <Button
                                        variant="outlined"
                                        startIcon={<AddIcon />}
                                        onClick={() => handleOpenMenuProductSelectionModal(menu.id)}
                                        size="small"
                                      >
                                        {t('qrMenuSettingsModal.menus.buttons.addProductToMenu')}
                                      </Button>
                                    </>
                                  )}
                                  
                                  {/* 📱 MOBİL: Sadece ikon butonlar */}
                                  {isMobile && (
                                    <>
                                      <Tooltip title={menu.imageUrl ? t('qrMenuSettingsModal.menus.buttons.updateImage') : t('qrMenuSettingsModal.menus.buttons.addImage')}>
                                        <IconButton
                                          size="small"
                                          color="secondary"
                                          onClick={(e) => handleMenuImageButtonClick(e, menu.id)}
                                          sx={{ border: '1px solid', borderColor: 'secondary.main' }}
                                        >
                                          <ImageIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                      {menu.imageUrl && (
                                        <Tooltip title={t('qrMenuSettingsModal.menus.buttons.deleteImage')}>
                                          <IconButton
                                            size="small"
                                            color="error"
                                            onClick={(e) => handleDeleteMenuImage(e, menu.id)}
                                            sx={{ border: '1px solid', borderColor: 'error.main' }}
                                          >
                                            <DeleteIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                      <Tooltip title={t('qrMenuSettingsModal.menus.buttons.addProduct')}>
                                        <IconButton
                                          size="small"
                                          color="primary"
                                          onClick={() => handleOpenMenuProductSelectionModal(menu.id)}
                                          sx={{ border: '1px solid', borderColor: 'primary.main' }}
                                        >
                                          <AddIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </>
                                  )}
                                </Box>
                              </Box>
                              
                              {menuProductIds.length === 0 ? (
                                <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                                  {t('qrMenuSettingsModal.menus.menuEmptyMessage')}
                                </Typography>
                              ) : (
                                <>
                                  {/* 💻 MASAÜSTÜ: Tablo Görünümü */}
                                  {!isMobile && (
                                    <TableContainer component={Paper} variant="outlined" sx={{ mt: 1, height: '250px', overflowY: 'auto' }}>
                                      <Table size="small" stickyHeader aria-label={t('qrMenuSettingsModal.menus.productsTableLabel', { name: menu.name })}>
                                        <TableHead>
                                          <TableRow sx={{ '& th': { fontWeight: 'bold' } }}>
                                            <TableCell>{isTicketMode ? t('qrMenuSettingsModal.menus.headers.ticketType') : t('qrMenuSettingsModal.menus.headers.productName')}</TableCell>
                                            <TableCell>{t('qrMenuSettingsModal.menus.headers.description')}</TableCell>
                                            <TableCell align="right">{t('qrMenuSettingsModal.menus.headers.price')}</TableCell>
                                            <TableCell align="center">{t('qrMenuSettingsModal.menus.headers.actions')}</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {paginatedMenuProducts.map((product) => (
                                            <TableRow key={product.id}>
                                              <TableCell component="th" scope="row" sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Box sx={{ display: 'inline-flex', marginRight: 1, alignItems: 'center' }}>
                                                  {product.imageUrl ? (
                                                    <Tooltip title={t('qrMenuSettingsModal.menus.tooltips.viewProductImage')}>
                                                      <Avatar 
                                                        src={product.imageUrl}
                                                        alt={product.name}
                                                        variant="rounded"
                                                        sx={{ 
                                                          width: 32, 
                                                          height: 32,
                                                          border: '1px solid #eee',
                                                          objectFit: 'contain'
                                                        }}
                                                        imgProps={{
                                                          onError: (e) => {
                                                            console.error("Ürün resmi yüklenemedi:", product.id, product.imageUrl);
                                                            const target = e.target as HTMLImageElement;
                                                            target.src = '';
                                                            target.onerror = null;
                                                          }
                                                        }}
                                                      />
                                                    </Tooltip>
                                                  ) : (
                                                    <Tooltip title={t('qrMenuSettingsModal.menus.tooltips.uploadProductImage')}>
                                                      <Avatar 
                                                        variant="rounded"
                                                        sx={{ 
                                                          width: 32, 
                                                          height: 32, 
                                                          bgcolor: 'grey.100',
                                                          border: '1px solid #eee'
                                                        }}
                                                        onClick={(e) => handleImageButtonClick(e, product.id)}
                                                      >
                                                        <ImageIcon fontSize="small" sx={{ color: 'grey.500' }} />
                                                      </Avatar>
                                                    </Tooltip>
                                                  )}
                                                </Box>
                                                {product.name}
                                              </TableCell>
                                              <TableCell>
                                                {product.description ? (
                                                  <Typography variant="body2" sx={{ 
                                                    maxWidth: '200px',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                  }}>
                                                    {product.description}
                                                  </Typography>
                                                ) : (
                                                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                    Açıklama yok
                                                  </Typography>
                                                )}
                                              </TableCell>
                                              <TableCell align="right">
                                                {getDisplayPrice(product).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
                                              </TableCell>
                                              <TableCell align="center">
                                                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', alignItems: 'center' }}>
                                                  <Tooltip title={t('qrMenuSettingsModal.menus.tooltips.moveUp')}>
                                                    <span>
                                                      <IconButton
                                                        size="small"
                                                        onClick={() => handleMoveProduct(menu.id, product.id, 'up')}
                                                        disabled={menuProductItems.findIndex(p => p.id === product.id) === 0}
                                                      >
                                                        <ArrowUpwardIcon fontSize="small" />
                                                      </IconButton>
                                                    </span>
                                                  </Tooltip>
                                                  <Tooltip title={t('qrMenuSettingsModal.menus.tooltips.moveDown')}>
                                                    <span>
                                                      <IconButton
                                                        size="small"
                                                        onClick={() => handleMoveProduct(menu.id, product.id, 'down')}
                                                        disabled={menuProductItems.findIndex(p => p.id === product.id) === menuProductItems.length - 1}
                                                      >
                                                        <ArrowDownwardIcon fontSize="small" />
                                                      </IconButton>
                                                    </span>
                                                  </Tooltip>

                                                  {!isTicketMode && (
                                                    <Tooltip title={`Yan ürünler (${(relatedProductsByProductId[product.id] || []).length})`}>
                                                      <IconButton
                                                        size="small"
                                                        color={(relatedProductsByProductId[product.id] || []).length > 0 ? 'success' : 'default'}
                                                        onClick={() => handleOpenRelatedProductSelectionModal(product.id)}
                                                      >
                                                        <LinkIcon fontSize="small" />
                                                      </IconButton>
                                                    </Tooltip>
                                                  )}

                                                  <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                                                  
                                                  <Tooltip title={product.imageUrl ? t('qrMenuSettingsModal.menus.tooltips.updateImage') : t('qrMenuSettingsModal.menus.tooltips.addProductImage')}>
                                                    <IconButton
                                                      size="small"
                                                      color="primary"
                                                      onClick={(e) => handleImageButtonClick(e, product.id)}
                                                      disabled={imageUploading}
                                                    >
                                                      {currentProductId === product.id && imageUploading ? (
                                                        <CircularProgress size={16} />
                                                      ) : (
                                                        <ImageIcon fontSize="small" />
                                                      )}
                                                    </IconButton>
                                                  </Tooltip>
                                                  <Tooltip title={t('qrMenuSettingsModal.menus.tooltips.removeFromMenu')}>
                                                    <IconButton
                                                      size="small"
                                                      color="error"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleOpenDeleteMenuProductConfirm(menu.id, product.id);
                                                      }}
                                                    >
                                                      <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                  </Tooltip>
                                                </Box>
                                              </TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </TableContainer>
                                  )}
                                  
                                  {/* 📱 MOBİL: Kart Görünümü */}
                                  {isMobile && (
                                    <Box sx={{ mt: 1 }}>
                                      {paginatedMenuProducts.map((product) => (
                                        <Paper 
                                          key={product.id} 
                                          variant="outlined" 
                                          sx={{ 
                                            p: 1.5, 
                                            mb: 1, 
                                            display: 'flex', 
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 1
                                          }}
                                        >
                                          {/* Sol: Resim + Bilgiler */}
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                                            {/* Ürün Resmi */}
                                            {product.imageUrl ? (
                                              <Avatar 
                                                src={product.imageUrl}
                                                alt={product.name}
                                                variant="rounded"
                                                sx={{ width: 40, height: 40, border: '1px solid #eee', flexShrink: 0 }}
                                              />
                                            ) : (
                                              <Avatar 
                                                variant="rounded"
                                                sx={{ width: 40, height: 40, bgcolor: 'grey.100', border: '1px solid #eee', flexShrink: 0 }}
                                              >
                                                <ImageIcon fontSize="small" sx={{ color: 'grey.400' }} />
                                              </Avatar>
                                            )}
                                            
                                            {/* Ürün Bilgileri */}
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                              <Typography variant="body2" fontWeight="bold" noWrap>
                                                {product.name}
                                              </Typography>
                                              <Typography variant="caption" color="primary" fontWeight="bold">
                                                {getDisplayPrice(product).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
                                              </Typography>
                                            </Box>
                                          </Box>
                                          
                                          {/* Sağ: Sadece Sil Butonu */}
                                          <IconButton
                                            size="small"
                                            color="error"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleOpenDeleteMenuProductConfirm(menu.id, product.id);
                                            }}
                                            sx={{ flexShrink: 0 }}
                                          >
                                            <DeleteIcon fontSize="small" />
                                          </IconButton>
                                        </Paper>
                                      ))}
                                    </Box>
                                  )}
                                  
                                  {/* Menü ürünleri için sayfalama - Her iki görünümde de aynı */}
                                  {menuProductItems.length > menuPaginationData.rowsPerPage && (
                                    <TablePagination
                                      component="div"
                                      count={menuProductItems.length}
                                      page={menuPaginationData.page}
                                      onPageChange={(event, newPage) => handleMenuPageChange(menu.id, newPage)}
                                      rowsPerPage={menuPaginationData.rowsPerPage}
                                      onRowsPerPageChange={(event) => handleMenuRowsPerPageChange(menu.id, parseInt(event.target.value, 10))}
                                      rowsPerPageOptions={[5, 10, 25]}
                                      labelRowsPerPage={isMobile ? t('qrMenuSettingsModal.menus.pagination.page') : t('qrMenuSettingsModal.menus.pagination.rowsPerPage')}
                                      labelDisplayedRows={({ from, to, count }) => isMobile ? `${from}-${to}/${count}` : t('qrMenuSettingsModal.menus.pagination.displayedRows', { from, to, count })}
                                      sx={{ borderTop: '1px solid rgba(224, 224, 224, 1)' }}
                                    />
                                  )}
                                </>
                              )}
                            </Box>
                          )}
                        </Box>
                      );
                    })
                  ) : null}
                  
                  <Divider sx={{ my: 3, display: menus.length > 0 ? 'block' : 'none' }} />
                  
                  {/* Ana Ürün Listesi */}
                  <Box sx={{ mt: menus.length > 0 ? 3 : 0 }}>
                    {menus.length > 0 && (
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                        {t('qrMenuSettingsModal.menus.generalProductList')}
                      </Typography>
                    )}
                    
                    {selectedProducts.length === 0 ? (
                      <Typography variant="body2" color="textSecondary" sx={{mt: 2}}>
                        {t('qrMenuSettingsModal.menus.emptyMessage')}
                      </Typography>
                    ) : (
                      <>
                        <TableContainer component={Paper} variant="outlined" sx={{ mt: 1, height: '250px', overflowY: 'auto' }}>
                          <Table size="small" stickyHeader aria-label={t('qrMenuSettingsModal.menus.qrProductsTableLabel')}>
                            <TableHead>
                              <TableRow sx={{ '& th': { fontWeight: 'bold' } }}>
                                <TableCell>{isTicketMode ? t('qrMenuSettingsModal.menus.headers.ticketType') : t('qrMenuSettingsModal.menus.headers.productName')}</TableCell>
                                <TableCell>{t('qrMenuSettingsModal.menus.headers.description')}</TableCell>
                                <TableCell align="right">{t('qrMenuSettingsModal.menus.headers.price')}</TableCell>
                                <TableCell align="center">{t('qrMenuSettingsModal.menus.headers.actions')}</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {paginatedProducts.map((product) => (
                                <TableRow key={product.id}>
                                  <TableCell component="th" scope="row" sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Box sx={{ display: 'inline-flex', marginRight: 1, alignItems: 'center' }}>
                                      {product.imageUrl ? (
                                        <Tooltip title={t('qrMenuSettingsModal.menus.tooltips.viewProductImage')}>
                                          <Avatar 
                                            src={product.imageUrl}
                                            alt={product.name}
                                            variant="rounded"
                                            sx={{ 
                                              width: 32, 
                                              height: 32,
                                              border: '1px solid #eee',
                                              objectFit: 'contain'
                                            }}
                                            imgProps={{
                                              onError: (e) => {
                                                console.error("Ürün resmi yüklenemedi:", product.id, product.imageUrl);
                                                const target = e.target as HTMLImageElement;
                                                target.src = ''; // Hatalı resim yerine boş göster
                                                target.onerror = null; // Sonsuz döngüyü önlemek için
                                              }
                                            }}
                                          />
                                        </Tooltip>
                                      ) : (
                                        <Tooltip title={t('qrMenuSettingsModal.menus.tooltips.uploadProductImage')}>
                                          <Avatar 
                                            variant="rounded"
                                            sx={{ 
                                              width: 32, 
                                              height: 32, 
                                              bgcolor: 'grey.100',
                                              border: '1px solid #eee'
                                            }}
                                            onClick={(e) => handleImageButtonClick(e, product.id)}
                                          >
                                            <ImageIcon fontSize="small" sx={{ color: 'grey.500' }} />
                                          </Avatar>
                                        </Tooltip>
                                      )}
                                    </Box>
                                    {product.name}
                                  </TableCell>
                                  <TableCell>
                                    {product.description ? (
                                      <Typography variant="body2" sx={{ 
                                        maxWidth: '200px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                      }}>
                                        {product.description}
                                      </Typography>
                                    ) : (
                                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                        {t('qrMenuSettingsModal.menus.noDescription')}
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell align="right">
                                    {getDisplayPrice(product).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
                                  </TableCell>
                                  <TableCell align="center">
                                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                      {!isTicketMode && (
                                        <Tooltip title={`Yan ürünler (${(relatedProductsByProductId[product.id] || []).length})`}>
                                          <IconButton
                                            size="small"
                                            color={(relatedProductsByProductId[product.id] || []).length > 0 ? 'success' : 'default'}
                                            onClick={() => handleOpenRelatedProductSelectionModal(product.id)}
                                          >
                                            <LinkIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                      <Tooltip title={product.imageUrl ? t('qrMenuSettingsModal.menus.tooltips.updateImage') : t('qrMenuSettingsModal.menus.tooltips.addProductImage')}>
                                        <IconButton
                                          size="small"
                                          color="primary"
                                          onClick={(e) => handleImageButtonClick(e, product.id)}
                                          disabled={imageUploading}
                                        >
                                          {currentProductId === product.id && imageUploading ? (
                                            <CircularProgress size={16} />
                                          ) : (
                                            <ImageIcon fontSize="small" />
                                          )}
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title={t('qrMenuSettingsModal.menus.tooltips.removeFromList')}>
                                        <IconButton
                                          size="small"
                                          color="error"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleOpenDeleteProductConfirm(product.id);
                                          }}
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        <TablePagination
                          component="div"
                          count={selectedProductItems.length}
                          page={page}
                          onPageChange={handleChangePage}
                          rowsPerPage={rowsPerPage}
                          onRowsPerPageChange={handleChangeRowsPerPage}
                          rowsPerPageOptions={[5, 10, 25, 50]}
                          labelRowsPerPage={t('qrMenuSettingsModal.menus.pagination.rowsPerPage')}
                          labelDisplayedRows={({ from, to, count }) => t('qrMenuSettingsModal.menus.pagination.displayedRows', { from, to, count })}
                          sx={{ mt: 0, borderTop: '1px solid rgba(224, 224, 224, 1)' }}
                        />
                      </>
                    )}
                  </Box>
                </Paper>
              )}
              {!isKitchenMode && activeTab === 1 && (
                <Paper elevation={0} sx={{p: 2, minHeight: '500px'}}>
                  <Typography variant="h6">{t('qrMenuSettingsModal.templatesTab.title')}</Typography>
                  <Typography variant="body2" color="textSecondary" sx={{mt: 2, mb: 3}}>
                    {t('qrMenuSettingsModal.templatesTab.description', {
                      mode: isTicketMode
                        ? t('qrMenuSettingsModal.templatesTab.mode.onlineTicket')
                        : t('qrMenuSettingsModal.templatesTab.mode.qrMenu')
                    })}
                  </Typography>
                  
                  {/* Şablonlar İçeriği */}
                  {isTicketMode ? (
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={4}>
                        <Card
                          variant="outlined"
                          sx={{
                            cursor: 'pointer',
                            position: 'relative',
                            transition: 'all 0.2s ease',
                            ...((!settings?.appearance?.template || settings?.appearance?.template === 'ticket-basic') ? {
                              border: '2px solid',
                              borderColor: 'primary.main',
                              boxShadow: 2
                            } : {}),
                            '&:hover': { boxShadow: 3 }
                          }}
                          onClick={() => handleTemplateSelect('ticket-basic')}
                        >
                          {(!settings?.appearance?.template || settings?.appearance?.template === 'ticket-basic') && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 10,
                                right: 10,
                                bgcolor: 'primary.main',
                                borderRadius: '50%',
                                width: 24,
                                height: 24,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                zIndex: 2
                              }}
                            >
                              <CheckIcon fontSize="small" />
                            </Box>
                          )}
                          <CardContent>
                            <Typography variant="subtitle1" component="div" gutterBottom>
                              {t('qrMenuSettingsModal.templatesTab.ticketStandard.title')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {t('qrMenuSettingsModal.templatesTab.ticketStandard.description')}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  ) : (
                  <Grid container spacing={2}>
                    {/* Standart Şablon */}
                    <Grid item xs={6} sm={4} md={3} sx={{ display: 'flex' }}>
                      <Card 
                        variant="outlined" 
                        sx={{ 
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          cursor: 'pointer',
                          position: 'relative',
                          transition: 'all 0.2s ease',
                          ...((!settings?.appearance?.template || settings?.appearance?.template === 'standard') ? {
                            border: '2px solid',
                            borderColor: 'primary.main',
                            boxShadow: 2
                          } : {}),
                          '&:hover': {
                            boxShadow: 3
                          }
                        }}
                        onClick={() => handleTemplateSelect('standard')}
                      >
                        {(!settings?.appearance?.template || settings?.appearance?.template === 'standard') && (
                          <Box 
                            sx={{ 
                              position: 'absolute', 
                              top: 10, 
                              right: 10, 
                              bgcolor: 'primary.main',
                              borderRadius: '50%',
                              width: 24,
                              height: 24,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              zIndex: 2
                            }}
                          >
                            <CheckIcon fontSize="small" />
                          </Box>
                        )}
                        
                        <CardContent>
                          <Typography variant="subtitle1" component="div" gutterBottom>
                            {t('qrMenuSettingsModal.templatesTab.standard.title')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {t('qrMenuSettingsModal.templatesTab.standard.description')}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    {/* Modern Şablon */}
                    <Grid item xs={6} sm={4} md={3}>
                      <Card 
                        variant="outlined" 
                        sx={{ 
                          cursor: 'pointer',
                          position: 'relative',
                          transition: 'all 0.2s ease',
                          ...(settings?.appearance?.template === 'modern' ? {
                            border: '2px solid',
                            borderColor: 'primary.main',
                            boxShadow: 2
                          } : {}),
                          '&:hover': {
                            boxShadow: 3
                          },
                          background: 'linear-gradient(to right bottom, #fafafa, #f5f5f5)'
                        }}
                        onClick={() => handleTemplateSelect('modern')}
                      >
                        {settings?.appearance?.template === 'modern' && (
                          <Box 
                            sx={{ 
                              position: 'absolute', 
                              top: 10, 
                              right: 10, 
                              bgcolor: 'primary.main',
                              borderRadius: '50%',
                              width: 24,
                              height: 24,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              zIndex: 2
                            }}
                          >
                            <CheckIcon fontSize="small" />
                          </Box>
                        )}
                        
                        <CardContent>
                          <Typography variant="subtitle1" component="div" gutterBottom>
                            {t('qrMenuSettingsModal.templatesTab.modern.title')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {t('qrMenuSettingsModal.templatesTab.modern.description')}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    {/* Liste Şablonu */}
                    <Grid item xs={6} sm={4} md={3}>
                      <Card 
                        variant="outlined" 
                        sx={{ 
                          cursor: 'pointer',
                          position: 'relative',
                          transition: 'all 0.2s ease',
                          ...(settings?.appearance?.template === 'list' ? {
                            border: '2px solid',
                            borderColor: 'primary.main',
                            boxShadow: 2
                          } : {}),
                          '&:hover': {
                            boxShadow: 3
                          },
                          background: 'linear-gradient(to right, #f8f9fa, #ffffff)'
                        }}
                        onClick={() => handleTemplateSelect('list')}
                      >
                        {settings?.appearance?.template === 'list' && (
                          <Box 
                            sx={{ 
                              position: 'absolute', 
                              top: 10, 
                              right: 10, 
                              bgcolor: 'primary.main',
                              borderRadius: '50%',
                              width: 24,
                              height: 24,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              zIndex: 2
                            }}
                          >
                            <CheckIcon fontSize="small" />
                          </Box>
                        )}
                        
                        <CardContent>
                          <Typography variant="subtitle1" component="div" gutterBottom>
                            {t('qrMenuSettingsModal.templatesTab.list.title')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {t('qrMenuSettingsModal.templatesTab.list.description')}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    {/* Karma Şablon */}
                    <Grid item xs={6} sm={4} md={3}>
                      <Card 
                        variant="outlined" 
                        sx={{ 
                          cursor: 'pointer',
                          position: 'relative',
                          transition: 'all 0.2s ease',
                          ...(settings?.appearance?.template === 'mixed' ? {
                            border: '2px solid',
                            borderColor: 'primary.main',
                            boxShadow: 2
                          } : {}),
                          '&:hover': {
                            boxShadow: 3
                          },
                          background: 'linear-gradient(135deg, #f5f7fa, #ffffff)'
                        }}
                        onClick={() => handleTemplateSelect('mixed')}
                      >
                        {settings?.appearance?.template === 'mixed' && (
                          <Box 
                            sx={{ 
                              position: 'absolute', 
                              top: 10, 
                              right: 10, 
                              bgcolor: 'primary.main',
                              borderRadius: '50%',
                              width: 24,
                              height: 24,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              zIndex: 2
                            }}
                          >
                            <CheckIcon fontSize="small" />
                          </Box>
                        )}
                        
                        <CardContent>
                          <Typography variant="subtitle1" component="div" gutterBottom>
                            {t('qrMenuSettingsModal.templatesTab.mixed.title')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {t('qrMenuSettingsModal.templatesTab.mixed.description')}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Carousel Şablonu */}
                    <Grid item xs={6} sm={4} md={3}>
                      <Card 
                        variant="outlined" 
                        sx={{ 
                          cursor: 'pointer',
                          position: 'relative',
                          transition: 'all 0.2s ease',
                          ...(settings?.appearance?.template === 'carousel' ? {
                            border: '2px solid',
                            borderColor: 'primary.main',
                            boxShadow: 2
                          } : {}),
                          '&:hover': {
                            boxShadow: 3
                          },
                          background: 'linear-gradient(135deg, #e3f2fd, #ffffff)'
                        }}
                        onClick={() => handleTemplateSelect('carousel')}
                      >
                        {settings?.appearance?.template === 'carousel' && (
                          <Box 
                            sx={{ 
                              position: 'absolute', 
                              top: 10, 
                              right: 10, 
                              bgcolor: 'primary.main',
                              borderRadius: '50%',
                              width: 24,
                              height: 24,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              zIndex: 2
                            }}
                          >
                            <CheckIcon fontSize="small" />
                          </Box>
                        )}
                        
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Typography variant="subtitle1" component="div" gutterBottom>
                            {t('qrMenuSettingsModal.templatesTab.carousel.title')}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}
                          >
                            {t('qrMenuSettingsModal.templatesTab.carousel.description')}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* QrTık Şablonu */}
                    <Grid item xs={6} sm={4} md={3} sx={{ display: 'flex' }}>
                      <Card 
                        variant="outlined" 
                        sx={{ 
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          cursor: 'pointer',
                          position: 'relative',
                          transition: 'all 0.2s ease',
                          ...(settings?.appearance?.template === 'qrtik' ? {
                            border: '2px solid',
                            borderColor: 'primary.main',
                            boxShadow: 2
                          } : {}),
                          '&:hover': {
                            boxShadow: 3
                          },
                          background: 'linear-gradient(135deg, #fff8e1, #ffffff)'
                        }}
                        onClick={() => handleTemplateSelect('qrtik')}
                      >
                        {settings?.appearance?.template === 'qrtik' && (
                          <Box 
                            sx={{ 
                              position: 'absolute', 
                              top: 10, 
                              right: 10, 
                              bgcolor: 'primary.main',
                              borderRadius: '50%',
                              width: 24,
                              height: 24,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              zIndex: 2
                            }}
                          >
                            <CheckIcon fontSize="small" />
                          </Box>
                        )}

                        <CardContent sx={{ flexGrow: 1 }}>
                          <Typography variant="subtitle1" component="div" gutterBottom>
                            {t('qrMenuSettingsModal.templatesTab.qrtik.title')}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}
                          >
                            {t('qrMenuSettingsModal.templatesTab.qrtik.description')}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                  )}
                </Paper>
              )}
              {!isKitchenMode && activeTab === 2 && (
                <Paper elevation={0} sx={{p: 2, minHeight: '500px'}}>
                  <Typography variant="h6">{t('qrMenuSettingsModal.extraSettingsTab.title')}</Typography>
                  <Typography variant="body2" color="textSecondary" sx={{mt: 2, mb: 3}}>
                    {t('qrMenuSettingsModal.extraSettingsTab.description')}
                  </Typography>
                  
                  {/* Menü Adı Değiştirme Bölümü */}
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 1, mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      {t('qrMenuSettingsModal.extraSettingsTab.menuInfo.title')}
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <TextField
                        fullWidth
                        label={t('qrMenuSettingsModal.extraSettingsTab.menuInfo.menuNameLabel')}
                        value={currentMenuName}
                        onChange={(e) => setCurrentMenuName(e.target.value)}
                        variant="outlined"
                        size="small"
                        helperText={t('qrMenuSettingsModal.extraSettingsTab.menuInfo.menuNameHelper')}
                        InputProps={{
                          startAdornment: <RestaurantMenuIcon color="action" sx={{ mr: 1 }} />
                        }}
                      />
                    </Box>
                  </Paper>
                  
                  <Grid container spacing={3}>
                    {/* Görsel Ayarlar */}
                    <Grid item xs={12} md={6}>
                      <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                          {t('qrMenuSettingsModal.extraSettingsTab.visualSettingsTitle')}
                        </Typography>
                        
                        <Box sx={{ my: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <ColorLensIcon sx={{ fontSize: '1.2rem', mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body2" fontWeight="medium">
                              {isTicketMode ? 'Mavi Alan Rengi' : 'Tema Rengi'}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ mt: 1 }}>
                            {/* Ana renk seçici kutusu */}
                            <Box sx={{ 
                              display: 'flex', 
                              flexDirection: 'column', 
                              alignItems: 'center', 
                              width: '100%',
                              p: 2,
                              border: '1px solid #e0e0e0',
                              borderRadius: 1,
                              bgcolor: 'background.paper'
                            }}>
                              <HexColorPicker 
                                color={settings?.appearance?.primaryColor || '#1976d2'} 
                                onChange={(color) => setSettings(prev => ({
                                  ...(prev || {}),
                                  appearance: {
                                    ...(prev?.appearance || {}),
                                    primaryColor: color
                                  }
                                }))}
                                style={{ width: '100%', height: 200 }}
                              />
                              
                              <Box sx={{ 
                                mt: 2, 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'space-between',
                                width: '100%'
                              }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Box 
                                    sx={{ 
                                      width: 36, 
                                      height: 36, 
                                      bgcolor: settings?.appearance?.primaryColor || '#1976d2', 
                                      borderRadius: 1, 
                                      border: '2px solid #ddd',
                                      mr: 1
                                    }} 
                                  />
                                  <TextField 
                                    value={settings?.appearance?.primaryColor || '#1976d2'} 
                                    size="small"
                                    variant="outlined"
                                    onChange={(e) => setSettings(prev => ({
                                      ...(prev || {}),
                                      appearance: {
                                        ...(prev?.appearance || {}),
                                        primaryColor: e.target.value
                                      }
                                    }))}
                                    sx={{ width: 120 }}
                                  />
                                </Box>
                                
                                <Button 
                                  size="small" 
                                  variant="outlined" 
                                  onClick={() => setSettings(prev => ({
                                    ...(prev || {}),
                                    appearance: {
                                      ...(prev?.appearance || {}),
                                      primaryColor: '#1976d2'
                                    }
                                  }))}
                                >
                                  Varsayılan
                                </Button>
                              </Box>
                            </Box>
                            
                            {/* Örnek renk gösterimi */}
                            <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 1 }}>
                              <Typography variant="body2" color="text.secondary" gutterBottom>
                                Önizleme:
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Box sx={{ 
                                  width: 120, 
                                  height: 40, 
                                  bgcolor: settings?.appearance?.primaryColor || '#1976d2', 
                                  borderRadius: 1, 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  justifyContent: 'center' 
                                }}>
                                  <Typography variant="button" sx={{ color: '#fff' }}>
                                    Düğme
                                  </Typography>
                                </Box>
                                <Typography variant="body2" sx={{ color: settings?.appearance?.primaryColor || '#1976d2', fontWeight: 'bold' }}>
                                  Metin Rengi
                                </Typography>
                              </Box>
                              <Box sx={{ width: '100%', height: 12, bgcolor: settings?.appearance?.primaryColor || '#1976d2', borderRadius: 0.5 }} />
                            </Box>
                          </Box>
                          
                          {/* Hızlı renk seçimi */}
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
                            Hızlı Seçim:
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                            {['#1976d2', '#2e7d32', '#d32f2f', '#ed6c02', '#9c27b0', '#000000', '#ff9800', '#3f51b5', '#607d8b', '#8bc34a'].map(color => (
                              <Box 
                                key={color}
                                onClick={() => setSettings(prev => ({
                                  ...(prev || {}),
                                  appearance: {
                                    ...(prev?.appearance || {}),
                                    primaryColor: color
                                  }
                                }))}
                                sx={{ 
                                  width: 28, 
                                  height: 28, 
                                  bgcolor: color, 
                                  borderRadius: 1, 
                                  cursor: 'pointer',
                                  border: settings?.appearance?.primaryColor === color ? '2px solid #666' : '1px solid #ddd',
                                  transition: 'transform 0.2s',
                                  '&:hover': {
                                    transform: 'scale(1.1)'
                                  }
                                }} 
                              />
                            ))}
                          </Box>
                        </Box>
                        
                        <Divider sx={{ my: 2 }} />
                        
                        <Box sx={{ my: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                            <TextFieldsIcon sx={{ fontSize: '1.2rem', mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body2" fontWeight="medium">
                              Yazı Tipi
                            </Typography>
                          </Box>
                          
                          <FormControl fullWidth variant="outlined" size="small">
                            <InputLabel id="font-family-select-label">{t('qrMenuSettingsModal.appearance.fontFamily')}</InputLabel>
                            <Select
                              labelId="font-family-select-label"
                              id="font-family-select"
                              value={settings?.appearance?.fontFamily || 'Roboto'}
                              onChange={(e) => setSettings(prev => ({
                                ...(prev || {}),
                                appearance: {
                                  ...(prev?.appearance || {}),
                                  fontFamily: e.target.value as string
                                }
                              }))}
                              label="Yazı Tipi"
                            >
                              {fontFamilies.map(font => (
                                <MenuItem 
                                  key={font} 
                                  value={font} 
                                  sx={{ 
                                    fontFamily: font,
                                    fontSize: '15px'
                                  }}
                                >
                                  {font}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          
                          <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ fontFamily: settings?.appearance?.fontFamily || 'Roboto', mb: 1 }}>
                              Örnek yazı: Türkçe karakterler (ğüşıöç)
                            </Typography>
                            <Typography variant="h6" sx={{ fontFamily: settings?.appearance?.fontFamily || 'Roboto' }}>
                              {moduleTitle} Başlık Örneği
                            </Typography>
                          </Box>
                        </Box>

                        {!isTicketMode && (
                          <>
                            <Divider sx={{ my: 2 }} />
                            <Box sx={{ my: 3 }}>
                              <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
                                Sosyal Medya Bağlantıları
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                                Platformu menüden seçip ekleyin, ardından bağlantıyı yazın.
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                                <FormControl size="small" sx={{ minWidth: 220, flex: 1 }}>
                                  <InputLabel>Platform</InputLabel>
                                  <Select
                                    value={socialPlatformToAdd}
                                    label="Platform"
                                    onChange={(e) => setSocialPlatformToAdd(e.target.value as SocialPlatformKey)}
                                  >
                                    {(availableSocialPlatforms.length > 0 ? availableSocialPlatforms : SOCIAL_PLATFORM_OPTIONS).map((option) => (
                                      <MenuItem key={option.key} value={option.key}>
                                        {option.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                <Button
                                  variant="outlined"
                                  onClick={handleAddSocialPlatform}
                                  startIcon={<AddIcon />}
                                >
                                  Ekle
                                </Button>
                              </Box>
                              {selectedSocialPlatforms.length > 0 ? (
                                <Grid container spacing={1.5}>
                                  {selectedSocialPlatforms.map((option) => (
                                    <Grid item xs={12} key={option.key}>
                                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                        <TextField
                                          fullWidth
                                          size="small"
                                          label={option.label}
                                          placeholder={option.placeholder}
                                          value={settings?.appearance?.socialLinks?.[option.key] || ''}
                                          onChange={(e) => setSettings(prev => ({
                                            ...(prev || {}),
                                            appearance: {
                                              ...(prev?.appearance || {}),
                                              socialLinks: {
                                                ...(prev?.appearance?.socialLinks || {}),
                                                [option.key]: e.target.value
                                              }
                                            }
                                          }))}
                                        />
                                        <IconButton
                                          color="error"
                                          size="small"
                                          onClick={() => handleRemoveSocialPlatform(option.key)}
                                          aria-label={`${option.label} kaldır`}
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </Box>
                                    </Grid>
                                  ))}
                                </Grid>
                              ) : (
                                <Typography variant="caption" color="text.secondary">
                                  Henüz platform eklenmedi.
                                </Typography>
                              )}
                            </Box>
                            <Divider sx={{ my: 2 }} />
                            <Box sx={{ my: 2 }}>
                              <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
                                Konum Linki
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                                Tüm şablonlarda konum butonu için kullanılır.
                              </Typography>
                              <TextField
                                fullWidth
                                size="small"
                                label="Konum Linki (Google Maps)"
                                placeholder="https://www.google.com/maps/..."
                                value={settings?.appearance?.businessLocationUrl || ''}
                                onChange={(e) => setSettings(prev => ({
                                  ...(prev || {}),
                                  appearance: {
                                    ...(prev?.appearance || {}),
                                    businessLocationUrl: e.target.value
                                  }
                                }))}
                              />
                            </Box>
                            <Divider sx={{ my: 2 }} />
                            <Box sx={{ my: 2 }}>
                              <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
                                WiFi Şifresi
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                                Tüm şablonlarda WiFi butonu için kullanılır.
                              </Typography>
                              <TextField
                                fullWidth
                                size="small"
                                label="WiFi Şifresi"
                                placeholder="Örn: 12345678"
                                value={settings?.appearance?.wifiPassword || ''}
                                onChange={(e) => setSettings(prev => ({
                                  ...(prev || {}),
                                  appearance: {
                                    ...(prev?.appearance || {}),
                                    wifiPassword: e.target.value
                                  }
                                }))}
                              />
                            </Box>
                          </>
                        )}
                      </Paper>
                    </Grid>
                    
                    {/* Görünüm Ayarları ve Menü Logosu */}
                    <Grid item xs={12} md={6}>
                      <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                          {t('qrMenuSettingsModal.extraSettingsTab.appearanceSettingsTitle')}
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        {!isTicketMode && (
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
                            Görüntülenecek Öğeler
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 2 }}>
                            <FormControlLabel 
                              control={
                                <Checkbox 
                                  checked={settings?.appearance?.showPrices !== false}
                                  onChange={(e) => setSettings(prev => ({
                                    ...(prev || {}),
                                    appearance: {
                                      ...(prev?.appearance || {}),
                                      showPrices: e.target.checked
                                    }
                                  }))}
                                  size="small"
                                />
                              } 
                              label={<Typography variant="body2">{t('qrMenuSettingsModal.appearance.showProductPrices')}</Typography>}
                            />
                            
                            <FormControlLabel 
                              control={
                                <Checkbox 
                                  checked={settings?.appearance?.showImages !== false}
                                  onChange={(e) => setSettings(prev => ({
                                    ...(prev || {}),
                                    appearance: {
                                      ...(prev?.appearance || {}),
                                      showImages: e.target.checked
                                    }
                                  }))}
                                  size="small"
                                />
                              } 
                              label={<Typography variant="body2">{t('qrMenuSettingsModal.appearance.showProductImages')}</Typography>}
                            />
                            
                            <FormControlLabel 
                              control={
                                <Checkbox 
                                  checked={settings?.appearance?.showDescriptions !== false}
                                  onChange={(e) => setSettings(prev => ({
                                    ...(prev || {}),
                                    appearance: {
                                      ...(prev?.appearance || {}),
                                      showDescriptions: e.target.checked
                                    }
                                  }))}
                                  size="small"
                                />
                              } 
                              label={<Typography variant="body2">{t('qrMenuSettingsModal.appearance.showProductDescriptions')}</Typography>}
                            />
                            
                            <FormControlLabel 
                              control={
                                <Checkbox 
                                  checked={settings?.appearance?.showLogo !== false}
                                  onChange={(e) => setSettings(prev => ({
                                    ...(prev || {}),
                                    appearance: {
                                      ...(prev?.appearance || {}),
                                      showLogo: e.target.checked
                                    }
                                  }))}
                                  size="small"
                                />
                              } 
                              label={<Typography variant="body2">{t('qrMenuSettingsModal.appearance.showCompanyLogo')}</Typography>}
                            />
                            
                            <FormControlLabel 
                              control={
                                <Checkbox 
                                  checked={settings?.appearance?.allowOrderNotes !== false}
                                  onChange={(e) => setSettings(prev => ({
                                    ...(prev || {}),
                                    appearance: {
                                      ...(prev?.appearance || {}),
                                      allowOrderNotes: e.target.checked
                                    }
                                  }))}
                                  size="small"
                                />
                              } 
                              label={<Typography variant="body2">{t('qrMenuSettingsModal.appearance.acceptOrderNote')}</Typography>}
                            />
                          </Box>
                        </Box>
                        )}

                        <Divider sx={{ my: 2 }} />

                        {isTicketMode && (
                          <Box sx={{ mb: 3 }}>
                            <TextField
                              fullWidth
                              label="Firma Adı (Mavi Başlık)"
                              value={settings?.appearance?.ticketCompanyName || ''}
                              onChange={(e) => setSettings(prev => ({
                                ...(prev || {}),
                                appearance: {
                                  ...(prev?.appearance || {}),
                                  ticketCompanyName: e.target.value
                                }
                              }))}
                              size="small"
                            />
                          </Box>
                        )}

                        {isTicketMode && (
                          <Box sx={{ mb: 3, display: 'grid', gap: 1.5 }}>
                            <TextField
                              fullWidth
                              type="number"
                              label="Çocuk Yaş Üst Sınırı"
                              inputProps={{ min: 1, max: 17 }}
                              helperText="Yaşı bu değer ve altında olanlar çocuk olarak hesaplanir."
                              value={settings?.appearance?.ticketChildMaxAge ?? 12}
                              onChange={(e) => {
                                const parsed = Number(e.target.value);
                                const safeValue = Number.isNaN(parsed) ? 12 : Math.min(Math.max(parsed, 1), 17);
                                setSettings(prev => ({
                                  ...(prev || {}),
                                  appearance: {
                                    ...(prev?.appearance || {}),
                                    ticketChildMaxAge: safeValue,
                                    ticketFreeMaxAge:
                                      prev?.appearance?.ticketEnableFreeAge === true
                                        ? Math.min(
                                            Math.max(Number(prev?.appearance?.ticketFreeMaxAge ?? 0), 0),
                                            safeValue - 1
                                          )
                                        : prev?.appearance?.ticketFreeMaxAge
                                  }
                                }));
                              }}
                              size="small"
                            />
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={settings?.appearance?.ticketEnableFreeAge === true}
                                  onChange={(e) => setSettings(prev => {
                                    const childMax = Number(prev?.appearance?.ticketChildMaxAge ?? 12);
                                    const suggestedFreeMax = Math.min(3, Math.max(0, childMax - 1));
                                    return {
                                      ...(prev || {}),
                                      appearance: {
                                        ...(prev?.appearance || {}),
                                        ticketEnableFreeAge: e.target.checked,
                                        ticketFreeMaxAge: e.target.checked
                                          ? Math.min(
                                              Math.max(Number(prev?.appearance?.ticketFreeMaxAge ?? suggestedFreeMax), 0),
                                              childMax - 1
                                            )
                                          : prev?.appearance?.ticketFreeMaxAge
                                      }
                                    };
                                  })}
                                  size="small"
                                />
                              }
                              label={<Typography variant="body2">{t('qrMenuSettingsModal.appearance.belowAgeFree')}</Typography>}
                            />
                            {settings?.appearance?.ticketEnableFreeAge === true && (
                              <TextField
                                fullWidth
                                type="number"
                                label="Kaç yaşın altı ücretsiz olsun"
                                inputProps={{ min: 0, max: Math.max(0, Number(settings?.appearance?.ticketChildMaxAge ?? 12) - 1) }}
                                helperText={`Çakışma olmaması için bu değer çocuk yaş üst sınırından küçük olmalı (maks. ${Math.max(0, Number(settings?.appearance?.ticketChildMaxAge ?? 12) - 1)}).`}
                                value={settings?.appearance?.ticketFreeMaxAge ?? Math.min(3, Math.max(0, Number(settings?.appearance?.ticketChildMaxAge ?? 12) - 1))}
                                onChange={(e) => {
                                  const childMax = Number(settings?.appearance?.ticketChildMaxAge ?? 12);
                                  const parsed = Number(e.target.value);
                                  const safeValue = Number.isNaN(parsed)
                                    ? Math.min(3, Math.max(0, childMax - 1))
                                    : Math.min(Math.max(parsed, 0), Math.max(0, childMax - 1));
                                  setSettings(prev => ({
                                    ...(prev || {}),
                                    appearance: {
                                      ...(prev?.appearance || {}),
                                      ticketFreeMaxAge: safeValue
                                    }
                                  }));
                                }}
                                size="small"
                              />
                            )}
                            <Typography variant="subtitle2" sx={{ mt: 2 }}>
                              Bilet Fiyatlandırma Modeli
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', mt: -0.5 }}>
                              Firmanın iş modeline göre fiyat hesaplama yöntemini seçin. Seçim her an değiştirilebilir.
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mt: 0.5 }}>
                              {([
                                {
                                  id: 'product',
                                  title: 'Basit Ürün Fiyatı',
                                  description: 'Stoktaki yetişkin ve çocuk biletinin sabit fiyatları kullanılır. Yaş bandı veya sezon hesabı yapılmaz.',
                                  hint: 'Sabit fiyat satışı yapan firmalar için önerilir.'
                                },
                                {
                                  id: 'dynamic',
                                  title: 'Dinamik İndirim ve Sezon',
                                  description: 'Yetişkin sezon fiyatı ve yaşa göre yüzde indirim bantları uygulanır.',
                                  hint: 'Sezona ve yaşa göre fiyat uygulayan firmalar için önerilir.'
                                }
                              ] as const).map(option => {
                                const isSelected = ticketPricingMode === option.id;
                                return (
                                  <Box
                                    key={option.id}
                                    onClick={() => setSettings(prev => ({
                                      ...(prev || {}),
                                      appearance: {
                                        ...(prev?.appearance || {}),
                                        ticketPricingMode: option.id
                                      }
                                    }))}
                                    sx={{
                                      cursor: 'pointer',
                                      border: '1px solid',
                                      borderColor: isSelected ? 'primary.main' : 'divider',
                                      borderRadius: 1.5,
                                      p: 1.5,
                                      bgcolor: isSelected ? 'primary.50' : 'background.paper',
                                      transition: 'all 0.15s ease',
                                      display: 'flex',
                                      gap: 1.25,
                                      alignItems: 'flex-start',
                                      '&:hover': {
                                        borderColor: 'primary.main',
                                        bgcolor: isSelected ? 'primary.50' : 'action.hover'
                                      }
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        mt: 0.25,
                                        width: 18,
                                        height: 18,
                                        borderRadius: '50%',
                                        border: '2px solid',
                                        borderColor: isSelected ? 'primary.main' : 'text.disabled',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0
                                      }}
                                    >
                                      {isSelected && (
                                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'primary.main' }} />
                                      )}
                                    </Box>
                                    <Box>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: isSelected ? 'primary.main' : 'text.primary' }}>
                                        {option.title}
                                      </Typography>
                                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
                                        {option.description}
                                      </Typography>
                                      <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                                        {option.hint}
                                      </Typography>
                                    </Box>
                                  </Box>
                                );
                              })}
                            </Box>
                            {!isDynamicPricingMode && (
                              <Alert severity="info" sx={{ mt: 0.5, py: 0.25 }}>
                                Aşağıdaki yaş bandı ve sezonluk fiyat alanları "Basit Ürün Fiyatı" modunda devre dışıdır. Hesaplamalarda yalnızca stoktaki yetişkin ve çocuk biletinin fiyatı kullanılır.
                              </Alert>
                            )}
                            <Box
                              sx={{
                                mt: 1,
                                display: 'grid',
                                gap: 1.5,
                                opacity: isDynamicPricingMode ? 1 : 0.5,
                                transition: 'opacity 0.2s ease'
                              }}
                            >
                            <Typography variant="subtitle2" sx={{ mt: 0 }}>
                              Yaş İndirim Ayarları
                            </Typography>
                            <TextField
                              fullWidth
                              disabled={!isDynamicPricingMode}
                              type="number"
                              label="Ilk indirim yas ust siniri (ornek: 6)"
                              inputProps={{ min: 0, max: 17 }}
                              helperText="%50 indirim bandi icin ust yas siniri."
                              value={settings?.appearance?.ticketDiscountBandOneMaxAge ?? 6}
                              onChange={(e) => {
                                const parsed = Number(e.target.value);
                                const safeValue = Number.isNaN(parsed) ? 6 : Math.min(Math.max(parsed, 0), 17);
                                setSettings(prev => ({
                                  ...(prev || {}),
                                  appearance: {
                                    ...(prev?.appearance || {}),
                                    ticketDiscountBandOneMaxAge: safeValue,
                                    ticketDiscountBandTwoMaxAge: Math.max(
                                      safeValue,
                                      Number(prev?.appearance?.ticketDiscountBandTwoMaxAge ?? 12)
                                    )
                                  }
                                }));
                              }}
                              size="small"
                            />
                            <TextField
                              fullWidth
                              disabled={!isDynamicPricingMode}
                              type="number"
                              label="Ilk indirim orani (%)"
                              inputProps={{ min: 0, max: 100 }}
                              helperText="Ornek: 50"
                              value={settings?.appearance?.ticketDiscountBandOnePercent ?? 50}
                              onChange={(e) => {
                                const parsed = Number(e.target.value);
                                const safeValue = Number.isNaN(parsed) ? 50 : Math.min(Math.max(parsed, 0), 100);
                                setSettings(prev => ({
                                  ...(prev || {}),
                                  appearance: {
                                    ...(prev?.appearance || {}),
                                    ticketDiscountBandOnePercent: safeValue
                                  }
                                }));
                              }}
                              size="small"
                            />
                            <TextField
                              fullWidth
                              disabled={!isDynamicPricingMode}
                              type="number"
                              label="Ikinci indirim yas ust siniri (ornek: 12)"
                              inputProps={{ min: Number(settings?.appearance?.ticketDiscountBandOneMaxAge ?? 6), max: 17 }}
                              helperText="%25 indirim bandi icin ust yas siniri."
                              value={settings?.appearance?.ticketDiscountBandTwoMaxAge ?? 12}
                              onChange={(e) => {
                                const bandOne = Number(settings?.appearance?.ticketDiscountBandOneMaxAge ?? 6);
                                const parsed = Number(e.target.value);
                                const safeValue = Number.isNaN(parsed) ? 12 : Math.min(Math.max(parsed, bandOne), 17);
                                setSettings(prev => ({
                                  ...(prev || {}),
                                  appearance: {
                                    ...(prev?.appearance || {}),
                                    ticketDiscountBandTwoMaxAge: safeValue
                                  }
                                }));
                              }}
                              size="small"
                            />
                            <TextField
                              fullWidth
                              disabled={!isDynamicPricingMode}
                              type="number"
                              label="Ikinci indirim orani (%)"
                              inputProps={{ min: 0, max: 100 }}
                              helperText="Ornek: 25"
                              value={settings?.appearance?.ticketDiscountBandTwoPercent ?? 25}
                              onChange={(e) => {
                                const parsed = Number(e.target.value);
                                const safeValue = Number.isNaN(parsed) ? 25 : Math.min(Math.max(parsed, 0), 100);
                                setSettings(prev => ({
                                  ...(prev || {}),
                                  appearance: {
                                    ...(prev?.appearance || {}),
                                    ticketDiscountBandTwoPercent: safeValue
                                  }
                                }));
                              }}
                              size="small"
                            />
                            <Typography variant="subtitle2" sx={{ mt: 1 }}>
                              Yetiskin Sezon Fiyatlari
                            </Typography>
                            <TextField
                              fullWidth
                              disabled={!isDynamicPricingMode}
                              type="number"
                              label="Mayis - Haziran Yetiskin Fiyati"
                              inputProps={{ min: 0 }}
                              value={settings?.appearance?.ticketAdultPriceMayJune ?? 2300}
                              onChange={(e) => {
                                const parsed = Number(e.target.value);
                                const safeValue = Number.isNaN(parsed) ? 2300 : Math.max(parsed, 0);
                                setSettings(prev => ({
                                  ...(prev || {}),
                                  appearance: {
                                    ...(prev?.appearance || {}),
                                    ticketAdultPriceMayJune: safeValue
                                  }
                                }));
                              }}
                              size="small"
                            />
                            <TextField
                              fullWidth
                              disabled={!isDynamicPricingMode}
                              type="number"
                              label="Temmuz - Agustos Yetiskin Fiyati"
                              inputProps={{ min: 0 }}
                              value={settings?.appearance?.ticketAdultPriceJulyAugust ?? 2500}
                              onChange={(e) => {
                                const parsed = Number(e.target.value);
                                const safeValue = Number.isNaN(parsed) ? 2500 : Math.max(parsed, 0);
                                setSettings(prev => ({
                                  ...(prev || {}),
                                  appearance: {
                                    ...(prev?.appearance || {}),
                                    ticketAdultPriceJulyAugust: safeValue
                                  }
                                }));
                              }}
                              size="small"
                            />
                            <TextField
                              fullWidth
                              disabled={!isDynamicPricingMode}
                              type="number"
                              label="Eylul Yetiskin Fiyati"
                              inputProps={{ min: 0 }}
                              value={settings?.appearance?.ticketAdultPriceSeptember ?? 2300}
                              onChange={(e) => {
                                const parsed = Number(e.target.value);
                                const safeValue = Number.isNaN(parsed) ? 2300 : Math.max(parsed, 0);
                                setSettings(prev => ({
                                  ...(prev || {}),
                                  appearance: {
                                    ...(prev?.appearance || {}),
                                    ticketAdultPriceSeptember: safeValue
                                  }
                                }));
                              }}
                              size="small"
                            />
                            </Box>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={settings?.appearance?.ticketRequireAdultForReservation === true}
                                  onChange={(e) => setSettings(prev => ({
                                    ...(prev || {}),
                                    appearance: {
                                      ...(prev?.appearance || {}),
                                      ticketRequireAdultForReservation: e.target.checked
                                    }
                                  }))}
                                  size="small"
                                />
                              }
                              label={<Typography variant="body2">{t('qrMenuSettingsModal.appearance.noReservationWithoutParent')}</Typography>}
                            />
                            <AppDatePicker
                              fullWidth
                              label="En Erken Rezervasyon Tarihi"
                              helperText={
                                ticketEarliestReservationDate
                                  ? `Bugünden +${ticketMinOffsetValue ?? 0} gün (${ticketEarliestReservationDate.toLocaleDateString('tr-TR')})`
                                  : 'Boş bırakırsan mevcut davranış korunur.'
                              }
                              value={ticketEarliestReservationIso}
                              minDate={todayIso}
                              onChange={(selectedIso) => {
                                if (selectedIso === '') {
                                  setSettings(prev => ({
                                    ...(prev || {}),
                                    appearance: {
                                      ...(prev?.appearance || {}),
                                      ticketMinReservationOffsetDays: undefined
                                    }
                                  }));
                                  return;
                                }

                                const [year, month, day] = selectedIso.split('-').map(Number);
                                const selectedDate = new Date(year, (month || 1) - 1, day || 1);
                                selectedDate.setHours(0, 0, 0, 0);
                                const today = new Date();
                                today.setHours(0, 0, 0, 0);
                                const diffDays = Math.round((selectedDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                                const safeValue = Math.min(Math.max(diffDays, 0), 365);

                                setSettings(prev => ({
                                  ...(prev || {}),
                                  appearance: {
                                    ...(prev?.appearance || {}),
                                    ticketMinReservationOffsetDays: safeValue
                                  }
                                }));
                              }}
                              size="small"
                            />
                            <TextField
                              fullWidth
                              type="number"
                              label="Maksimum Kişi Sayısı"
                              inputProps={{ min: 1, max: 50 }}
                              helperText="Boş bırakılırsa varsayılan 10 kişi kullanılır."
                              value={settings?.appearance?.ticketMaxGuestCount ?? ''}
                              onChange={(e) => {
                                const raw = e.target.value;
                                if (raw === '') {
                                  setSettings(prev => ({
                                    ...(prev || {}),
                                    appearance: {
                                      ...(prev?.appearance || {}),
                                      ticketMaxGuestCount: undefined
                                    }
                                  }));
                                  return;
                                }

                                const parsed = Number(raw);
                                const safeValue = Number.isNaN(parsed) ? 10 : Math.min(Math.max(Math.floor(parsed), 1), 50);
                                setSettings(prev => ({
                                  ...(prev || {}),
                                  appearance: {
                                    ...(prev?.appearance || {}),
                                    ticketMaxGuestCount: safeValue
                                  }
                                }));
                              }}
                              size="small"
                            />
                            <AppDatePicker
                              fullWidth
                              label="En Geç Rezervasyon Tarihi"
                              helperText={ticketDateRangeValidationText || 'Boş bırakırsan mevcut davranış korunur.'}
                              error={Boolean(ticketDateRangeValidationText)}
                              value={settings?.appearance?.ticketMaxReservationDate || ''}
                              minDate={ticketEarliestReservationIso || undefined}
                              onChange={(value) => {
                                let selected = value || '';
                                if (selected && ticketEarliestReservationIso && selected < ticketEarliestReservationIso) {
                                  selected = ticketEarliestReservationIso;
                                }
                                setSettings(prev => ({
                                  ...(prev || {}),
                                  appearance: {
                                    ...(prev?.appearance || {}),
                                    ticketMaxReservationDate: selected || undefined
                                  }
                                }));
                              }}
                              size="small"
                            />
                            <TextField
                              fullWidth
                              label="Cagri Merkezi Metni"
                              value={settings?.appearance?.ticketCallCenterText || ''}
                              onChange={(e) => setSettings(prev => ({
                                ...(prev || {}),
                                appearance: {
                                  ...(prev?.appearance || {}),
                                  ticketCallCenterText: e.target.value
                                }
                              }))}
                              size="small"
                            />
                            <TextField
                              fullWidth
                              multiline
                              minRows={2}
                              maxRows={6}
                              label="Sol Bilgi Kutusu Metni"
                              value={settings?.appearance?.ticketLeftInfoText || ''}
                              onChange={(e) => setSettings(prev => ({
                                ...(prev || {}),
                                appearance: {
                                  ...(prev?.appearance || {}),
                                  ticketLeftInfoText: e.target.value
                                }
                              }))}
                              inputProps={{ style: { overflowY: 'auto' } }}
                              size="small"
                            />
                            <TextField
                              fullWidth
                              multiline
                              minRows={2}
                              maxRows={6}
                              label="Ust Aciklama Metni"
                              value={settings?.appearance?.ticketNoticeText || ''}
                              onChange={(e) => setSettings(prev => ({
                                ...(prev || {}),
                                appearance: {
                                  ...(prev?.appearance || {}),
                                  ticketNoticeText: e.target.value
                                }
                              }))}
                              inputProps={{ style: { overflowY: 'auto' } }}
                              size="small"
                            />
                            <Typography variant="subtitle2" sx={{ mt: 1 }}>
                              Bilgilendirme Popup Butonları
                            </Typography>
                            <TextField
                              fullWidth
                              label="1. Buton Başlığı"
                              placeholder="Hizmet Detayları"
                              value={settings?.appearance?.ticketServiceDetailsTitle || ''}
                              onChange={(e) => setSettings(prev => ({
                                ...(prev || {}),
                                appearance: {
                                  ...(prev?.appearance || {}),
                                  ticketServiceDetailsTitle: e.target.value
                                }
                              }))}
                              size="small"
                            />
                            <TextField
                              fullWidth
                              multiline
                              minRows={5}
                              maxRows={12}
                              label="1. Buton İçeriği"
                              value={settings?.appearance?.ticketServiceDetailsContent || ''}
                              onChange={(e) => setSettings(prev => ({
                                ...(prev || {}),
                                appearance: {
                                  ...(prev?.appearance || {}),
                                  ticketServiceDetailsContent: e.target.value
                                }
                              }))}
                              inputProps={{ style: { overflowY: 'auto' } }}
                              size="small"
                            />
                            <TextField
                              fullWidth
                              label="2. Buton Başlığı"
                              placeholder="Online Satış Sözleşmesi"
                              value={settings?.appearance?.ticketSalesAgreementTitle || ''}
                              onChange={(e) => setSettings(prev => ({
                                ...(prev || {}),
                                appearance: {
                                  ...(prev?.appearance || {}),
                                  ticketSalesAgreementTitle: e.target.value
                                }
                              }))}
                              size="small"
                            />
                            <TextField
                              fullWidth
                              multiline
                              minRows={5}
                              maxRows={12}
                              label="2. Buton İçeriği"
                              value={settings?.appearance?.ticketSalesAgreementContent || ''}
                              onChange={(e) => setSettings(prev => ({
                                ...(prev || {}),
                                appearance: {
                                  ...(prev?.appearance || {}),
                                  ticketSalesAgreementContent: e.target.value
                                }
                              }))}
                              inputProps={{ style: { overflowY: 'auto' } }}
                              size="small"
                            />
                            <TextField
                              fullWidth
                              label="3. Buton Başlığı"
                              placeholder="KVKK"
                              value={settings?.appearance?.ticketKvkkTitle || ''}
                              onChange={(e) => setSettings(prev => ({
                                ...(prev || {}),
                                appearance: {
                                  ...(prev?.appearance || {}),
                                  ticketKvkkTitle: e.target.value
                                }
                              }))}
                              size="small"
                            />
                            <TextField
                              fullWidth
                              multiline
                              minRows={5}
                              maxRows={12}
                              label="3. Buton İçeriği"
                              value={settings?.appearance?.ticketKvkkContent || ''}
                              onChange={(e) => setSettings(prev => ({
                                ...(prev || {}),
                                appearance: {
                                  ...(prev?.appearance || {}),
                                  ticketKvkkContent: e.target.value
                                }
                              }))}
                              inputProps={{ style: { overflowY: 'auto' } }}
                              size="small"
                            />
                            <Typography variant="subtitle2" sx={{ mt: 1 }}>
                              Buton Altı İletişim Alanı
                            </Typography>
                            <TextField
                              fullWidth
                              label="Adres"
                              value={settings?.appearance?.ticketContactAddress || ''}
                              onChange={(e) => setSettings(prev => ({
                                ...(prev || {}),
                                appearance: {
                                  ...(prev?.appearance || {}),
                                  ticketContactAddress: e.target.value
                                }
                              }))}
                              size="small"
                            />
                            <TextField
                              fullWidth
                              label="Telefon 1"
                              value={settings?.appearance?.ticketContactPhonePrimary || ''}
                              onChange={(e) => setSettings(prev => ({
                                ...(prev || {}),
                                appearance: {
                                  ...(prev?.appearance || {}),
                                  ticketContactPhonePrimary: e.target.value
                                }
                              }))}
                              size="small"
                            />
                            <TextField
                              fullWidth
                              label="Telefon 2"
                              value={settings?.appearance?.ticketContactPhoneSecondary || ''}
                              onChange={(e) => setSettings(prev => ({
                                ...(prev || {}),
                                appearance: {
                                  ...(prev?.appearance || {}),
                                  ticketContactPhoneSecondary: e.target.value
                                }
                              }))}
                              size="small"
                            />
                            <TextField
                              fullWidth
                              label="Telefon 3 (Opsiyonel)"
                              value={settings?.appearance?.ticketContactPhoneTertiary || ''}
                              onChange={(e) => setSettings(prev => ({
                                ...(prev || {}),
                                appearance: {
                                  ...(prev?.appearance || {}),
                                  ticketContactPhoneTertiary: e.target.value
                                }
                              }))}
                              size="small"
                            />
                            <TextField
                              fullWidth
                              label="E-posta"
                              value={settings?.appearance?.ticketContactEmail || ''}
                              onChange={(e) => setSettings(prev => ({
                                ...(prev || {}),
                                appearance: {
                                  ...(prev?.appearance || {}),
                                  ticketContactEmail: e.target.value
                                }
                              }))}
                              size="small"
                            />
                            <TextField
                              fullWidth
                              label="E-posta 2 (Opsiyonel)"
                              value={settings?.appearance?.ticketContactEmailSecondary || ''}
                              onChange={(e) => setSettings(prev => ({
                                ...(prev || {}),
                                appearance: {
                                  ...(prev?.appearance || {}),
                                  ticketContactEmailSecondary: e.target.value
                                }
                              }))}
                              size="small"
                            />
                            <Typography variant="subtitle2" sx={{ mt: 2 }}>
                              İletişim Bilgileri Düzeni
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', mt: -0.5 }}>
                              Adres, telefon ve e-posta bilgilerinin sayfada nasıl gösterileceğini seçin.
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mt: 0.5 }}>
                              {([
                                {
                                  id: 'stacked',
                                  title: 'Alt Alta',
                                  description: 'Her iletişim bilgisi ayrı satırda, ortalı şekilde gösterilir.',
                                  hint: 'Çok bilgi olduğunda okunaklıdır.'
                                },
                                {
                                  id: 'inline',
                                  title: 'Gruplu Yan Yana',
                                  description: 'Adres üstte; telefonlar bir satırda, e-postalar bir satırda yan yana ortalanır.',
                                  hint: 'Az satırlı, kompakt görünüm sağlar.'
                                }
                              ] as const).map(option => {
                                const currentLayout = settings?.appearance?.ticketContactLayout === 'inline' ? 'inline' : 'stacked';
                                const isSelected = currentLayout === option.id;
                                return (
                                  <Box
                                    key={option.id}
                                    onClick={() => setSettings(prev => ({
                                      ...(prev || {}),
                                      appearance: {
                                        ...(prev?.appearance || {}),
                                        ticketContactLayout: option.id
                                      }
                                    }))}
                                    sx={{
                                      cursor: 'pointer',
                                      border: '1px solid',
                                      borderColor: isSelected ? 'primary.main' : 'divider',
                                      borderRadius: 1.5,
                                      p: 1.5,
                                      bgcolor: isSelected ? 'primary.50' : 'background.paper',
                                      transition: 'all 0.15s ease',
                                      display: 'flex',
                                      gap: 1.25,
                                      alignItems: 'flex-start',
                                      '&:hover': {
                                        borderColor: 'primary.main',
                                        bgcolor: isSelected ? 'primary.50' : 'action.hover'
                                      }
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        mt: 0.25,
                                        width: 18,
                                        height: 18,
                                        borderRadius: '50%',
                                        border: '2px solid',
                                        borderColor: isSelected ? 'primary.main' : 'text.disabled',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0
                                      }}
                                    >
                                      {isSelected && (
                                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'primary.main' }} />
                                      )}
                                    </Box>
                                    <Box>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: isSelected ? 'primary.main' : 'text.primary' }}>
                                        {option.title}
                                      </Typography>
                                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
                                        {option.description}
                                      </Typography>
                                      <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                                        {option.hint}
                                      </Typography>
                                    </Box>
                                  </Box>
                                );
                              })}
                            </Box>
                          </Box>
                        )}

                        <Box sx={{ mb: 3 }}>
                          <Typography variant="body2" fontWeight="medium" sx={{ mb: 1.5 }}>
                            {isTicketMode ? 'Bilet Görseli' : 'Menü Logosu'}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Tooltip title={isTicketMode ? 'Bilet görselini değiştir' : 'Menü logosunu değiştir'}>
                              <Avatar 
                                src={settings?.appearance?.menuLogoUrl || undefined}
                                alt="Menü Logosu"
                                variant="rounded"
                                sx={{ 
                                  width: 80, 
                                  height: 80, 
                                  bgcolor: 'grey.100',
                                  border: '1px solid #eee',
                                  cursor: 'pointer'
                                }}
                                onClick={handleMenuLogoUploadClick}
                              >
                                {!settings?.appearance?.menuLogoUrl && <ImageIcon fontSize="large" sx={{ color: 'grey.500' }} />}
                              </Avatar>
                            </Tooltip>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              <Button
                                variant="outlined"
                                startIcon={<ImageIcon />}
                                onClick={handleMenuLogoUploadClick}
                                size="small"
                              >
                                {settings?.appearance?.menuLogoUrl ? (isTicketMode ? 'Görseli Değiştir' : 'Logoyu Değiştir') : (isTicketMode ? 'Görsel Yükle' : 'Logo Yükle')}
                              </Button>
                              {settings?.appearance?.menuLogoUrl && (
                                <Button
                                  variant="outlined"
                                  color="error"
                                  startIcon={<DeleteIcon />}
                                  onClick={() => setDeleteLogoConfirmOpen(true)}
                                  size="small"
                                >
                                  {isTicketMode ? 'Görseli Sil' : 'Logoyu Sil'}
                                </Button>
                              )}
                            </Box>
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            {isTicketMode
                              ? 'Şablon 1 ekranında bilet kartında görünecek görsel.'
                              : 'Menü başlığının üstünde görünecek logo. Kare formatta (örn: 200x200px) önerilir.'}
                          </Typography>
                        </Box>

                        {isQrTikTemplate && (
                          <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" fontWeight="medium" sx={{ mb: 1.5 }}>
                              Kapak Resmi
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Tooltip title="Şablon iç ekranında kullanılacak kapak görselini değiştir">
                                <Avatar
                                  src={settings?.appearance?.qrtikCoverImageUrl || undefined}
                                  alt="QrTık Kapak"
                                  variant="rounded"
                                  sx={{
                                    width: 120,
                                    height: 68,
                                    bgcolor: 'grey.100',
                                    border: '1px solid #eee',
                                    cursor: 'pointer'
                                  }}
                                  onClick={handleQrTikCoverUploadClick}
                                >
                                  {!settings?.appearance?.qrtikCoverImageUrl && <ImageIcon fontSize="large" sx={{ color: 'grey.500' }} />}
                                </Avatar>
                              </Tooltip>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Button
                                  variant="outlined"
                                  startIcon={<ImageIcon />}
                                  onClick={handleQrTikCoverUploadClick}
                                  size="small"
                                >
                                  {settings?.appearance?.qrtikCoverImageUrl ? 'Kapağı Değiştir' : 'Kapak Yükle'}
                                </Button>
                                {settings?.appearance?.qrtikCoverImageUrl && (
                                  <Button
                                    variant="outlined"
                                    color="error"
                                    startIcon={<DeleteIcon />}
                                    onClick={() => setSettings(prev => ({
                                      ...(prev || {}),
                                      appearance: {
                                        ...(prev?.appearance || {}),
                                        qrtikCoverImageUrl: undefined
                                      }
                                    }))}
                                    size="small"
                                  >
                                    Kapağı Sil
                                  </Button>
                                )}
                              </Box>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                              Şablonun iç ekran üst kısmında görünen kapak resmi.
                            </Typography>
                          </Box>
                        )}

                        <Divider sx={{ my: 2 }} />

                        {isTicketMode && (
                          <>
                            <Box sx={{ mb: 3 }}>
                              <Typography variant="body2" fontWeight="medium" sx={{ mb: 1.5 }}>
                                Sol Alan Logosu (Küçük Kare)
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Tooltip title="Sol panelde görünecek küçük logoyu değiştir">
                                  <Avatar
                                    src={settings?.appearance?.ticketSidebarLogoUrl || undefined}
                                    alt="Sol Alan Logosu"
                                    variant="rounded"
                                    sx={{
                                      width: 72,
                                      height: 72,
                                      bgcolor: 'grey.100',
                                      border: '1px solid #eee',
                                      cursor: 'pointer'
                                    }}
                                    onClick={handleTicketSidebarLogoUploadClick}
                                  >
                                    {!settings?.appearance?.ticketSidebarLogoUrl && <ImageIcon fontSize="large" sx={{ color: 'grey.500' }} />}
                                  </Avatar>
                                </Tooltip>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                  <Button
                                    variant="outlined"
                                    startIcon={<ImageIcon />}
                                    onClick={handleTicketSidebarLogoUploadClick}
                                    size="small"
                                  >
                                    {settings?.appearance?.ticketSidebarLogoUrl ? 'Logoyu Değiştir' : 'Logo Yükle'}
                                  </Button>
                                  {settings?.appearance?.ticketSidebarLogoUrl && (
                                    <Button
                                      variant="outlined"
                                      color="error"
                                      startIcon={<DeleteIcon />}
                                      onClick={() => setSettings(prev => ({
                                        ...(prev || {}),
                                        appearance: {
                                          ...(prev?.appearance || {}),
                                          ticketSidebarLogoUrl: undefined
                                        }
                                      }))}
                                      size="small"
                                    >
                                      Logoyu Sil
                                    </Button>
                                  )}
                                </Box>
                              </Box>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                Ticket ekranında sol bilgi kutularının üstünde küçük kare logo olarak gösterilir.
                              </Typography>
                            </Box>
                            <Divider sx={{ my: 2 }} />

                            <Box sx={{ mb: 3 }}>
                              <Typography variant="body2" fontWeight="medium" sx={{ mb: 1.5 }}>
                                Arkaplan Resmi
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Tooltip title="Ticket sayfasi arkaplan resmini degistir">
                                  <Avatar
                                    src={settings?.appearance?.ticketBackgroundImageUrl || undefined}
                                    alt="Ticket Arkaplan"
                                    variant="rounded"
                                    sx={{
                                      width: 80,
                                      height: 80,
                                      bgcolor: 'grey.100',
                                      border: '1px solid #eee',
                                      cursor: 'pointer'
                                    }}
                                    onClick={handleTicketBackgroundUploadClick}
                                  >
                                    {!settings?.appearance?.ticketBackgroundImageUrl && <ImageIcon fontSize="large" sx={{ color: 'grey.500' }} />}
                                  </Avatar>
                                </Tooltip>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                  <Button
                                    variant="outlined"
                                    startIcon={<ImageIcon />}
                                    onClick={handleTicketBackgroundUploadClick}
                                    size="small"
                                  >
                                    {settings?.appearance?.ticketBackgroundImageUrl ? 'Arkaplani Degistir' : 'Arkaplan Yukle'}
                                  </Button>
                                  {settings?.appearance?.ticketBackgroundImageUrl && (
                                    <Button
                                      variant="outlined"
                                      color="error"
                                      startIcon={<DeleteIcon />}
                                      onClick={() => setSettings(prev => ({
                                        ...(prev || {}),
                                        appearance: {
                                          ...(prev?.appearance || {}),
                                          ticketBackgroundImageUrl: undefined
                                        }
                                      }))}
                                      size="small"
                                    >
                                      Arkaplani Sil
                                    </Button>
                                  )}
                                </Box>
                              </Box>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                Resim varsa ticket sayfasi wallpaper olarak gorunur. Bos birakilirsa beyaz arkaplan kullanilir.
                              </Typography>
                            </Box>
                            <Divider sx={{ my: 2 }} />
                          </>
                        )}

                        {isTicketMode && (
                          <>
                            <Box sx={{ mb: 3 }}>
                              <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
                                Buton Renkleri
                              </Typography>
                              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                                <TextField
                                  label="Hesapla Buton Rengi"
                                  size="small"
                                  value={settings?.appearance?.ticketCalculateButtonColor || '#2563eb'}
                                  onChange={(e) => setSettings(prev => ({
                                    ...(prev || {}),
                                    appearance: {
                                      ...(prev?.appearance || {}),
                                      ticketCalculateButtonColor: e.target.value
                                    }
                                  }))}
                                />
                                <TextField
                                  label="Rezervasyon Buton Rengi"
                                  size="small"
                                  value={settings?.appearance?.ticketReservationButtonColor || '#16a34a'}
                                  onChange={(e) => setSettings(prev => ({
                                    ...(prev || {}),
                                    appearance: {
                                      ...(prev?.appearance || {}),
                                      ticketReservationButtonColor: e.target.value
                                    }
                                  }))}
                                />
                              </Box>
                            </Box>
                            <Divider sx={{ my: 2 }} />
                          </>
                        )}

                        {/* Sepete Ekle Butonu Ayarları */}
                        {!isTicketMode && (
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="body2" fontWeight="medium" sx={{ mb: 2 }}>
                            Sepete Ekle Butonu Ayarları
                          </Typography>
                          
                          {/* Buton Aktif/Pasif */}
                          <FormControlLabel 
                            control={
                              <Checkbox 
                                checked={settings?.appearance?.addToCartButton?.enabled !== false}
                                onChange={(e) => setSettings(prev => ({
                                  ...(prev || {}),
                                  appearance: {
                                    ...(prev?.appearance || {}),
                                    addToCartButton: {
                                      ...(prev?.appearance?.addToCartButton || {}),
                                      enabled: e.target.checked
                                    }
                                  }
                                }))}
                                size="small"
                              />
                            } 
                            label={<Typography variant="body2">{t('qrMenuSettingsModal.appearance.showAddToCartButton')}</Typography>}
                            sx={{ mb: 2 }}
                          />

                          {settings?.appearance?.addToCartButton?.enabled !== false && (
                            <Box sx={{ ml: 3, space: 2 }}>
                              {/* Buton Metni */}
                              <TextField
                                fullWidth
                                label="Buton Metni"
                                value={settings?.appearance?.addToCartButton?.text || 'Sepete Ekle'}
                                onChange={(e) => setSettings(prev => ({
                                  ...(prev || {}),
                                  appearance: {
                                    ...(prev?.appearance || {}),
                                    addToCartButton: {
                                      ...(prev?.appearance?.addToCartButton || {}),
                                      text: e.target.value
                                    }
                                  }
                                }))}
                                variant="outlined"
                                size="small"
                                sx={{ mb: 2 }}
                              />

                              {/* Buton Stili */}
                              <FormControl fullWidth variant="outlined" size="small" sx={{ mb: 2 }}>
                                <InputLabel>Buton Stili</InputLabel>
                                <Select
                                  value={settings?.appearance?.addToCartButton?.style || 'filled'}
                                  onChange={(e) => setSettings(prev => ({
                                    ...(prev || {}),
                                    appearance: {
                                      ...(prev?.appearance || {}),
                                      addToCartButton: {
                                        ...(prev?.appearance?.addToCartButton || {}),
                                        style: e.target.value as 'filled' | 'outlined' | 'text'
                                      }
                                    }
                                  }))}
                                  label="Buton Stili"
                                >
                                  <MenuItem value="filled">Dolu (Filled)</MenuItem>
                                  <MenuItem value="outlined">{t('qrMenuSettingsModal.appearance.cartButtonStyle.outlined')}</MenuItem>
                                  <MenuItem value="text">Sadece Metin</MenuItem>
                                </Select>
                              </FormControl>

                              {/* Buton Boyutu */}
                              <FormControl fullWidth variant="outlined" size="small" sx={{ mb: 2 }}>
                                <InputLabel>Buton Boyutu</InputLabel>
                                <Select
                                  value={settings?.appearance?.addToCartButton?.size || 'medium'}
                                  onChange={(e) => setSettings(prev => ({
                                    ...(prev || {}),
                                    appearance: {
                                      ...(prev?.appearance || {}),
                                      addToCartButton: {
                                        ...(prev?.appearance?.addToCartButton || {}),
                                        size: e.target.value as 'small' | 'medium' | 'large'
                                      }
                                    }
                                  }))}
                                  label="Buton Boyutu"
                                >
                                  <MenuItem value="small">{t('qrMenuSettingsModal.appearance.size.small')}</MenuItem>
                                  <MenuItem value="medium">Orta</MenuItem>
                                  <MenuItem value="large">{t('qrMenuSettingsModal.appearance.size.large')}</MenuItem>
                                </Select>
                              </FormControl>

                              {/* Buton Pozisyonu */}
                              <FormControl fullWidth variant="outlined" size="small" sx={{ mb: 2 }}>
                                <InputLabel>Buton Pozisyonu</InputLabel>
                                <Select
                                  value={settings?.appearance?.addToCartButton?.position || 'bottom'}
                                  onChange={(e) => setSettings(prev => ({
                                    ...(prev || {}),
                                    appearance: {
                                      ...(prev?.appearance || {}),
                                      addToCartButton: {
                                        ...(prev?.appearance?.addToCartButton || {}),
                                        position: e.target.value as 'bottom' | 'overlay' | 'side'
                                      }
                                    }
                                  }))}
                                  label="Buton Pozisyonu"
                                >
                                  <MenuItem value="bottom">{t('qrMenuSettingsModal.appearance.position.bottom')}</MenuItem>
                                  <MenuItem value="overlay">{t('qrMenuSettingsModal.appearance.position.overlay')}</MenuItem>
                                  <MenuItem value="side">Yan Taraf</MenuItem>
                                </Select>
                              </FormControl>

                              {/* Renk Ayarları */}
                              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                    Arka Plan Rengi
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box 
                                      sx={{ 
                                        width: 32, 
                                        height: 32, 
                                        bgcolor: settings?.appearance?.addToCartButton?.backgroundColor || settings?.appearance?.primaryColor || '#1976d2', 
                                        borderRadius: 1, 
                                        border: '1px solid #ddd'
                                      }} 
                                    />
                                    <TextField 
                                      value={settings?.appearance?.addToCartButton?.backgroundColor || settings?.appearance?.primaryColor || '#1976d2'} 
                                      size="small"
                                      variant="outlined"
                                      onChange={(e) => setSettings(prev => ({
                                        ...(prev || {}),
                                        appearance: {
                                          ...(prev?.appearance || {}),
                                          addToCartButton: {
                                            ...(prev?.appearance?.addToCartButton || {}),
                                            backgroundColor: e.target.value
                                          }
                                        }
                                      }))}
                                      sx={{ flex: 1 }}
                                    />
                                  </Box>
                                </Box>

                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                    Metin Rengi
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box 
                                      sx={{ 
                                        width: 32, 
                                        height: 32, 
                                        bgcolor: settings?.appearance?.addToCartButton?.textColor || '#ffffff', 
                                        borderRadius: 1, 
                                        border: '1px solid #ddd'
                                      }} 
                                    />
                                    <TextField 
                                      value={settings?.appearance?.addToCartButton?.textColor || '#ffffff'} 
                                      size="small"
                                      variant="outlined"
                                      onChange={(e) => setSettings(prev => ({
                                        ...(prev || {}),
                                        appearance: {
                                          ...(prev?.appearance || {}),
                                          addToCartButton: {
                                            ...(prev?.appearance?.addToCartButton || {}),
                                            textColor: e.target.value
                                          }
                                        }
                                      }))}
                                      sx={{ flex: 1 }}
                                    />
                                  </Box>
                                </Box>
                              </Box>

                              {/* Köşe Yuvarlaklığı */}
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                  Köşe Yuvarlaklığı: {settings?.appearance?.addToCartButton?.borderRadius || 8}px
                                </Typography>
                                <Slider
                                  value={settings?.appearance?.addToCartButton?.borderRadius || 8}
                                  min={0}
                                  max={25}
                                  step={1}
                                  onChange={(e, value) => setSettings(prev => ({
                                    ...(prev || {}),
                                    appearance: {
                                      ...(prev?.appearance || {}),
                                      addToCartButton: {
                                        ...(prev?.appearance?.addToCartButton || {}),
                                        borderRadius: value as number
                                      }
                                    }
                                  }))}
                                  sx={{ width: '100%' }}
                                />
                              </Box>

                              {/* İkon Göster */}
                              <FormControlLabel 
                                control={
                                  <Checkbox 
                                    checked={settings?.appearance?.addToCartButton?.showIcon !== false}
                                    onChange={(e) => setSettings(prev => ({
                                      ...(prev || {}),
                                      appearance: {
                                        ...(prev?.appearance || {}),
                                        addToCartButton: {
                                          ...(prev?.appearance?.addToCartButton || {}),
                                          showIcon: e.target.checked
                                        }
                                      }
                                    }))}
                                    size="small"
                                  />
                                } 
                                label={<Typography variant="body2">{t('qrMenuSettingsModal.appearance.showCartIcon')}</Typography>}
                                sx={{ mb: 2 }}
                              />

                              {/* Animasyon */}
                              <FormControl fullWidth variant="outlined" size="small" sx={{ mb: 2 }}>
                                <InputLabel>Animasyon</InputLabel>
                                <Select
                                  value={settings?.appearance?.addToCartButton?.animation || 'none'}
                                  onChange={(e) => setSettings(prev => ({
                                    ...(prev || {}),
                                    appearance: {
                                      ...(prev?.appearance || {}),
                                      addToCartButton: {
                                        ...(prev?.appearance?.addToCartButton || {}),
                                        animation: e.target.value as 'none' | 'pulse' | 'bounce'
                                      }
                                    }
                                  }))}
                                  label="Animasyon"
                                >
                                  <MenuItem value="none">Animasyon Yok</MenuItem>
                                  <MenuItem value="pulse">{t('qrMenuSettingsModal.appearance.animation.pulse')}</MenuItem>
                                  <MenuItem value="bounce">{t('qrMenuSettingsModal.appearance.animation.bounce')}</MenuItem>
                                </Select>
                              </FormControl>

                              {/* Önizleme */}
                              <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 1 }}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                  Buton Önizlemesi:
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                  <Button
                                    variant={settings?.appearance?.addToCartButton?.style === 'outlined' ? 'outlined' : settings?.appearance?.addToCartButton?.style === 'text' ? 'text' : 'contained'}
                                    size={settings?.appearance?.addToCartButton?.size || 'medium'}
                                    sx={{
                                      backgroundColor: settings?.appearance?.addToCartButton?.style !== 'outlined' && settings?.appearance?.addToCartButton?.style !== 'text' 
                                        ? (settings?.appearance?.addToCartButton?.backgroundColor || settings?.appearance?.primaryColor || '#1976d2')
                                        : 'transparent',
                                      color: settings?.appearance?.addToCartButton?.textColor || '#ffffff',
                                      borderColor: settings?.appearance?.addToCartButton?.borderColor || settings?.appearance?.primaryColor || '#1976d2',
                                      borderRadius: `${settings?.appearance?.addToCartButton?.borderRadius || 8}px`,
                                      '&:hover': {
                                        backgroundColor: settings?.appearance?.addToCartButton?.style !== 'outlined' && settings?.appearance?.addToCartButton?.style !== 'text' 
                                          ? (settings?.appearance?.addToCartButton?.backgroundColor || settings?.appearance?.primaryColor || '#1976d2')
                                          : 'rgba(25, 118, 210, 0.04)',
                                      },
                                      animation: settings?.appearance?.addToCartButton?.animation === 'pulse' 
                                        ? 'pulse 2s infinite' 
                                        : settings?.appearance?.addToCartButton?.animation === 'bounce' 
                                        ? 'bounce 1s infinite' 
                                        : 'none',
                                      '@keyframes pulse': {
                                        '0%': { transform: 'scale(1)' },
                                        '50%': { transform: 'scale(1.05)' },
                                        '100%': { transform: 'scale(1)' }
                                      },
                                      '@keyframes bounce': {
                                        '0%, 20%, 53%, 80%, 100%': { transform: 'translateY(0)' },
                                        '40%, 43%': { transform: 'translateY(-8px)' },
                                        '70%': { transform: 'translateY(-4px)' },
                                        '90%': { transform: 'translateY(-2px)' }
                                      }
                                    }}
                                    startIcon={settings?.appearance?.addToCartButton?.showIcon !== false ? '🛒' : undefined}
                                  >
                                    {settings?.appearance?.addToCartButton?.text || 'Sepete Ekle'}
                                  </Button>
                                </Box>
                              </Box>

                              <Divider sx={{ my: 2 }} />

                              <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                <Typography variant="body2" fontWeight="medium" sx={{ mb: 0.5 }}>
                                  Karşılama Ekranı
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                                  Şablonlarda ilk açılıştaki karşılama ekranını gösterir/gizler.
                                </Typography>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={settings?.appearance?.showSplashScreen !== false}
                                      onChange={(e) => setSettings(prev => ({
                                        ...(prev || {}),
                                        appearance: {
                                          ...(prev?.appearance || {}),
                                          showSplashScreen: e.target.checked
                                        }
                                      }))}
                                      size="small"
                                    />
                                  }
                                  label={<Typography variant="body2">Karşılama ekranı göster</Typography>}
                                />
                              </Box>
                            </Box>
                          )}
                        </Box>
                        )}
                      </Paper>
                    </Grid>
                  </Grid>
                </Paper>
              )}
              
              {!isTicketMode && (((!isKitchenMode && activeTab === 3) || (isKitchenMode && activeTab === 0))) && (
                <Paper elevation={0} sx={{p: 2, minHeight: '500px'}}>
                  <Typography variant="h6" gutterBottom>{t('qrMenuSettings.personnel.title')}</Typography>
                  <Typography variant="body2" color="textSecondary" sx={{mb: 3}}>
                    {t('qrMenuSettings.personnel.description')}
                  </Typography>
                  
                  {allPersonnel.length > 0 ? (
                    <Grid container spacing={2}>
                      {/* Sol Taraf: Personel Arama ve Listeleme */}
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" fontWeight="medium" gutterBottom>
                          {t('qrMenuSettings.personnel.selectionTitle')}
                        </Typography>
                        <Autocomplete
                          multiple
                          size="small"
                          id="kitchen-personnel-autocomplete"
                          options={allPersonnel}
                          getOptionLabel={(option) => option.name}
                          value={allPersonnel.filter(p => selectedPersonnelIds.includes(p.id))}
                          onChange={(event, newValue) => {
                            const newSelectedIds = newValue.map(p => p.id);
                            handlePersonnelSelectionChange(newSelectedIds);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              label={t('qrMenuSettings.personnel.searchLabel')}
                              fullWidth
                            />
                          )}
                          renderTags={() => null}
                          renderOption={(props, option, { selected }) => (
                            <li {...props} style={{ fontSize: '0.875rem' }}>
                              <Checkbox
                                size="small"
                                style={{ marginRight: 4 }}
                                checked={selected}
                              />
                              {option.name}
                            </li>
                          )}
                          loading={personnelLoading}
                          sx={{ mb: 2 }}
                        />

                        {selectedPersonnelIds.length > 0 && (
                          <Box>
                            <Typography variant="caption" display="block" gutterBottom sx={{ fontWeight: 'medium' }}>
                              {t('qrMenuSettings.personnel.selectedListTitle')}
                            </Typography>
                            <Paper
                              variant="outlined"
                              sx={{
                                maxHeight: 400,
                                overflowY: 'auto'
                              }}
                            >
                              <List dense disablePadding>
                                {allPersonnel
                                  .filter(p => selectedPersonnelIds.includes(p.id))
                                  .map(p => (
                                    <ListItem
                                      key={p.id}
                                      selected={selectedPersonnelForPermissions === p.id}
                                      button
                                      onClick={() => handleSelectPersonnelForPermissions(p.id)}
                                      secondaryAction={
                                        <IconButton
                                          edge="end"
                                          aria-label="delete"
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            const newSelectedIds = selectedPersonnelIds.filter(id => id !== p.id);
                                            handlePersonnelSelectionChange(newSelectedIds);
                                          }}
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      }
                                      divider
                                    >
                                      <ListItemText primary={p.name} />
                                    </ListItem>
                                  ))}
                              </List>
                            </Paper>
                          </Box>
                        )}
                      </Grid>

                      {/* Sağ Taraf: Yetki Ayarları */}
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" fontWeight="medium" gutterBottom>
                          {t('qrMenuSettings.personnel.permissionsTitle')}
                        </Typography>

                        {selectedPersonnelForPermissions ? (
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 2,
                              minHeight: 200,
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 1
                            }}
                          >
                            <Typography variant="subtitle2" color="primary" gutterBottom>
                              {allPersonnel.find(p => p.id === selectedPersonnelForPermissions)?.name || t('qrMenuSettings.personnel.selectedPersonnelFallback')}
                            </Typography>

                            <FormGroup>
                              <FormControlLabel
                                control={
                                  <Switch
                                    size="small"
                                    checked={personnelPermissionsMap[selectedPersonnelForPermissions]?.accessKitchenScreen || false}
                                    onChange={(e) => handlePermissionChange(selectedPersonnelForPermissions, 'accessKitchenScreen', e.target.checked)}
                                  />
                                }
                                label={t('qrMenuSettings.personnel.permissions.accessKitchenScreen')}
                              />
                              <FormControlLabel
                                control={
                                  <Switch
                                    size="small"
                                    checked={personnelPermissionsMap[selectedPersonnelForPermissions]?.viewAllOrders || false}
                                    onChange={(e) => handlePermissionChange(selectedPersonnelForPermissions, 'viewAllOrders', e.target.checked)}
                                  />
                                }
                                label={t('qrMenuSettings.personnel.permissions.viewAllOrders')}
                              />
                              <FormControlLabel
                                control={
                                  <Switch
                                    size="small"
                                    checked={personnelPermissionsMap[selectedPersonnelForPermissions]?.updateOrderStatus || false}
                                    onChange={(e) => handlePermissionChange(selectedPersonnelForPermissions, 'updateOrderStatus', e.target.checked)}
                                  />
                                }
                                label={t('qrMenuSettings.personnel.permissions.updateOrderStatus')}
                              />
                              <FormControlLabel
                                control={
                                  <Switch
                                    size="small"
                                    checked={personnelPermissionsMap[selectedPersonnelForPermissions]?.cancelOrders || false}
                                    onChange={(e) => handlePermissionChange(selectedPersonnelForPermissions, 'cancelOrders', e.target.checked)}
                                  />
                                }
                                label={t('qrMenuSettings.personnel.permissions.cancelOrders')}
                              />
                              
                              <Divider sx={{ my: 1 }} />
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', mb: 1, display: 'block' }}>
                                {t('qrMenuSettings.personnel.specialSettings')}
                              </Typography>
                              
                              <FormControlLabel
                                control={
                                  <Switch
                                    size="small"
                                    checked={personnelPermissionsMap[selectedPersonnelForPermissions]?.showPrintButton !== false}
                                    onChange={(e) => handlePermissionChange(selectedPersonnelForPermissions, 'showPrintButton', e.target.checked)}
                                  />
                                }
                                label={t('qrMenuSettings.personnel.permissions.showPrintButton')}
                              />
                              <FormControlLabel
                                control={
                                  <Switch
                                    size="small"
                                    checked={personnelPermissionsMap[selectedPersonnelForPermissions]?.listReadyOrders !== false}
                                    onChange={(e) => handlePermissionChange(selectedPersonnelForPermissions, 'listReadyOrders', e.target.checked)}
                                  />
                                }
                                label={t('qrMenuSettings.personnel.permissions.listReadyOrders')}
                              />
                              <FormControlLabel
                                control={
                                  <Switch
                                    size="small"
                                    checked={personnelPermissionsMap[selectedPersonnelForPermissions]?.waiterMode || false}
                                    onChange={(e) => handlePermissionChange(selectedPersonnelForPermissions, 'waiterMode', e.target.checked)}
                                  />
                                }
                                label={t('qrMenuSettings.personnel.permissions.waiterMode')}
                              />
                              <FormControlLabel
                                control={
                                  <Switch
                                    size="small"
                                    checked={personnelPermissionsMap[selectedPersonnelForPermissions]?.autoPrint || false}
                                    onChange={(e) => handlePermissionChange(selectedPersonnelForPermissions, 'autoPrint', e.target.checked)}
                                  />
                                }
                                label={t('qrMenuSettings.personnel.permissions.autoPrint')}
                              />
                              <FormControlLabel
                                control={
                                  <Switch
                                    size="small"
                                    checked={personnelPermissionsMap[selectedPersonnelForPermissions]?.showPrices !== false}
                                    onChange={(e) => handlePermissionChange(selectedPersonnelForPermissions, 'showPrices', e.target.checked)}
                                  />
                                }
                                label={t('qrMenuSettings.personnel.permissions.showPrices')}
                              />
                            </FormGroup>
                          </Paper>
                        ) : (
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 3,
                              minHeight: 200,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'rgba(0,0,0,0.02)'
                            }}
                          >
                            <Typography variant="body2" color="text.secondary" align="center">
                              {t('qrMenuSettings.personnel.selectLeftHint')}
                            </Typography>
                          </Paper>
                        )}
                      </Grid>
                    </Grid>
                  ) : (
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 3,
                        textAlign: 'center',
                        backgroundColor: 'rgba(0,0,0,0.02)'
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {personnelLoading
                          ? t('qrMenuSettings.personnel.loading')
                          : t('qrMenuSettings.personnel.noPersonnel')}
                      </Typography>
                    </Paper>
                  )}
                </Paper>
              )}
              
              {!isTicketMode && (((!isKitchenMode && activeTab === 4) || (isKitchenMode && activeTab === 1))) && (
                <Paper elevation={0} sx={{p: 2, minHeight: '500px'}}>
                  <Typography variant="h6" gutterBottom>{t('qrMenuSettings.printing.title')}</Typography>
                  <Typography variant="body2" color="textSecondary" sx={{mb: 3}}>
                    {t('qrMenuSettings.printing.description')}
                  </Typography>
                  
                  <PrintingSettingsTab
                    printingMode={printingMode}
                    defaultReceiptPrinter={defaultReceiptPrinter}
                    definedPrinters={definedPrinters}
                    categoryPrinterAssignments={categoryPrinterAssignments}
                    uniqueCategoryIds={uniqueCategoryIds}
                    categoriesToListForPrinting={categoriesToListForPrinting}
                    localPrinters={localPrinters}
                    loadingLocalPrinters={loadingLocalPrinters}
                    localPrintersError={localPrintersError}
                    onPrintingModeChange={handlePrintingModeChange}
                    onSetDefaultPrinter={handleSetDefaultPrinter}
                    onRemoveDefaultPrinter={handleRemoveDefaultPrinter}
                    onCategoryPrinterChange={handleCategoryPrinterChange}
                    onRefreshLocalPrinters={handleRefreshLocalPrinters}
                    onPrintTestPage={handlePrintTestPage}
                    printTestLoading={printTestLoading}
                    onOpenReceiptSettingsModal={handleOpenReceiptSettingsModal}
                    onResetReceiptNumber={handleResetReceiptNumber}
                    receiptNumberEnabled={receiptDisplaySettings.showReceiptNumber}
                  />
                </Paper>
              )}
              
              {!isTicketMode && (((!isKitchenMode && activeTab === 5) || (isKitchenMode && activeTab === 2))) && (
                <Paper elevation={0} sx={{p: 2, minHeight: '500px'}}>
                  <KitchenScreenSettings
                    settings={kitchenScreenSettings}
                    onSettingsChange={setKitchenScreenSettings}
                  />
                </Paper>
              )}
            </>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 1.5, py: 1, borderTop: 1, borderColor: 'divider', justifyContent: 'space-between', flexWrap: isMobile ? 'wrap' : 'nowrap', gap: isMobile ? 1 : 0 }}>
        {!isKitchenMode && (
          <Box sx={{ display: 'flex', gap: 0.5, order: isMobile ? 2 : 1, width: isMobile ? '100%' : 'auto', justifyContent: isMobile ? 'center' : 'flex-start' }}>
            {/* 💻 MASAÜSTÜ: Tam butonlar */}
            {!isMobile && (
              <>
                <Button 
                  onClick={() => setTemplateSaveModalOpen(true)}
                  variant="outlined"
                  size="small"
                  color="secondary"
                  startIcon={<AddIcon />}
                >
                  {t('qrMenuSettingsModal.actions.saveAsTemplate')}
                </Button>
                <Button 
                  onClick={() => {
                    setTemplateApplyModalOpen(true);
                    setSelectedTemplateKey('');
                  }}
                  variant="outlined"
                  size="small"
                  color="secondary"
                  disabled={Object.keys(savedTemplates).length === 0}
                >
                  {t('qrMenuSettingsModal.actions.selectAndApplyTemplate')}
                </Button>
              </>
            )}
            
            {/* 📱 MOBİL: Kompakt butonlar */}
            {isMobile && (
              <>
                <Button 
                  onClick={() => setTemplateSaveModalOpen(true)}
                  variant="outlined"
                  size="small"
                  color="secondary"
                  sx={{ fontSize: '0.7rem', px: 1 }}
                >
                  {t('qrMenuSettingsModal.actions.saveTemplateShort')}
                </Button>
                <Button 
                  onClick={() => {
                    setTemplateApplyModalOpen(true);
                    setSelectedTemplateKey('');
                  }}
                  variant="outlined"
                  size="small"
                  color="secondary"
                  disabled={Object.keys(savedTemplates).length === 0}
                  sx={{ fontSize: '0.7rem', px: 1 }}
                >
                  {t('qrMenuSettingsModal.actions.applyTemplateShort')}
                </Button>
              </>
            )}
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 0.5, order: isMobile ? 1 : 2, width: isMobile ? '100%' : 'auto', justifyContent: isMobile ? 'space-between' : 'flex-end' }}>
          <Button onClick={onClose} color="inherit" disabled={saving} size="small">
            {t('qrMenuSettings.common.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || loading || !hasChanges}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
            size="small"
            sx={{ fontSize: isMobile ? '0.75rem' : 'inherit' }}
          >
            {saving
              ? t('qrMenuSettings.common.saving')
              : (isMobile ? t('qrMenuSettings.common.save') : t('qrMenuSettings.common.saveChanges'))}
          </Button>
        </Box>
      </DialogActions>

      {/* Menü Ekleme Dialog */}
      <Dialog open={menuDialogOpen} onClose={handleCloseMenuDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{t('qrMenuSettingsModal.addMenuDialog.title')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('qrMenuSettingsModal.addMenuDialog.menuName')}
            type="text"
            fullWidth
            variant="outlined"
            value={newMenuName}
            onChange={(e) => setNewMenuName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMenuDialog} color="inherit">{t('qrMenuSettingsModal.common.cancel')}</Button>
          <Button 
            onClick={handleAddMenu} 
            color="primary" 
            variant="contained"
            disabled={!newMenuName.trim()}
          >
            {t('qrMenuSettingsModal.common.add')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Product Selection Modal */}
      <QRMenuProductSelectionModal
        open={productSelectionModalOpen}
        onClose={() => setProductSelectionModalOpen(false)}
        allInventoryItems={allInventoryItems}
        selectedProductIds={currentMenuForProducts ? (selectedProductsForMenu[currentMenuForProducts] || []) : selectedProducts}
        onSelectProducts={handleSelectProducts}
        companyId={companyId}
        isTicketMode={isTicketMode}
      />

      <QRMenuProductSelectionModal
        open={relatedProductSelectionModalOpen}
        onClose={() => {
          setRelatedProductSelectionModalOpen(false);
          setCurrentProductForRelatedSelection(null);
        }}
        allInventoryItems={allInventoryItems}
        selectedProductIds={currentProductForRelatedSelection ? (relatedProductsByProductId[currentProductForRelatedSelection] || []) : []}
        onSelectProducts={(selectedIds) => handleSelectRelatedProducts(selectedIds)}
        companyId={companyId}
        isTicketMode={false}
        prioritizeSelected={true}
      />

      {/* Fiş Ayarları Modalı */}
      <ReceiptSettingsModal
        open={receiptSettingsModalOpen}
        onClose={handleCloseReceiptSettingsModal}
        onSave={handleSaveReceiptSettings}
        initialSettings={receiptDisplaySettings}
        onImageUpload={onImageUpload ? (file: File) => onImageUpload('receipt-logo', file) : undefined}
      />

      {/* Şablon Kaydetme Modalı */}
      <Dialog open={templateSaveModalOpen} onClose={() => setTemplateSaveModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {t('qrMenuSettingsModal.saveTemplateDialog.title')}
          <IconButton
            aria-label={t('qrMenuSettingsModal.imageUpload.close')}
            onClick={() => setTemplateSaveModalOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('qrMenuSettingsModal.saveTemplateDialog.description')}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label={t('qrMenuSettingsModal.saveTemplateDialog.templateName')}
            type="text"
            fullWidth
            variant="outlined"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder={t('qrMenuSettingsModal.saveTemplateDialog.templatePlaceholder')}
            helperText={t('qrMenuSettingsModal.saveTemplateDialog.templateHelper')}
          />
          
          <Box sx={{ mt: 3, p: 2, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              {t('qrMenuSettingsModal.saveTemplateDialog.includedSettings')}
            </Typography>
            <Typography variant="body2" component="div">
              ✅ {t('qrMenuSettingsModal.saveTemplateDialog.selectedProducts', { count: selectedProducts.length })}<br/>
              ✅ {t('qrMenuSettingsModal.saveTemplateDialog.subMenus', { count: menus.length })}<br/>
              ✅ {t('qrMenuSettingsModal.saveTemplateDialog.productOrder')}<br/>
              ✅ {t('qrMenuSettingsModal.saveTemplateDialog.appearanceSettings')}<br/>
              ✅ {t('qrMenuSettingsModal.saveTemplateDialog.kitchenColors')}<br/>
              ✅ {t('qrMenuSettingsModal.saveTemplateDialog.menuName')}<br/>
              <br/>
              ❌ {t('qrMenuSettingsModal.saveTemplateDialog.personnelExcluded')}<br/>
              ❌ {t('qrMenuSettingsModal.saveTemplateDialog.printingExcluded')}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateSaveModalOpen(false)} color="inherit">
            {t('qrMenuSettingsModal.common.cancel')}
          </Button>
          <Button 
            onClick={handleSaveTemplate} 
            color="primary" 
            variant="contained"
            disabled={!templateName.trim()}
          >
            {t('qrMenuSettingsModal.saveTemplateDialog.saveTemplate')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Şablon Uygulama Modalı */}
      <Dialog open={templateApplyModalOpen} onClose={() => setTemplateApplyModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {t('qrMenuSettingsModal.applyTemplateDialog.title')}
          <IconButton
            aria-label={t('qrMenuSettingsModal.imageUpload.close')}
            onClick={() => setTemplateApplyModalOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('qrMenuSettingsModal.applyTemplateDialog.description')}
          </Typography>
          
          {Object.keys(savedTemplates).length === 0 ? (
            <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', bgcolor: 'rgba(0,0,0,0.02)' }}>
              <Typography variant="body2" color="text.secondary">
                {t('qrMenuSettingsModal.applyTemplateDialog.noTemplates')}
              </Typography>
            </Paper>
          ) : (
            <>
              {/* Şablon Seçici */}
              <FormControl fullWidth variant="outlined" sx={{ mb: 3 }}>
                <InputLabel id="template-select-label">{t('qrMenuSettingsModal.applyTemplateDialog.selectTemplate')}</InputLabel>
                <Select
                  labelId="template-select-label"
                  id="template-select"
                  value={selectedTemplateKey}
                  onChange={(e) => setSelectedTemplateKey(e.target.value as string)}
                  label={t('qrMenuSettingsModal.applyTemplateDialog.selectTemplate')}
                >
                  <MenuItem value="">
                    <em>{t('qrMenuSettingsModal.applyTemplateDialog.selectTemplatePlaceholder')}</em>
                  </MenuItem>
                  {Object.entries(savedTemplates).map(([templateKey, template]) => (
                    <MenuItem key={templateKey} value={templateKey}>
                      {templateKey}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Seçilen Şablon Detayları */}
              {selectedTemplateKey && savedTemplates[selectedTemplateKey] && (
                <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'rgba(25, 118, 210, 0.04)' }}>
                  <Typography variant="subtitle2" gutterBottom color="primary">
                    {t('qrMenuSettingsModal.applyTemplateDialog.selectedTemplate', { name: selectedTemplateKey })}
                  </Typography>
                  
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {savedTemplates[selectedTemplateKey].menuName && 
                        t('qrMenuSettingsModal.applyTemplateDialog.menuNameLabel', { name: savedTemplates[selectedTemplateKey].menuName })
                      }
                    </Typography>
                    
                    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      <Chip 
                        size="small" 
                        label={t('qrMenuSettingsModal.applyTemplateDialog.productsChip', { count: savedTemplates[selectedTemplateKey].products?.length || 0 })}
                        variant="outlined"
                      />
                      <Chip 
                        size="small" 
                        label={t('qrMenuSettingsModal.applyTemplateDialog.subMenusChip', { count: Object.keys(savedTemplates[selectedTemplateKey].menus || {}).length })}
                        variant="outlined"
                      />
                      <Chip 
                        size="small" 
                        label={t('qrMenuSettingsModal.applyTemplateDialog.templateChip', { template: savedTemplates[selectedTemplateKey].appearance?.template || t('qrMenuSettingsModal.applyTemplateDialog.standardTemplate') })}
                        variant="outlined"
                      />
                    </Box>
                    
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                      {t('qrMenuSettingsModal.applyTemplateDialog.savedDate', { date: new Date(savedTemplates[selectedTemplateKey].createdAt).toLocaleDateString((i18n.language || 'tr').toLowerCase().startsWith('en') ? 'en-US' : 'tr-TR') })}
                    </Typography>
                  </Box>
                </Paper>
              )}

              {/* Şablon Yönetimi */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="body2" fontWeight="medium">
                  {t('qrMenuSettingsModal.applyTemplateDialog.templateManagement')}
                </Typography>
                {selectedTemplateKey && (
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    startIcon={<DeleteIcon />}
                    onClick={() => {
                      handleDeleteTemplate(selectedTemplateKey);
                      setSelectedTemplateKey('');
                    }}
                  >
                    {t('qrMenuSettingsModal.applyTemplateDialog.deleteSelected')}
                  </Button>
                )}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateApplyModalOpen(false)} color="inherit">
            {t('qrMenuSettingsModal.common.cancel')}
          </Button>
          <Button
            onClick={handleApplyTemplate}
            variant="contained"
            disabled={!selectedTemplateKey}
          >
            {t('qrMenuSettingsModal.applyTemplateDialog.applyTemplate')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Silme Onay Modalı */}
      <Dialog 
        open={deleteConfirmModalOpen} 
        onClose={() => {
          setDeleteConfirmModalOpen(false);
          setDeleteTarget(null);
        }} 
        maxWidth="xs" 
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <DeleteIcon />
          {t('qrMenuSettingsModal.deleteConfirmDialog.title')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {deleteTarget?.type === 'menu' && t('qrMenuSettingsModal.deleteConfirmDialog.menuConfirm', { name: deleteTarget.itemName })}
            {deleteTarget?.type === 'product' && t('qrMenuSettingsModal.deleteConfirmDialog.productConfirm', { name: deleteTarget.itemName })}
            {deleteTarget?.type === 'menuProduct' && t('qrMenuSettingsModal.deleteConfirmDialog.menuProductConfirm', { name: deleteTarget.itemName })}
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            {deleteTarget?.type === 'menu' && t('qrMenuSettingsModal.deleteConfirmDialog.menuWarning')}
            {deleteTarget?.type === 'product' && t('qrMenuSettingsModal.deleteConfirmDialog.productWarning')}
            {deleteTarget?.type === 'menuProduct' && t('qrMenuSettingsModal.deleteConfirmDialog.menuProductWarning')}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setDeleteConfirmModalOpen(false);
              setDeleteTarget(null);
            }} 
            color="inherit"
          >
            {t('qrMenuSettingsModal.common.cancel')}
          </Button>
          <Button
            onClick={() => {
              if (deleteTarget?.type === 'menu' && deleteTarget.menuId) {
                handleRemoveMenu(deleteTarget.menuId);
              } else if (deleteTarget?.type === 'product' && deleteTarget.productId) {
                handleRemoveProduct(deleteTarget.productId);
              } else if (deleteTarget?.type === 'menuProduct' && deleteTarget.menuId && deleteTarget.productId) {
                handleRemoveProductFromMenu(deleteTarget.menuId, deleteTarget.productId);
              }
            }}
            color="error"
            variant="contained"
            startIcon={<DeleteIcon />}
          >
            {t('qrMenuSettingsModal.deleteConfirmDialog.yesDelete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resim Kütüphanesi Modalı */}
      <ImageLibrarySelectionModal
        open={isImageLibraryModalOpen}
        onClose={handleCloseImageLibraryModal}
        onSave={handleSaveImages}
        productId={selectedImageItem?.id || null}
        productName={selectedImageItem?.name || 'QR Menü Ürünleri'}
        currentImageUrls={selectedImageItem?.imageUrl?.split(',').filter(Boolean) || []}
        companyId={companyId}
        singleSelect={currentUploadType === 'menuLogo' || currentUploadType === 'ticketBackground' || currentUploadType === 'ticketSidebarLogo' || currentUploadType === 'qrtikCover'}
      />

      {/* Menü Logosu Silme Onay Modalı */}
      <Dialog
        open={deleteLogoConfirmOpen}
        onClose={() => setDeleteLogoConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('qrMenuSettingsModal.deleteLogoDialog.title')}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('qrMenuSettingsModal.deleteLogoDialog.warning')}
          </Alert>
          {settings?.appearance?.menuLogoUrl && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Avatar
                src={settings.appearance.menuLogoUrl}
                variant="rounded"
                sx={{ width: 120, height: 120, border: '1px solid #eee' }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteLogoConfirmOpen(false)} disabled={deletingLogo}>
            {t('qrMenuSettingsModal.common.cancel')}
          </Button>
          <Button
            onClick={handleDeleteMenuLogo}
            color="error"
            variant="contained"
            disabled={deletingLogo}
            startIcon={deletingLogo ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deletingLogo ? t('qrMenuSettingsModal.common.deleting') : t('qrMenuSettingsModal.common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default QRMenuSettingsModal; 